import { useState, useEffect, useRef, useMemo, useCallback } from "react";

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  // Theatrical depths — dark cinema walls, gradient-ready
  navy: "#04080F",
  navyMid: "#0A1220",
  navyLight: "#111E36",
  navyCard: "#0C1628",
  navyGlass: "rgba(4,8,15,0.92)",
  // Screen glow — warm amber-gold, like projector light
  gold: "#C9A84C",
  goldLight: "#E4C566",
  goldDim: "rgba(201,168,76,0.10)",
  goldBorder: "rgba(201,168,76,0.55)",
  goldGlow: "rgba(201,168,76,0.08)",
  // Warm white hierarchy — slightly cream, not clinical
  white: "#F0EBE0",
  offwhite: "#B5B0A5",
  grey: "#8A94A8",
  greyDim: "#566A82",
  // Semantic
  red: "#E04B3D",
  green: "#27AE60",
  // Atmosphere — glass with cinematic warmth
  glass: "rgba(255,255,255,0.025)",
  glassBorder: "rgba(255,255,255,0.10)",
};

const FontStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,900;1,400&family=Outfit:wght@300;400;500;600;700&display=swap');

    *{box-sizing:border-box;margin:0;padding:0;}
    html,body{background:#020408;font-family:'Outfit',sans-serif;color:${T.white};-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;color-scheme:dark;
      /* Lock body so elastic overscroll never reveals content above the sticky header. */
      overscroll-behavior:none;-webkit-overflow-scrolling:auto;
    }
    button,a,[role="button"]{touch-action:manipulation;-webkit-tap-highlight-color:transparent;}

    /* Hide scrollbars everywhere — apps don't show them. Wheels and touch still scroll. */
    *{scrollbar-width:none;-ms-overflow-style:none;}
    *::-webkit-scrollbar{width:0;height:0;display:none;}

    /* ── Animations ── */
    @keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
    @keyframes fadeIn{from{opacity:0}to{opacity:1}}
    @keyframes scaleIn{from{transform:scale(0.88);opacity:0}to{transform:scale(1);opacity:1}}
    @keyframes slideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
    @keyframes shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
    @keyframes dotPop{0%,100%{transform:scale(1)}50%{transform:scale(1.4)}}
    @keyframes glowPulse{0%,100%{box-shadow:0 0 20px rgba(201,168,76,.2),0 0 40px rgba(201,168,76,.05)}50%{box-shadow:0 0 28px rgba(201,168,76,.3),0 0 56px rgba(201,168,76,.1)}}
    @keyframes seatPulse{0%,100%{box-shadow:0 0 0 0 rgba(201,168,76,.4)}70%{box-shadow:0 0 0 8px rgba(201,168,76,0)}}
    @keyframes grainDrift{0%{transform:translate(0,0)}10%{transform:translate(-1%,-1%)}20%{transform:translate(1%,0.5%)}30%{transform:translate(-0.5%,1%)}40%{transform:translate(0.5%,-0.5%)}50%{transform:translate(-1%,0.5%)}60%{transform:translate(1%,-1%)}70%{transform:translate(-0.5%,-0.5%)}80%{transform:translate(0.5%,1%)}90%{transform:translate(-1%,0.5%)}100%{transform:translate(0,0)}}

    /* ── Screen transitions ── */
    .screen{
      animation:fadeUp .38s cubic-bezier(.22,1,.36,1) both;
      flex:1;min-height:0;overflow-y:auto;
      /* Contain elastic overscroll so it doesn't bleed past the sticky header. */
      overscroll-behavior:contain;-webkit-overflow-scrolling:touch;
    }
    .fade{animation:fadeIn .24s ease both;}

    /* ── Interactions — visible tap feedback ── */
    .press{transition:transform .12s ease,opacity .12s ease,background-color .12s ease;cursor:pointer;}
    .press:active{transform:scale(.94);opacity:.72;}
    @media(hover:none){
      .press:active{transform:scale(.94);opacity:.7;}
    }
    .card-lift{transition:transform .22s cubic-bezier(.22,1,.36,1),box-shadow .22s ease;cursor:pointer;}
    .card-lift:hover{transform:translateY(-3px);box-shadow:0 16px 44px rgba(0,0,0,.65);}

    /* ── Buttons — luminous CTAs ── */
    .btn-gold{
      background:linear-gradient(135deg,${T.gold} 0%,${T.goldLight} 100%);
      color:#04080F;border:none;border-radius:10px;
      font-family:'Outfit',sans-serif;font-weight:700;letter-spacing:.01em;
      cursor:pointer;transition:filter .22s ease,transform .22s ease,box-shadow .22s ease;
      box-shadow:0 4px 20px rgba(201,168,76,.25),0 1px 0 rgba(255,255,255,.15) inset;
    }
    .btn-gold:hover{filter:brightness(1.1);transform:translateY(-1px);box-shadow:0 8px 32px rgba(201,168,76,.35),0 1px 0 rgba(255,255,255,.15) inset;}
    .btn-gold:active{transform:scale(.97);box-shadow:0 2px 12px rgba(201,168,76,.2);}

    .btn-outline{
      background:transparent;color:${T.gold};
      border:1.5px solid ${T.goldBorder};border-radius:10px;
      font-family:'Outfit',sans-serif;font-weight:500;
      cursor:pointer;transition:background .22s ease,border-color .22s ease,box-shadow .22s ease,color .22s ease;
    }
    .btn-outline:hover{background:${T.goldDim};border-color:${T.gold};box-shadow:0 0 20px rgba(201,168,76,.08);}
    .btn-outline:active{transform:scale(.97);background:${T.goldDim};border-color:${T.gold};}

    /* ── Glass surfaces ── */
    .glass-card{
      background:linear-gradient(145deg,rgba(255,255,255,0.03),rgba(255,255,255,0.012));
      border:1px solid ${T.glassBorder};
      backdrop-filter:blur(16px);border-radius:14px;
    }

    /* ── Tabs ── */
    .tab-on{color:${T.gold};border-bottom:2px solid ${T.gold};transition:color .2s,opacity .15s;}
    .tab-off{color:${T.grey};border-bottom:2px solid transparent;transition:color .2s,opacity .15s;}
    .tab-off:hover{color:${T.offwhite};}
    .tab-on:active,.tab-off:active{opacity:.6;}
    .view-all:active{opacity:.55;}

    /* ── Tags & inputs ── */
    .tag{background:${T.goldDim};color:${T.goldLight};border:1px solid ${T.goldBorder};border-radius:5px;font-size:10px;font-weight:700;padding:2px 7px;letter-spacing:.04em;}
    .inp{
      background:${T.navyLight};border:1px solid ${T.greyDim};border-radius:10px;
      color:${T.white};font-family:'Outfit',sans-serif;padding:11px 14px;font-size:14px;
      outline:none;width:100%;transition:border-color .2s,box-shadow .2s;
    }
    .inp:focus{border-color:${T.gold};box-shadow:0 0 0 3px rgba(201,168,76,.08);}

    /* ── Focus indicators (WCAG 2.4.7) ── */
    *:focus-visible{outline:2px solid ${T.gold};outline-offset:2px;border-radius:4px;}
    .btn-gold:focus-visible{outline-offset:3px;box-shadow:0 0 0 4px rgba(201,168,76,.3),0 4px 20px rgba(201,168,76,.25);}
    .btn-outline:focus-visible{outline-offset:3px;box-shadow:0 0 0 4px rgba(201,168,76,.2);}
    .pill-time:focus-visible{outline-offset:2px;}
    .tab-on:focus-visible,.tab-off:focus-visible{outline-offset:4px;}
    .inp:focus-visible{outline:none;border-color:${T.gold};box-shadow:0 0 0 3px rgba(201,168,76,.15);}
    .glass-card:focus-visible{outline-offset:2px;}

    /* ── Horizontal scroll, labels, pills ── */
    .hscroll{display:flex;gap:12px;overflow-x:auto;padding-bottom:4px;scrollbar-width:none;}
    .hscroll::-webkit-scrollbar{display:none;}
    .section-label{font-size:16px;font-weight:700;color:${T.white};letter-spacing:.01em;}
    .view-all{font-size:12px;font-weight:600;color:${T.gold};background:none;border:none;cursor:pointer;font-family:'Outfit',sans-serif;transition:color .18s;}
    .view-all:hover{color:${T.goldLight};}

    .pill-time{
      background:transparent;border:1.5px solid rgba(138,148,168,.4);border-radius:50px;
      color:${T.white};padding:8px 16px;font-size:13px;font-weight:600;
      font-family:'Outfit',sans-serif;cursor:pointer;transition:background .2s,border-color .2s,color .2s,box-shadow .2s;
    }
    .pill-time:hover{border-color:${T.gold};color:${T.gold};box-shadow:0 0 16px rgba(201,168,76,.08);}
    .pill-time:active{transform:scale(.95);background:${T.goldDim};border-color:${T.gold};color:${T.gold};}
    .pill-time.selected{background:${T.gold};border-color:${T.gold};color:#04080F;box-shadow:0 4px 16px rgba(201,168,76,.3);}
    .pill-time.selected:active{transform:scale(.95);filter:brightness(.92);}

    /* ── Check Seats button ── */
    .check-seats{transition:background .2s ease,border-color .2s ease,box-shadow .2s ease;}
    .check-seats:hover{background:rgba(201,168,76,.08);border-color:${T.goldBorder};box-shadow:0 0 14px rgba(201,168,76,.10);}
    .check-seats:hover .check-seats__ico{stroke:${T.goldLight};}
    .check-seats:hover .check-seats__lbl,.check-seats:hover .check-seats__caret{color:${T.goldLight};}
    .check-seats.open:hover{background:rgba(201,168,76,.18);}

    /* ── Responsive scaling ── */
    .phone-wrapper{
      display:flex;flex-direction:column;align-items:center;
      min-height:100vh;min-height:100dvh;
      background:#020408;padding:20px 16px;
      position:relative;box-sizing:border-box;overflow:hidden;
    }
    .phone-shell{width:360px;height:812px;transform-origin:top center;flex-shrink:0;overflow:hidden;}

    /* Real mobile devices — detected via touch input OR narrow viewport. Full-bleed, locked. */
    @media (pointer:coarse) and (hover:none),(max-width:600px){
      html,body{
        /* Lock the body so the only thing that scrolls is the inner .screen. */
        position:fixed;inset:0;
        width:100%;height:100%;
        overflow:hidden;
      }
      #root{position:fixed;inset:0;overflow:hidden;}
      .phone-wrapper{
        padding:0;
        height:100dvh;min-height:100dvh;max-height:100dvh;
        overflow:hidden;
      }
      .phone-shell{
        width:100vw;height:100dvh;
        border-radius:0 !important;border-width:0 !important;
        transform:none !important;
        overflow:hidden;
      }
      .proto-pills{display:none !important;}
    }

    /* Touch devices — disable hover effects that cause visual issues */
    @media(hover:none){
      .card-lift:hover{transform:none;box-shadow:none;}
      .btn-gold:hover{filter:none;transform:none;}
      .btn-outline:hover{background:transparent;border-color:${T.goldBorder};box-shadow:none;}
      .press:hover{transform:none;opacity:1;}
    }

    /* Reduced motion — honor prefers-reduced-motion (WCAG 2.3.3) */
    @media (prefers-reduced-motion:reduce){
      *,*::before,*::after{
        animation-duration:.01ms !important;
        animation-iteration-count:1 !important;
        transition-duration:.01ms !important;
        scroll-behavior:auto !important;
      }
    }
  `}</style>
);

// ─── HOOKS ────────────────────────────────────────────────────────────────────
// Long-press handler — used for the prototype reset gesture on the logo.
// Returns event handlers to spread onto an element. Cancels on move/leave/cancel.
const useLongPress = (onLongPress, ms = 700) => {
  const timer = useRef(null);
  const fired = useRef(false);
  const start = useCallback(() => {
    fired.current = false;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { fired.current = true; onLongPress(); }, ms);
  }, [onLongPress, ms]);
  const cancel = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
  }, []);
  return {
    onMouseDown: start,
    onMouseUp: cancel,
    onMouseLeave: cancel,
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchCancel: cancel,
    onTouchMove: cancel,
    onContextMenu: e => e.preventDefault(), // suppress mobile long-press context menu
  };
};

// Modal a11y: closes on Esc, restores focus to the element that opened it (WCAG 2.1.2, 2.4.3)
const useModalDismiss = (onClose) => {
  useEffect(() => {
    const prev = document.activeElement;
    const onKey = e => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (prev && typeof prev.focus === "function") { try { prev.focus(); } catch(_){} }
    };
  }, [onClose]);
};

// ─── DATA ─────────────────────────────────────────────────────────────────────
const MOVIES = [
  { id:1, title:"Wuthering Heights", genre:"Romance", rating:"R", runtime:"2h 16m", score:6.3, rtFresh:58, rtAudience:84, year:2026, desc:"Emerald Fennell's visceral reimagining of Emily Brontë's gothic classic, following the tempestuous and destructive bond between Catherine Earnshaw and Heathcliff across the Yorkshire moors.", color:"#1a080e", poster:"🌿", cast:["Margot Robbie","Jacob Elordi","Hong Chau","Shazad Latif"], director:"Emerald Fennell" },
  { id:2, title:"Hoppers", genre:"Animation", rating:"PG", runtime:"1h 44m", score:7.7, rtFresh:94, rtAudience:94, year:2026, desc:"Teenage animal-lover Mabel uses new technology to hop her consciousness into a robotic beaver — and discovers a hidden world of animal secrets that puts her at odds with powerful human forces.", color:"#0a1e0a", poster:"🦫", cast:["Piper Curda","Bobby Moynihan","Jon Hamm","Meryl Streep"], director:"Daniel Chong" },
  { id:3, title:"The Bride!", genre:"Horror", rating:"R", runtime:"2h 6m", score:6.1, rtFresh:59, rtAudience:50, year:2026, desc:"In 1930s Chicago, Frankenstein's Monster persuades an eccentric scientist to resurrect a murdered young woman as his companion — but The Bride has ambitions that go far beyond what either man imagined.", color:"#1a0624", poster:"⚡", cast:["Jessie Buckley","Christian Bale","Annette Bening","Penélope Cruz"], director:"Maggie Gyllenhaal" },
  { id:4, title:"Peaky Blinders: The Immortal Man", genre:"Crime", rating:"R", runtime:"1h 52m", score:7.6, rtFresh:93, rtAudience:94, year:2026, desc:"Self-exiled gangster Tommy Shelby is dragged back to Birmingham when his estranged son becomes entangled in a dangerous Nazi conspiracy, forcing one final reckoning with his past.", color:"#100e08", poster:"🎩", cast:["Cillian Murphy","Barry Keoghan","Rebecca Ferguson","Tim Roth"], director:"Tom Harper" },
  { id:5, title:"undertone", genre:"Horror", rating:"R", runtime:"1h 33m", score:6.5, rtFresh:88, rtAudience:76, year:2026, desc:"A skeptical paranormal podcaster moves home to care for her terminally ill mother and becomes consumed by chilling audio recordings sent by a couple experiencing unexplained phenomena in their home.", color:"#080810", poster:"🎙️", cast:["Nina Kiri","Adam DiMarco","Ari Millen"], director:"Ian Tuason" },
  { id:6, title:"Reminders of Him", genre:"Romance", rating:"PG-13", runtime:"1h 54m", score:7.2, rtFresh:69, rtAudience:84, year:2026, desc:"After serving time for an accident that took her partner's life, Kenna Rowan returns to her Wyoming hometown hoping to reunite with the young daughter she's never known — but the community won't forgive.", color:"#200a0a", poster:"💌", cast:["Maika Monroe","Tyriq Withers","Rudy Pankow","Lauren Graham"], director:"Vanessa Caswill" },
];
const COMING_SOON = [
  { id:7, title:"Project Hail Mary", genre:"Sci-Fi", rating:"PG-13", runtime:"2h 36m", rtFresh:null, year:2026, desc:"A science teacher wakes alone on a spacecraft light-years from Earth with no memory of who he is or how he got there — and must solve an impossible mystery to save humanity.", color:"#04081a", poster:"🚀", releaseDate:"Mar 20" },
  { id:8, title:"Ready or Not 2: Here I Come", genre:"Thriller", rating:"R", runtime:"1h 48m", rtFresh:null, year:2026, desc:"Moments after surviving the Le Domas family's deadly game, Grace discovers she's been thrust into a global hunt by the world's most powerful dynasties — with her estranged sister now caught in the crossfire.", color:"#1a0408", poster:"🃏", releaseDate:"Mar 20" },
  { id:9, title:"They Will Kill You", genre:"Action", rating:"R", runtime:"1h 42m", rtFresh:null, year:2026, desc:"A new housekeeper at a mysterious New York City high-rise discovers that her wealthy employers are members of a murderous Satanic cult — and she's already their next target.", color:"#1a0804", poster:"🗡️", releaseDate:"Mar 27" },
];
const THEATERS = [
  { id:1, name:"ReelView Grand Cineplex", short:"Grand Cineplex", address:"450 Cinema Blvd, Downtown", city:"Los Angeles, CA 90012", phone:"(213) 555-0182", distance:"1.2 mi", screens:14, amenities:["IMAX","Dolby Atmos","Recliner Seats"] },
  { id:2, name:"ReelView Westside 10", short:"Westside 10", address:"1820 Sunset Pkwy", city:"Los Angeles, CA 90025", phone:"(310) 555-0247", distance:"3.8 mi", screens:10, amenities:["Dolby Atmos","Recliner Seats","Bar & Lounge"] },
  { id:3, name:"ReelView Marina Bay", short:"Marina Bay", address:"750 Harbor View Dr", city:"Los Angeles, CA 90292", phone:"(310) 555-0391", distance:"6.1 mi", screens:8, amenities:["Dolby Atmos","Recliner Seats"] },
];


// Pricing per format (adult base) — fee is baked in for display
const FORMAT_PRICING = { "IMAX":21.49, "Dolby Atmos":19.49, "Standard":16.50 };
const SERVICE_FEE_GLOBAL = 2.19;
const MATINEE_CUTOFF = "4:00 PM"; // Shows before this get matinee discount
const MATINEE_DISCOUNT = 3.00;
const isMatinee = t => { const h = parseInt(t); const pm = t.includes("PM"); return (!pm || h === 12) && !(pm && h !== 12); };

// Showroom types — different sizes/experiences per format & theater
const SHOWROOMS = {
  "IMAX":       { name:"IMAX Grand Auditorium", screenNum:1, seats:224, tag:"IMAX Experience", size:"large",
                  rows:["A","B","C","D","E","F","G","H","I","J","K","L","M","N"], cols:16, accRow:"E",
                  premRows:["G","H","I","J"], frontEnd:"F", midEnd:"J",
                  sections:{A:{label:"FRONT",sub:"Rows A–F"},G:{label:"CENTER",sub:"Rows G–J"},K:{label:"REAR",sub:"Elevated · Rows K–N"}} },
  "Dolby Atmos":{ name:"Dolby Cinema", screenNum:2, seats:154, tag:"Medium", size:"medium",
                  rows:["A","B","C","D","E","F","G","H","I","J","K"], cols:14, accRow:"D",
                  premRows:["E","F","G"], frontEnd:"C", midEnd:"G",
                  sections:{A:{label:"FRONT",sub:"Rows A–C"},E:{label:"CENTER",sub:"Rows E–G"},H:{label:"REAR",sub:"Elevated · Rows H–K"}} },
  "Standard-L": { name:"Auditorium 1", screenNum:3, seats:208, tag:"Large", size:"large",
                  rows:["A","B","C","D","E","F","G","H","I","J","K","L","M"], cols:16, accRow:"E",
                  premRows:["G","H","I"], frontEnd:"D", midEnd:"I",
                  sections:{A:{label:"FRONT",sub:"Rows A–D"},G:{label:"CENTER",sub:"Rows G–I"},J:{label:"REAR",sub:"Elevated · Rows J–M"}} },
  "Standard-M": { name:"Auditorium 5", screenNum:5, seats:132, tag:"Medium", size:"medium",
                  rows:["A","B","C","D","E","F","G","H","I","J","K"], cols:12, accRow:"D",
                  premRows:["E","F","G"], frontEnd:"C", midEnd:"G",
                  sections:{A:{label:"FRONT",sub:"Rows A–C"},E:{label:"CENTER",sub:"Rows E–G"},H:{label:"REAR",sub:"Elevated · Rows H–K"}} },
  "Standard-S": { name:"Screening Room 3", screenNum:8, seats:70, tag:"Intimate", size:"small",
                  rows:["A","B","C","D","E","F","G"], cols:10, accRow:"C",
                  premRows:["D","E"], frontEnd:"B", midEnd:"E",
                  sections:{A:{label:"FRONT",sub:"Rows A–B"},D:{label:"CENTER",sub:"Rows D–E"},F:{label:"REAR",sub:"Rows F–G"}} },
};
const SHOWTIMES_DATA = {
  1: { formats:[
    { fmt:"IMAX", tagline:"PREMIUM IMMERSION", amenities:"Heated Recliners · Reserved Seating · Closed Caption · Audio Description",
      showroom:"IMAX",
      times:["10:15 AM","1:30 PM","4:45 PM","8:00 PM","11:00 PM"],
      seatsLeft:{  "8:00 PM":12, "11:00 PM":38 } },
    { fmt:"Dolby Atmos", tagline:"3D AUDIO", amenities:"Heated Recliners · Dine-In Delivery · Reserved Seating · Closed Caption",
      showroom:"Dolby Atmos",
      times:["12:00 PM","3:20 PM","6:50 PM","10:10 PM"],
      seatsLeft:{ "6:50 PM":8 } },
    { fmt:"Standard", tagline:"", amenities:"Heated Recliners · Reserved Seating · Closed Caption",
      showroom:"Standard-L",
      times:["11:00 AM","2:00 PM","5:15 PM","8:30 PM"] },
  ]},
  2: { formats:[
    { fmt:"Dolby Atmos", tagline:"3D AUDIO", amenities:"Heated Recliners · Reserved Seating · Closed Caption",
      showroom:"Dolby Atmos",
      times:["2:30 PM","5:45 PM","9:00 PM"],
      seatsLeft:{ "9:00 PM":15 } },
    { fmt:"Standard", tagline:"", amenities:"Recliner Seats · Reserved Seating",
      showroom:"Standard-M",
      times:["1:15 PM","4:20 PM","7:30 PM","10:45 PM"] },
  ]},
  3: { formats:[
    { fmt:"Standard", tagline:"", amenities:"Recliner Seats · Reserved Seating · Audio Description",
      showroom:"Standard-S",
      times:["12:30 PM","3:45 PM","7:00 PM"] },
  ]},
};
const CONCESSION_IMAGES = {
  1: "https://images.unsplash.com/photo-1585647347483-22b66260dfff?w=200&h=200&fit=crop",  // Popcorn
  3: "https://images.unsplash.com/photo-1513456852971-30c0b8199d4d?w=200&h=200&fit=crop",  // Nachos
  5: "https://images.unsplash.com/photo-1619740455993-9e612b1af08a?w=200&h=200&fit=crop",  // Hotdog
  6: "https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?w=200&h=200&fit=crop",  // Candy assortment
  7: "https://images.unsplash.com/photo-1622483767028-3f66f32aef97?w=200&h=200&fit=crop",  // Fountain drink / soda cup
  8: "https://images.unsplash.com/photo-1616118132534-381148898bb4?w=200&h=200&fit=crop",  // Plastic water bottle
};
const CONCESSIONS = [
  { id:1, name:"Popcorn", emoji:"🍿", cat:"Snacks", sizes:{S:5.50,M:7.50,L:9.50} },
  { id:7, name:"Fountain Drink", emoji:"🥤", cat:"Drinks", sizes:{S:3.50,M:4.50,L:5.50} },
  { id:3, name:"Nachos & Cheese", price:8.75, emoji:"🧀", cat:"Snacks" },
  { id:5, name:"Hotdog", price:7.25, emoji:"🌭", cat:"Snacks" },
  { id:6, name:"Candy Box", price:5.00, emoji:"🍬", cat:"Snacks" },
  { id:8, name:"Bottled Water", price:3.50, emoji:"💧", cat:"Drinks" },
];
// Helper: get price for a cart key like "1:M" or "3"
const getCartItemPrice = (key) => {
  const [idStr,size] = key.split(":");
  const item = CONCESSIONS.find(c=>c.id===+idStr);
  if(!item) return 0;
  return item.sizes ? (item.sizes[size]||item.sizes.M) : item.price;
};
const getCartItemName = (key) => {
  const [idStr,size] = key.split(":");
  const item = CONCESSIONS.find(c=>c.id===+idStr);
  if(!item) return "";
  return item.sizes ? `${item.name} (${size})` : item.name;
};

// ─── LOGO ─────────────────────────────────────────────────────────────────────
const Logo = ({ size=1 }) => (
  <div style={{display:"flex",alignItems:"center",gap:7*size,userSelect:"none"}}>
    <svg width={26*size} height={26*size} viewBox="0 0 26 26" fill="none">
      {/* Outer reel ring */}
      <circle cx="13" cy="13" r="12" stroke={T.gold} strokeWidth="1.5"/>
      {/* Center hub */}
      <circle cx="13" cy="13" r="3.2" fill={T.gold} opacity=".9"/>
      <circle cx="13" cy="13" r="1.6" fill={T.navy}/>
      {/* Sprocket holes — evenly spaced around the reel */}
      <circle cx="13" cy="5.2" r="1.8" fill={T.navy} stroke={T.gold} strokeWidth="1"/>
      <circle cx="19.6" cy="9.1" r="1.8" fill={T.navy} stroke={T.gold} strokeWidth="1"/>
      <circle cx="19.6" cy="16.9" r="1.8" fill={T.navy} stroke={T.gold} strokeWidth="1"/>
      <circle cx="13" cy="20.8" r="1.8" fill={T.navy} stroke={T.gold} strokeWidth="1"/>
      <circle cx="6.4" cy="16.9" r="1.8" fill={T.navy} stroke={T.gold} strokeWidth="1"/>
      <circle cx="6.4" cy="9.1" r="1.8" fill={T.navy} stroke={T.gold} strokeWidth="1"/>
      {/* Spokes from hub to sprockets */}
      <path d="M13 6.4V9.8M13 16.2v3.4M17.7 10.3l-2.9 1.7M11.2 14l-2.9 1.7M17.7 15.7l-2.9-1.7M11.2 12l-2.9-1.7" stroke={T.gold} strokeWidth=".8" opacity=".5"/>
    </svg>
    <span style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:17*size,color:T.white,letterSpacing:".02em"}}>
      Reel<span style={{color:T.gold}}>View</span>
    </span>
  </div>
);

// ─── BOTTOM NAV ───────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  {id:"movies",label:"Movies",icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M8 4v16M16 4v16M2 9h4M18 9h4M2 15h4M18 15h4"/></svg>},
  {id:"theaters",label:"Theaters",icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>},
  {id:"concessions",label:"Concessions",icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22"><path d="M8 22h8l1-12H7l1 12z"/><path d="M7 10c0-1.5.8-3 2-3.5C9.2 5 10.5 4 12 4s2.8 1 3 2.5c1.2.5 2 2 2 3.5"/><circle cx="10" cy="6.5" r="1.5"/><circle cx="14" cy="5.5" r="1.5"/><circle cx="12" cy="8" r="1.5"/></svg>},
  {id:"myreelview",label:"My ReelView",icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>},
];

const BottomNav = ({ active, nav }) => (
  <nav aria-label="Main navigation" style={{width:"100%",background:T.navyGlass,backdropFilter:"blur(24px)",borderTop:`1px solid ${T.glassBorder}`,display:"flex",flexShrink:0,zIndex:200}}>
    {NAV_ITEMS.map(it => {
      const on = active === it.id;
      return (
        <button key={it.id} onClick={()=>nav(it.id)} style={{flex:1,padding:"9px 0 12px",background:"none",border:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
          <span style={{color:on?T.gold:T.grey,transition:"color .2s"}}>{it.icon}</span>
          <span style={{fontSize:9,fontWeight:on?700:500,color:on?T.gold:T.grey,letterSpacing:".04em",textTransform:"uppercase",transition:"color .2s"}}>{it.label}</span>
          {on&&<div style={{width:16,height:2,background:T.gold,borderRadius:1,marginTop:1}}/>}
        </button>
      );
    })}
  </nav>
);

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const QuestionIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{display:"inline",verticalAlign:"middle",marginLeft:3,cursor:"help"}}>
    <circle cx="7" cy="7" r="6.3" stroke={T.grey} strokeWidth="1.2"/>
    <text x="7" y="11" textAnchor="middle" fill={T.grey} fontSize="9" fontFamily="DM Sans" fontWeight="700">?</text>
  </svg>
);

const RTScore = ({ pct, type }) => (
  <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${T.glassBorder}`}}>
    <div style={{width:36,height:36,borderRadius:"50%",background:type==="fresh"?"rgba(201,168,76,.15)":"rgba(255,100,50,.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>
      {type==="fresh"?"🍅":"🍿"}
    </div>
    <div>
      <div style={{fontSize:16,fontWeight:700,color:T.white}}>{pct}%</div>
      <div style={{fontSize:11,color:T.grey}}>{type==="fresh"?"Tomatometer":"Audience Score"}</div>
    </div>
  </div>
);

// ─── MOVIE POSTERS — Official TMDb poster images ─────────────────────────────
const POSTER_IMAGES = {
  1: "https://image.tmdb.org/t/p/w500/3YBce6dTh1D5oCMITXk2S5QhPt.jpg",   // Wuthering Heights
  2: "https://image.tmdb.org/t/p/w500/xjtWQ2CL1mpmMNwuU5HeS4Iuwuu.jpg",   // Hoppers
  3: "https://image.tmdb.org/t/p/w500/lV8YHwGkYZsm6EfIqnhaSz2avKt.jpg",   // The Bride!
  4: "https://image.tmdb.org/t/p/w500/gRMalasZEzsZi4w2VFuYusfSfqf.jpg",   // Peaky Blinders: The Immortal Man
  5: "https://image.tmdb.org/t/p/w500/1wREWUEHzjDWSreNFbFLDO7YEaM.jpg",   // undertone
  6: "https://image.tmdb.org/t/p/w500/8hEBaGD2sx4uVe4A1E44NkFIrDm.jpg",   // Reminders of Him
  7: "https://image.tmdb.org/t/p/w500/vOhW7Lbt4kZAqe0vJhVC4Ced9x2.jpg",   // Project Hail Mary
  8: "https://image.tmdb.org/t/p/w500/13ZcJzSGEqVgDSqsS9U5EkQwPkV.jpg",   // Ready or Not 2
  9: "https://image.tmdb.org/t/p/w500/j1yZpgJzjDCbXyA7voSC7SEhcZY.jpg",   // They Will Kill You
};
const POSTER_COLORS = {
  1:"#1a080e",  // Wuthering Heights — dark moors
  2:"#0a1e0a",  // Hoppers — forest green
  3:"#1a0624",  // The Bride! — gothic purple
  4:"#100e08",  // Peaky Blinders — industrial shadow
  5:"#080810",  // undertone — near black
  6:"#200a0a",  // Reminders of Him — warm deep red
  7:"#04081a",  // Project Hail Mary — deep space
  8:"#1a0408",  // Ready or Not 2 — dark blood red
  9:"#1a0804",  // They Will Kill You — dark ember
};
// ─── HERO BACKDROP IMAGES — reuse poster images displayed as cinematic backdrops ──
const MoviePoster = ({ movieId, width=150, height=215, mini=false, title }) => {
  const src = POSTER_IMAGES[movieId];
  const fallbackBg = POSTER_COLORS[movieId] || "#080D1A";
  return (
    <div style={{width,height,borderRadius:mini?7:10,overflow:"hidden",position:"relative",background:fallbackBg}}>
      {src && <img src={src} alt={title||"Movie poster"} style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top"}} loading="lazy"/>}
      {/* Subtle cinematic vignette — preserves the official poster art */}
      <div style={{position:"absolute",inset:0,background:`linear-gradient(180deg,rgba(0,0,0,.08) 0%,transparent 35%,rgba(0,0,0,.18) 100%)`}}/>
      <div style={{position:"absolute",inset:0,background:`linear-gradient(145deg,${fallbackBg}22,transparent 50%)`}}/>
    </div>
  );
};

// Seat preview component — per-showtime availability
const InlineSeatPreview = ({ showtimeKey, times, seatsLeft, showroom }) => {
  const [selTime, setSelTime] = useState(times?.[0] || null);
  const rm = SHOWROOMS[showroom] || SHOWROOMS["Standard-M"];
  const PREV_ROWS = rm.rows;
  const PREV_COLS = rm.cols;
  const ACC_ROW = rm.accRow;
  const PREM_ROWS = rm.premRows;
  const sectionAt = r => rm.sections[r]?.label || null;

  // Deterministic pseudo-random per showtime (hash-based for even distribution)
  const hashStr = s => { let h=0; for(let i=0;i<s.length;i++) h=((h<<5)-h)+s.charCodeAt(i)|0; return Math.abs(h); };
  const seed = hashStr(`${showtimeKey}-${selTime}`);
  const prng = i => { let h=(seed+i*374761393)|0; h=Math.imul(h^(h>>>15),0x1a85ec53); h=Math.imul(h^(h>>>13),0xc4ae3d47); return ((h^(h>>>16))>>>0)/4294967296; };

  const allSeats = PREV_ROWS.flatMap(r=>Array.from({length:PREV_COLS},(_,i)=>`${r}${i+1}`));
  const left = seatsLeft?.[selTime];
  // Popular showtimes fill more seats
  const fillPct = left!=null ? Math.min(0.82, Math.max(0.25, 1-left/(PREV_ROWS.length*PREV_COLS))) : 0.12+prng(999)*0.28;
  const TAKEN_SET = new Set();
  allSeats.forEach((s,i) => { if(prng(i+1)<fillPct) TAKEN_SET.add(s); });
  const available = allSeats.length - TAKEN_SET.size;

  return (
    <div style={{background:T.navyMid,borderRadius:12,padding:"12px 8px 10px",marginTop:10,animation:"fadeIn .2s ease"}}>
      {/* Showtime selector */}
      {times&&times.length>1&&(
        <div style={{display:"flex",gap:4,flexWrap:"wrap",padding:"0 4px",marginBottom:8}}>
          {times.map(t=>{
            const on=selTime===t;
            const tLeft=seatsLeft?.[t];
            return(
              <button key={t} onClick={()=>setSelTime(t)} style={{padding:"4px 10px",borderRadius:6,fontSize:10,fontWeight:600,background:on?T.goldDim:"transparent",border:`1px solid ${on?T.goldBorder:"rgba(138,148,168,.55)"}`,color:on?T.goldLight:T.offwhite,cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontVariantNumeric:"tabular-nums",transition:"background .15s,border-color .15s,color .15s",display:"flex",alignItems:"center",gap:3}}>
                {t}
                {tLeft!=null&&tLeft<=20&&<span style={{width:4,height:4,borderRadius:"50%",background:tLeft<=10?T.red:"rgba(255,160,0,.7)"}}/>}
              </button>
            );
          })}
        </div>
      )}

      {/* Availability summary */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 6px",marginBottom:6}}>
        <span style={{fontSize:8,color:T.grey}}>{available} of {allSeats.length} seats available</span>
        {left!=null&&left<=20&&<span style={{fontSize:8,color:T.red,fontWeight:700}}>{left<=10?"Almost sold out!":"Filling fast"}</span>}
      </div>

      {/* Screen arc — scales with room size */}
      {(() => {
        const arcW = rm.size==="large"?240:rm.size==="small"?150:200;
        const arcH = rm.size==="large"?14:rm.size==="small"?10:12;
        return (
          <div style={{textAlign:"center",marginBottom:6}}>
            <svg width={arcW} height={arcH} viewBox={`0 0 ${arcW} ${arcH}`} fill="none" style={{display:"block",margin:"0 auto"}}>
              <path d={`M4 ${arcH-2} Q${arcW/2} 1 ${arcW-4} ${arcH-2}`} stroke={T.gold} strokeWidth="1.5" fill="none" opacity=".6" strokeLinecap="round"/>
            </svg>
            <div style={{fontSize:7,color:T.grey,letterSpacing:".14em",marginTop:1}}>SCREEN</div>
          </div>
        );
      })()}

      {/* Seat grid with section labels — scaled to room size */}
      {(() => {
        const mW = rm.size==="large"?8:rm.size==="small"?13:11;
        const mH = rm.size==="large"?7:rm.size==="small"?11:9;
        const mAW = rm.size==="large"?8:rm.size==="small"?13:11;
        const mAH = rm.size==="large"?7:rm.size==="small"?12:10;
        const mPG = rm.size==="large"?2:3;
        return (
      <div style={{display:"flex",flexDirection:"column",gap:0,alignItems:"center"}}>
        {PREV_ROWS.flatMap(row => {
          const sec = sectionAt(row);
          const isPrem = PREM_ROWS.includes(row);
          const isAcc = row === ACC_ROW;
          const seatRow = (
            <div key={row} style={{display:"flex",alignItems:"center",gap:1,marginBottom:rm.size==="large"?1:2}}>
              <span style={{fontSize:rm.size==="large"?5:6,color:T.grey,width:8,marginRight:1,alignSelf:"stretch",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>{row}</span>
              <div style={{display:"flex",gap:0}}>
                {Array.from({length:PREV_COLS},(_,i) => {
                  const id = `${row}${i+1}`;
                  const taken = TAKEN_SET.has(id);
                  const pairGap = i > 0 && i % 2 === 0;
                  const isLeftOfPair = i % 2 === 0;
                  return (
                    <div key={i} style={{display:"flex",marginLeft:pairGap?mPG:0}}>
                      {isAcc ? (
                        <div style={{width:mAW,height:mAH,borderRadius:isLeftOfPair?"3px 0 0 3px":"0 3px 3px 0",background:taken?"rgba(255,255,255,.06)":"rgba(100,160,255,.25)",border:`1px solid ${taken?"rgba(255,255,255,.10)":"rgba(100,160,255,.4)"}`,display:"flex",alignItems:"center",justifyContent:"center",opacity:taken?.45:1,marginRight:isLeftOfPair?-1:0}}>
                          {taken ? <svg width={mAW-2} height={mAH-2} viewBox={`0 0 ${mAW-2} ${mAH-2}`} style={{display:"block"}}><line x1="2" y1="2" x2={mAW-4} y2={mAH-4} stroke="rgba(200,205,215,.5)" strokeWidth={rm.size==="large"?.8:1}/><line x1={mAW-4} y1="2" x2="2" y2={mAH-4} stroke="rgba(200,205,215,.5)" strokeWidth={rm.size==="large"?.8:1}/></svg> : <span style={{fontSize:rm.size==="large"?4:5,color:"rgba(120,180,255,.95)",fontWeight:700}}>♿</span>}
                        </div>
                      ) : (
                        <div style={{
                          width:mW,height:mH,
                          borderRadius:isLeftOfPair?"2px 0 0 1px":"0 2px 1px 0",
                          background:taken?"rgba(255,255,255,.06)":"rgba(201,168,76,.12)",
                          border:`1px solid ${taken?"rgba(255,255,255,.10)":"rgba(201,168,76,.32)"}`,
                          opacity:taken?.45:1,
                          marginRight:isLeftOfPair?-1:0,
                          display:"flex",alignItems:"center",justifyContent:"center",
                        }}>
                          {taken && <svg width={mW-2} height={mH-2} viewBox={`0 0 ${mW-2} ${mH-2}`} style={{display:"block"}}><line x1="2" y1="2" x2={mW-4} y2={mH-4} stroke="rgba(200,205,215,.5)" strokeWidth={rm.size==="large"?.8:1}/><line x1={mW-4} y1="2" x2="2" y2={mH-4} stroke="rgba(200,205,215,.5)" strokeWidth={rm.size==="large"?.8:1}/></svg>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <span style={{fontSize:rm.size==="large"?5:6,color:T.grey,width:8,marginLeft:1,alignSelf:"stretch",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>{row}</span>
            </div>
          );
          if (sec) {
            return [
              <div key={`sec-${row}`} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 8px 2px",width:"100%"}}>
                <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${T.glassBorder},transparent)`}}/>
                <span style={{fontSize:7,fontWeight:700,color:T.grey,letterSpacing:".1em"}}>{sec}</span>
                <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${T.glassBorder},transparent)`}}/>
              </div>,
              seatRow,
            ];
          }
          return [seatRow];
        })}
      </div>
        );
      })()}

      {/* Exit markers */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:4,marginTop:6,padding:"0 8px"}}>
        {rm.size==="large" && <span style={{fontSize:7,color:T.green,fontWeight:700}}>EXIT</span>}
        <div style={{flex:1,borderTop:`1px dashed ${T.greyDim}`}}/>
        <span style={{fontSize:7,color:T.grey,letterSpacing:".08em"}}>BACK</span>
        <div style={{flex:1,borderTop:`1px dashed ${T.greyDim}`}}/>
        <span style={{fontSize:7,color:T.green,fontWeight:700}}>EXIT</span>
      </div>

      {/* Mini legend */}
      <div style={{display:"flex",gap:8,justifyContent:"center",marginTop:6}}>
        {[
          {bg:"rgba(201,168,76,.12)",bd:"rgba(201,168,76,.32)",l:"Available"},
          {bg:"rgba(255,255,255,.06)",bd:"rgba(255,255,255,.10)",l:"Occupied",occ:true},
          {bg:"rgba(100,160,255,.25)",bd:"rgba(100,160,255,.4)",l:"Accessible"},
        ].map(({bg,bd,l,occ})=>(
          <div key={l} style={{display:"flex",alignItems:"center",gap:2}}>
            <div style={{width:7,height:6,borderRadius:1,background:bg,border:`1px solid ${bd}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
              {occ && <svg width="5" height="4" viewBox="0 0 5 4" style={{display:"block"}}><line x1="1" y1="1" x2="4" y2="3" stroke="rgba(180,185,195,.5)" strokeWidth=".8"/><line x1="4" y1="1" x2="1" y2="3" stroke="rgba(180,185,195,.5)" strokeWidth=".8"/></svg>}
            </div>
            <span style={{fontSize:7,color:T.offwhite}}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── FILTER SHEET ─────────────────────────────────────────────────────────────
const FilterSheet = ({ onClose, sortBy, setSortBy, ratings, setRatings, genres, setGenres, runtime, setRuntime }) => {
  useModalDismiss(onClose);
  const toggleArr = (arr,setArr,val) => setArr(a=>a.includes(val)?a.filter(x=>x!==val):[...a,val]);
  const GENRES = ["3D","Action/Adventure","Animated","Art House/Foreign","Comedy","Special Events","Documentary","Drama","Family","Horror","IMAX","LGBTQ+","Romance","Sci-Fi/Fantasy","Suspense/Thriller"];
  const RATINGS = ["G","PG","PG-13","R","NC-17","NC"];
  const RUNTIMES = ["Less than 1 hour","1 to 1.5 hours","1.5 to 2 hours","2 to 2.5 hours","More than 2.5 hours"];
  const total = ratings.length+genres.length+runtime.length;

  return (
    <div style={{position:"absolute",inset:0,zIndex:300}} onClick={onClose}>
      <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.6)"}}/>
      <div onClick={e=>e.stopPropagation()} style={{position:"absolute",bottom:0,left:0,right:0,background:T.navyMid,borderRadius:"20px 20px 0 0",maxHeight:"85%",display:"flex",flexDirection:"column",overflow:"hidden",overscrollBehavior:"contain",boxShadow:"0 -8px 40px rgba(0,0,0,.5)"}}>
        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 12px",borderBottom:`1px solid ${T.glassBorder}`,flexShrink:0}}>
          <span style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700}}>Filter & Sort</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.grey,fontSize:20,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        {/* Scrollable body */}
        <div style={{flex:1,overflowY:"auto",minHeight:0}}>
          <div style={{padding:"16px 20px"}}>
            {/* Sort */}
            <div style={{fontSize:13,fontWeight:700,color:T.white,marginBottom:12,letterSpacing:".04em",textTransform:"uppercase"}}>Sort By</div>
            {["Popularity","Release Date (oldest to newest)","Release Date (newest to oldest)","Runtime"].map(s=>(
              <div key={s} role="radio" aria-checked={sortBy===s} tabIndex={0} onClick={()=>setSortBy(s)} onKeyDown={e=>{if(e.key===" "||e.key==="Enter"){e.preventDefault();setSortBy(s);}}} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:`1px solid ${T.glassBorder}`,cursor:"pointer"}}>
                <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${sortBy===s?T.gold:T.greyDim}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {sortBy===s&&<div style={{width:9,height:9,borderRadius:"50%",background:T.gold}}/>}
                </div>
                <span style={{fontSize:14,color:sortBy===s?T.white:T.grey}}>{s}</span>
              </div>
            ))}

            {/* MPA Ratings */}
            <div style={{fontSize:13,fontWeight:700,color:T.white,margin:"20px 0 12px",letterSpacing:".04em",textTransform:"uppercase"}}>MPA Ratings</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {RATINGS.map(r=>(
                <button key={r} onClick={()=>toggleArr(ratings,setRatings,r)} style={{padding:"7px 14px",borderRadius:8,background:ratings.includes(r)?T.goldDim:"transparent",color:ratings.includes(r)?T.goldLight:T.offwhite,border:`1.5px solid ${ratings.includes(r)?T.goldBorder:T.greyDim}`,cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:600,fontSize:13}}>{r}</button>
              ))}
            </div>

            {/* Genres */}
            <div style={{fontSize:13,fontWeight:700,color:T.white,margin:"20px 0 12px",letterSpacing:".04em",textTransform:"uppercase"}}>Genres</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {GENRES.map(g=>(
                <button key={g} onClick={()=>toggleArr(genres,setGenres,g)} style={{padding:"7px 12px",borderRadius:8,background:genres.includes(g)?T.goldDim:"transparent",color:genres.includes(g)?T.goldLight:T.offwhite,border:`1.5px solid ${genres.includes(g)?T.goldBorder:T.greyDim}`,cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:500,fontSize:12}}>{g}</button>
              ))}
            </div>

            {/* Runtime */}
            <div style={{fontSize:13,fontWeight:700,color:T.white,margin:"20px 0 12px",letterSpacing:".04em",textTransform:"uppercase"}}>Runtime</div>
            {RUNTIMES.map(r=>(
              <div key={r} role="checkbox" aria-checked={runtime.includes(r)} tabIndex={0} onClick={()=>toggleArr(runtime,setRuntime,r)} onKeyDown={e=>{if(e.key===" "||e.key==="Enter"){e.preventDefault();toggleArr(runtime,setRuntime,r);}}} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 0",borderBottom:`1px solid ${T.glassBorder}`,cursor:"pointer"}}>
                <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${runtime.includes(r)?T.gold:T.greyDim}`,background:runtime.includes(r)?T.gold:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {runtime.includes(r)&&<span style={{fontSize:11,color:T.navy,fontWeight:900}}>✓</span>}
                </div>
                <span style={{fontSize:14,color:runtime.includes(r)?T.white:T.grey}}>{r}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Footer — always at bottom */}
        <div style={{flexShrink:0,background:T.navyMid,padding:"12px 20px 24px",borderTop:`1px solid ${T.glassBorder}`,display:"flex",gap:12}}>
          <button onClick={()=>{setSortBy("Popularity");setRatings([]);setGenres([]);setRuntime([]);}} style={{background:"none",border:"none",color:T.gold,fontFamily:"'Outfit',sans-serif",fontWeight:600,fontSize:14,cursor:"pointer"}}>Clear All</button>
          <button className="btn-gold" onClick={onClose} style={{flex:1,padding:13,fontSize:14}}>Show Movies{total>0?` (${total} filter${total>1?"s":""})`:"" }</button>
        </div>
      </div>
    </div>
  );
};

// Helper: parse runtime string like "2h 16m" to total minutes
const parseRuntimeMins = (rt) => {
  const hm = rt.match(/(\d+)h\s*(\d+)m/);
  if(hm) return parseInt(hm[1])*60 + parseInt(hm[2]);
  const hOnly = rt.match(/(\d+)h/);
  if(hOnly) return parseInt(hOnly[1])*60;
  const mOnly = rt.match(/(\d+)m/);
  if(mOnly) return parseInt(mOnly[1]);
  return 120;
};

// Helper: check if runtime falls in a filter bucket
const runtimeInBucket = (mins, bucket) => {
  if(bucket==="Less than 1 hour") return mins<60;
  if(bucket==="1 to 1.5 hours") return mins>=60 && mins<=90;
  if(bucket==="1.5 to 2 hours") return mins>90 && mins<=120;
  if(bucket==="2 to 2.5 hours") return mins>120 && mins<=150;
  if(bucket==="More than 2.5 hours") return mins>150;
  return false;
};

// Helper: apply movie filters and sorting
const applyMovieFilters = (movies, sortBy, ratings, genres, runtime) => {
  let result = [...movies];
  // Filter by rating
  if(ratings.length>0) result = result.filter(m=>ratings.includes(m.rating));
  // Filter by genre — match if the movie genre contains any selected genre keyword
  if(genres.length>0) result = result.filter(m=>genres.some(g=>{
    const gl = g.toLowerCase();
    const mg = m.genre.toLowerCase();
    if(gl==="animated"||gl==="animation") return mg.includes("anim");
    if(gl==="suspense/thriller") return mg.includes("thriller")||mg.includes("suspense");
    if(gl==="sci-fi/fantasy") return mg.includes("sci-fi")||mg.includes("fantasy");
    if(gl==="action/adventure") return mg.includes("action")||mg.includes("adventure");
    return mg.includes(gl);
  }));
  // Filter by runtime
  if(runtime.length>0) result = result.filter(m=>{
    const mins = parseRuntimeMins(m.runtime);
    return runtime.some(b=>runtimeInBucket(mins,b));
  });
  // Sort
  if(sortBy==="Release Date (newest to oldest)") result.sort((a,b)=>b.year-a.year || b.id-a.id);
  else if(sortBy==="Release Date (oldest to newest)") result.sort((a,b)=>a.year-b.year || a.id-b.id);
  else if(sortBy==="Runtime") result.sort((a,b)=>parseRuntimeMins(a.runtime)-parseRuntimeMins(b.runtime));
  // "Popularity" = default order (by id)
  return result;
};

// ══════════════════════════════════════════════════════
// MOVIES HOME SCREEN
// ══════════════════════════════════════════════════════
const MoviesScreen = ({ nav, setMovie, favs, toggleFav, tab, setTab, onResetPrototype, onOpenDemoInfo, logoFlash }) => {
  // Long-press the ReelView logo for 700ms to reset the demo.
  const longPressBindings = useLongPress(() => onResetPrototype && onResetPrototype(), 700);
  const [heroIdx, setHeroIdx] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [sortBy, setSortBy] = useState("Popularity");
  const [ratings, setRatings] = useState([]);
  const [genres, setGenres] = useState([]);
  const [runtime, setRuntime] = useState([]);
  const heroes = MOVIES.slice(0,5);
  const hero = heroes[heroIdx];
  const filteredMovies = applyMovieFilters(MOVIES, sortBy, ratings, genres, runtime);
  const hasFilters = ratings.length>0 || genres.length>0 || runtime.length>0 || sortBy!=="Popularity";

  const goMovie = (m) => { setMovie(m); nav("movie-detail"); };

  // Hero swipe support
  const heroTouchRef = useRef({ startX:0, startY:0 });
  const handleHeroTouchStart = (e) => {
    heroTouchRef.current.startX = e.touches[0].clientX;
    heroTouchRef.current.startY = e.touches[0].clientY;
  };
  const handleHeroTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - heroTouchRef.current.startX;
    const dy = e.changedTouches[0].clientY - heroTouchRef.current.startY;
    // Only register horizontal swipe if it's clearly horizontal (not a scroll)
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) setHeroIdx(i => Math.min(i + 1, heroes.length - 1)); // swipe left → next
      else setHeroIdx(i => Math.max(i - 1, 0)); // swipe right → prev
    }
  };

  const PosterCard = ({ movie, width="100%", height=null }) => (
    <div className="press card-lift" onClick={()=>goMovie(movie)} style={{width,position:"relative",minWidth:0}}>
      <div style={{width:"100%",aspectRatio:"150 / 215",borderRadius:10,overflow:"hidden",position:"relative",border:`1px solid ${T.glassBorder}`}}>
        <MoviePoster movieId={movie.id} width="100%" height="100%" title={movie.title}/>
        {/* Play overlay */}
        <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",opacity:0,transition:"opacity .2s"}} className="play-overlay">
          <div style={{width:36,height:36,borderRadius:"50%",border:`2px solid ${T.white}`,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{width:0,height:0,borderTop:"7px solid transparent",borderBottom:"7px solid transparent",borderLeft:`12px solid ${T.white}`,marginLeft:2}}/>
          </div>
        </div>
        {/* Heart */}
        <button aria-label={favs.includes(movie.id)?`Remove ${movie.title} from favorites`:`Add ${movie.title} to favorites`} onClick={e=>toggleFav(e,movie.id)} style={{position:"absolute",top:7,right:7,background:"rgba(0,0,0,.55)",border:"none",borderRadius:"50%",width:28,height:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:favs.includes(movie.id)?T.gold:T.white,transition:"color .2s"}}>
          {favs.includes(movie.id)?"♥":"♡"}
        </button>
        {movie.releaseDate&&<div style={{position:"absolute",bottom:8,left:8,background:T.goldDim,border:`1px solid ${T.goldBorder}`,borderRadius:5,padding:"2px 7px",fontSize:10,fontWeight:700,color:T.goldLight}}>{movie.releaseDate}</div>}
      </div>
      <div style={{marginTop:7}}>
        <div style={{fontSize:12,fontWeight:600,color:T.white,lineHeight:1.3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{movie.title}</div>
        <div style={{fontSize:10,color:T.grey,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{movie.genre}</div>
        <div style={{display:"flex",alignItems:"center",gap:4,marginTop:2}}>
          <span style={{fontSize:10,color:T.grey,background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:3,padding:"0 4px",fontWeight:600}}>{movie.rating}</span>
          <span style={{fontSize:10,color:T.grey}}>{movie.runtime}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{flex:1,minHeight:0,position:"relative",display:"flex",flexDirection:"column"}}>
      {showFilter && <FilterSheet onClose={()=>setShowFilter(false)} sortBy={sortBy} setSortBy={setSortBy} ratings={ratings} setRatings={setRatings} genres={genres} setGenres={setGenres} runtime={runtime} setRuntime={setRuntime}/>}
    <div className="screen" style={{paddingBottom:80,position:"relative"}}>

      {/* Header — fixed position */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px 10px",position:"sticky",top:0,zIndex:50,background:T.navy,borderBottom:`1px solid ${T.glassBorder}`}}>
        <h1
          {...longPressBindings}
          aria-label="ReelView. Hold for 1 second to restart the demo."
          style={{
            margin:0,fontSize:"inherit",fontWeight:"inherit",lineHeight:1,
            cursor:"pointer",userSelect:"none",WebkitUserSelect:"none",
            WebkitTouchCallout:"none",
            display:"inline-flex",alignItems:"center",
            padding:"4px 6px",margin:"-4px -6px",borderRadius:8,
            transition:"filter .25s ease,transform .25s ease",
            filter: logoFlash ? "drop-shadow(0 0 14px rgba(228,197,102,.85)) drop-shadow(0 0 24px rgba(201,168,76,.5))" : "none",
            transform: logoFlash ? "scale(1.04)" : "scale(1)",
          }}
        >
          <Logo size={0.9}/>
        </h1>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          {/* Demo info — explains the prototype + reset gesture. Tucked at the start of the right cluster, far from the logo. */}
          <button
            aria-label="About this prototype"
            onClick={()=>onOpenDemoInfo && onOpenDemoInfo()}
            style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:4,opacity:.7,transition:"opacity .2s"}}
            onMouseEnter={e=>e.currentTarget.style.opacity="1"}
            onMouseLeave={e=>e.currentTarget.style.opacity=".7"}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="11" x2="12" y2="16"/>
              <circle cx="12" cy="8" r="0.6" fill={T.grey} stroke="none"/>
            </svg>
          </button>
          <button aria-label="Search" style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:4}}><svg viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg></button>
          <button aria-label="My profile" onClick={()=>nav("myreelview")} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:"50%",width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><svg viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></button>
        </div>
      </div>

      {/* Hero Carousel — Fixed-height editorial marquee */}
      <div style={{margin:"18px 16px 0",position:"relative",height:230,cursor:"pointer"}} onClick={()=>goMovie(hero)} onTouchStart={handleHeroTouchStart} onTouchEnd={handleHeroTouchEnd}>
        {/* Poster — locked dimensions, left-aligned */}
        <div style={{position:"absolute",top:0,left:0,width:155,height:230}}>
          <div style={{position:"absolute",top:-3,left:-3,right:-3,bottom:-3,borderRadius:14,background:`linear-gradient(145deg,${T.gold},rgba(201,168,76,.3))`,opacity:.4}}/>
          <div style={{width:155,height:230,borderRadius:12,overflow:"hidden",position:"relative",border:`1.5px solid ${T.goldBorder}`,boxShadow:`0 16px 40px rgba(0,0,0,.6)`}}>
            <MoviePoster movieId={hero.id} width={155} height={230} mini title={hero.title}/>
            <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.15)"}}>
              <div style={{width:38,height:38,borderRadius:"50%",border:`2px solid rgba(255,255,255,.85)`,background:"rgba(0,0,0,.35)",display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}}>
                <div style={{width:0,height:0,borderTop:"7px solid transparent",borderBottom:"7px solid transparent",borderLeft:`12px solid ${T.white}`,marginLeft:2}}/>
              </div>
            </div>
          </div>
        </div>
        {/* Info — top-right area, full titles visible */}
        <div style={{position:"absolute",top:0,left:172,right:0}}>
          <div style={{fontSize:9,fontWeight:700,color:T.gold,letterSpacing:".14em",marginBottom:8,textTransform:"uppercase"}}>
            ★ Featured This Week
          </div>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:21,fontWeight:900,color:T.white,lineHeight:1.15,marginBottom:8}}>{hero.title}</h2>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:5,flexWrap:"wrap"}}>
            <span className="tag">{hero.rating}</span>
            <span className="tag">{hero.genre}</span>
          </div>
          <div style={{fontSize:11,color:T.grey}}>{hero.runtime} · {hero.year}</div>
        </div>
        {/* CTA — pinned bottom-right with breathing room */}
        <button className="btn-gold press" onClick={e=>{e.stopPropagation();goMovie(hero);}} style={{position:"absolute",bottom:0,right:0,padding:"10px 22px",fontSize:13,borderRadius:10,whiteSpace:"nowrap"}}>Get Tickets</button>
      </div>
      {/* Carousel dots */}
      <div style={{display:"flex",justifyContent:"center",gap:5,marginTop:10}}>
        {heroes.map((_,i)=>(
          <button key={i} onClick={()=>setHeroIdx(i)} aria-label={"Go to slide "+(i+1)} style={{width:i===heroIdx?18:6,height:6,borderRadius:3,background:i===heroIdx?T.gold:T.greyDim,transition:"all .25s",cursor:"pointer",border:"none",padding:0}}/>
        ))}
      </div>

      {/* Tabs + Filter */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-evenly",padding:"14px 16px 0",borderBottom:`1px solid ${T.glassBorder}`}}>
        {[["now","Now Playing"],["soon","Coming Soon"],["fav","Favorites"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} className={tab===id?"tab-on":"tab-off"} style={{background:"none",border:"none",cursor:"pointer",padding:"10px 0",fontSize:13,fontWeight:600,fontFamily:"'Outfit',sans-serif",textAlign:"center",transition:"color .2s",whiteSpace:"nowrap"}}>{label}</button>
        ))}
        <button onClick={()=>setShowFilter(true)} aria-label={`Filter${hasFilters?` (${ratings.length+genres.length+runtime.length} active)`:""}`} style={{flexShrink:0,background:hasFilters?T.goldDim:T.navyLight,border:`1px solid ${hasFilters?T.goldBorder:T.greyDim}`,borderRadius:8,padding:"6px 8px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",position:"relative"}}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 3h12M3 7h8M5 11h4" stroke={hasFilters?T.goldLight:T.grey} strokeWidth="1.5" strokeLinecap="round"/></svg>
          {hasFilters&&<div style={{position:"absolute",top:-3,right:-3,width:14,height:14,borderRadius:"50%",background:T.gold,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:800,color:T.navy}}>{ratings.length+genres.length+runtime.length}</div>}
        </button>
      </div>

      <div style={{padding:"16px 16px 0"}}>

        {/* Favorites */}
        {tab==="now" && (
          <div style={{marginBottom:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <div style={{display:"flex",alignItems:"center",gap:7}}>
                <span className="section-label">Favorites</span>
                <span style={{fontSize:16}}>♥</span>
              </div>
              <button className="view-all" onClick={()=>setTab("fav")}>View all</button>
            </div>
            <div style={{fontSize:11,color:T.grey,marginBottom:12}}>Movies you've saved for later</div>
            {favs.length === 0 ? (
              <div style={{padding:"20px 14px",background:T.navyLight,border:`1px dashed ${T.greyDim}`,borderRadius:12,textAlign:"center"}}>
                <div style={{fontSize:22,marginBottom:6}}>♡</div>
                <div style={{fontSize:12,color:T.grey}}>No favorites yet — tap ♡ on any movie to save it here.</div>
              </div>
            ) : MOVIES.filter(m=>favs.includes(m.id)).length === 0 ? (
              <div style={{padding:"20px 14px",background:T.navyLight,border:`1px dashed ${T.greyDim}`,borderRadius:12,textAlign:"center"}}>
                <div style={{fontSize:22,marginBottom:6}}>♡</div>
                <div style={{fontSize:12,color:T.grey}}>Your favorites are <strong style={{color:T.offwhite}}>Coming Soon</strong> — view them in the Favorites tab!</div>
              </div>
            ) : (
              <div className="hscroll" style={{paddingTop:4}}>
                {MOVIES.filter(m=>favs.includes(m.id)).map(m=>(
                  <div key={m.id} className="press card-lift" onClick={()=>goMovie(m)} style={{flexShrink:0,width:148,borderRadius:12,overflow:"hidden",border:`1px solid ${T.goldBorder}`,position:"relative",background:T.navyCard}}>
                    <div style={{height:140,position:"relative",overflow:"hidden"}}>
                      {POSTER_IMAGES[m.id] && <img src={POSTER_IMAGES[m.id]} alt="" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top"}} loading="lazy"/>}
                      <div style={{position:"absolute",inset:0,background:`linear-gradient(180deg,transparent 60%,rgba(0,0,0,.4) 100%)`}}/>
                      <button onClick={e=>toggleFav(e,m.id)} aria-label={`Remove ${m.title} from favorites`} style={{position:"absolute",top:7,right:7,background:"rgba(0,0,0,.6)",backdropFilter:"blur(4px)",border:`1px solid ${T.goldBorder}`,borderRadius:"50%",width:28,height:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:T.gold,zIndex:2,transition:"all .18s",padding:0}}>♥</button>
                    </div>
                    <div style={{padding:"9px 10px 11px"}}>
                      <div style={{fontSize:12,fontWeight:700,color:T.white,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginBottom:3}}>{m.title}</div>
                      <div style={{fontSize:10,color:T.grey}}>{m.genre} · {m.runtime}</div>
                      <button className="btn-gold" style={{width:"100%",padding:"6px 0",fontSize:10,marginTop:8,borderRadius:7}} onClick={e=>{e.stopPropagation();goMovie(m);}}>Get Tickets</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Now Playing grid */}
        {tab==="now" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <span className="section-label">Now Playing{hasFilters?` (${filteredMovies.length})`:""}</span>
              <div style={{display:"flex",alignItems:"center",gap:5}}>
                <svg width="12" height="12" viewBox="0 0 12 12"><path d="M6 1C3.24 1 1 3.24 1 6s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5z" stroke={T.grey} strokeWidth="1.2" fill="none"/><path d="M6 3v4l2.5 1.5" stroke={T.grey} strokeWidth="1.2" strokeLinecap="round" fill="none"/></svg>
                <span style={{fontSize:11,color:T.grey}}>Los Angeles, CA</span>
              </div>
            </div>
            {filteredMovies.length>0 ? (
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
                {filteredMovies.map(m=><PosterCard key={m.id} movie={m}/>)}
              </div>
            ) : (
              <div style={{textAlign:"center",padding:"36px 20px",marginBottom:16}}>
                <div style={{fontSize:28,marginBottom:10}}>🎬</div>
                <div style={{fontSize:13,color:T.grey,marginBottom:12}}>No movies match your filters</div>
                <button className="btn-outline" style={{padding:"9px 20px",fontSize:12}} onClick={()=>{setSortBy("Popularity");setRatings([]);setGenres([]);setRuntime([]);}}>Clear Filters</button>
              </div>
            )}
            <button className="btn-outline" disabled style={{width:"100%",padding:13,fontSize:14,marginBottom:8,cursor:"default",borderColor:"rgba(201,168,76,0.5)",color:T.gold}} aria-label="No more movies to show">View More</button>
          </div>
        )}

        {tab==="soon" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <span className="section-label">Coming Soon</span>
              <button className="view-all">View all</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
              {COMING_SOON.map(m=><PosterCard key={m.id} movie={m}/>)}
            </div>
          </div>
        )}

        {tab==="fav" && (
          <div>
            <div style={{fontSize:13,color:T.grey,marginBottom:14}}>Your favorited movies</div>
            {favs.length===0 && (
              <div style={{textAlign:"center",padding:"40px 0"}}>
                <div style={{fontSize:32,marginBottom:10}}>♡</div>
                <div style={{color:T.grey,fontSize:14,marginBottom:4}}>No favorites yet</div>
                <div style={{color:T.grey,fontSize:12}}>Tap ♡ on any movie to add it here.</div>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              {[...MOVIES,...COMING_SOON].filter(m=>favs.includes(m.id)).map(m=>(
                <PosterCard key={m.id} movie={m}/>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// MOVIE DETAIL SCREEN — ReelView visual identity
// ══════════════════════════════════════════════════════
const MovieDetailScreen = ({ movie, nav, goBack, setShowtime, setTheater, setTheaterInitTab, favs, toggleFav, setSeatTimer, setSeatData, setTicketOrder }) => {
  const [tab, setTab] = useState("showtimes");
  const [openPreview, setOpenPreview] = useState(null);
  const [selectedDate, setSelectedDate] = useState(0);
  const [descExpanded, setDescExpanded] = useState(false);
  const [fmtFilter, setFmtFilter] = useState("All Formats");
  const [fmtSheetOpen, setFmtSheetOpen] = useState(false);
  const [priceInfoOpen, setPriceInfoOpen] = useState(null); // key of format showing price info

  // Esc closes inline popovers
  useEffect(() => {
    if (openPreview==null && priceInfoOpen==null && !fmtSheetOpen) return;
    const onKey = e => { if (e.key === "Escape") { setOpenPreview(null); setPriceInfoOpen(null); setFmtSheetOpen(false); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openPreview, priceInfoOpen, fmtSheetOpen]);

  // Format filter bottom sheet — mirrors the MovieSheet pattern in Theater Detail
  const FormatSheet = () => {
    useModalDismiss(()=>setFmtSheetOpen(false));
    const options = ["All Formats","IMAX","Dolby Atmos","Standard"];
    return (
      <div style={{position:"absolute",inset:0,zIndex:300}} onClick={()=>setFmtSheetOpen(false)}>
        <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.55)"}}/>
        <div onClick={e=>e.stopPropagation()} style={{position:"absolute",bottom:0,left:0,right:0,background:T.navyMid,borderRadius:"20px 20px 0 0",maxHeight:"70%",display:"flex",flexDirection:"column",overflow:"hidden",overscrollBehavior:"contain",boxShadow:"0 -8px 40px rgba(0,0,0,.5)"}}>
          <button onClick={()=>setFmtSheetOpen(false)} aria-label="Close" style={{display:"block",width:48,height:16,padding:"6px",margin:"10px auto 2px",background:"none",border:"none",cursor:"pointer"}}>
            <div style={{width:36,height:4,borderRadius:2,background:T.greyDim,margin:"0 auto"}}/>
          </button>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 20px 12px",borderBottom:`1px solid ${T.glassBorder}`,flexShrink:0}}>
            <span style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700,color:T.white}}>Format</span>
            <button onClick={()=>setFmtSheetOpen(false)} style={{background:"none",border:"none",color:T.grey,fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
          </div>
          <div style={{overflowY:"auto",flex:1,minHeight:0}}>
            {options.map(f=>{
              const isSelected = f === fmtFilter;
              return (
                <div key={f} role="radio" aria-checked={isSelected} tabIndex={0} onClick={()=>{setFmtFilter(f);setFmtSheetOpen(false);}} onKeyDown={e=>{if(e.key===" "||e.key==="Enter"){e.preventDefault();setFmtFilter(f);setFmtSheetOpen(false);}}} style={{padding:"14px 20px",borderBottom:`1px solid ${T.glassBorder}`,cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
                  {isSelected
                    ? <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8l4 4 6-7" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    : <span style={{width:14}}/>
                  }
                  <span style={{fontSize:14,color:isSelected?T.gold:T.white,fontWeight:isSelected?700:400,fontFamily:"'Outfit',sans-serif"}}>{f}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };
  if (!movie) return null;
  const isComingSoon = !!movie.releaseDate;
  const dates = ["Today","Tue 6/3","Wed 6/4","Thu 6/5","Fri 6/6","Sat 6/7","Sun 6/8"];

  const handleTime = (theater, fmt, time) => {
    const base = FORMAT_PRICING[fmt.fmt] || 16.50;
    const mat = isMatinee(time);
    const price = mat ? base - MATINEE_DISCOUNT : base;
    setShowtime({ movie, theater, fmt: fmt.fmt, time, date: dates[selectedDate], basePrice: price, isMatinee: mat, showroom: fmt.showroom, seatsLeft: fmt.seatsLeft });
    setSeatTimer(null); setSeatData(null); setTicketOrder(null);
    nav("seat-map");
  };

  const TABS = ["Showtimes","Details","Videos"];

  return (
    <div style={{flex:1,minHeight:0,display:"flex",flexDirection:"column",position:"relative"}}>
      {fmtSheetOpen && <FormatSheet/>}
      <div className="screen" style={{paddingBottom:80}}>

      {/* ── HERO: full-bleed poster atmosphere with ReelView gold treatment ── */}
      <div style={{position:"relative",height:300,overflow:"hidden"}}>
        {/* Atmospheric backdrop */}
        <div style={{position:"absolute",inset:0,background:`linear-gradient(145deg,${movie.color} 0%,#04081A 75%)`}}/>
        {/* Subtle grain texture overlay */}
        <div style={{position:"absolute",inset:0,backgroundImage:"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")",opacity:.4}}/>
        {/* Large poster art, offset right */}
        <div style={{position:"absolute",right:-10,top:"50%",transform:"translateY(-54%)",opacity:.18,filter:"blur(2px)",userSelect:"none"}}><MoviePoster movieId={movie.id} width={160} height={220} mini title={movie.title}/></div>
        {/* Crisp centered poster */}
        <div style={{position:"absolute",left:20,top:"50%",transform:"translateY(-50%)",width:110,height:160,borderRadius:12,overflow:"hidden",border:`1px solid ${T.goldBorder}`,boxShadow:`0 20px 50px rgba(0,0,0,.65), 0 0 0 1px ${T.goldBorder}`}}>
          <MoviePoster movieId={movie.id} width={110} height={160} mini title={movie.title}/>
        </div>
        {/* Title + meta stacked right of poster */}
        <div style={{position:"absolute",left:146,right:52,top:"50%",transform:"translateY(-50%)"}}>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:18,lineHeight:1.2,color:T.white,marginBottom:8}}>{movie.title}</h1>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
            <span className="tag">{movie.rating}</span>
            <span className="tag">{movie.genre}</span>
          </div>
          <div style={{fontSize:11,color:T.grey}}>{movie.runtime} · {movie.year}</div>
        </div>
        {/* Back btn */}
        <button aria-label="Go back" onClick={goBack} style={{position:"absolute",top:14,left:14,background:"rgba(4,8,26,.7)",backdropFilter:"blur(10px)",border:`1px solid ${T.glassBorder}`,borderRadius:10,width:34,height:34,cursor:"pointer",color:T.white,fontSize:15,display:"flex",alignItems:"center",justifyContent:"center",zIndex:10}}>←</button>
        {/* Share btn */}
        <button aria-label="Share" style={{position:"absolute",top:14,right:14,background:"rgba(4,8,26,.7)",backdropFilter:"blur(10px)",border:`1px solid ${T.glassBorder}`,borderRadius:10,width:34,height:34,cursor:"pointer",color:T.gold,display:"flex",alignItems:"center",justifyContent:"center",zIndex:10}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
        </button>
        {/* Bottom fade */}
        <div style={{position:"absolute",bottom:0,left:0,right:0,height:80,background:"linear-gradient(to top,#080D1A,transparent)"}}/>
      </div>

      {/* ── TAB SWITCHER — pill style, ReelView branded ── */}
      <div style={{padding:"14px 16px 0",background:T.navy}}>
        <div style={{display:"flex",background:T.navyLight,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:3,gap:2}}>
          {TABS.map(t=>{
            const key=t.toLowerCase();
            const on=tab===key;
            return (
              <button key={t} onClick={()=>setTab(key)} style={{flex:1,padding:"8px 0",borderRadius:9,background:on?T.gold:"transparent",color:on?T.navy:T.grey,border:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:700,fontSize:12,letterSpacing:".03em",transition:"all .2s"}}>
                {t}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── SHOWTIMES TAB ── */}
      {tab==="showtimes" && isComingSoon && (
        <div className="fade" style={{padding:"24px 16px 100px",textAlign:"center"}}>
          <div style={{background:`linear-gradient(145deg,rgba(201,168,76,.06),rgba(201,168,76,.02))`,border:`1px solid ${T.goldBorder}`,borderRadius:18,padding:"32px 20px"}}>
            <div style={{fontSize:36,marginBottom:12}}>🎬</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:T.white,marginBottom:8}}>Coming {movie.releaseDate}</div>
            <p style={{fontSize:13,color:T.offwhite,lineHeight:1.6,marginBottom:20,maxWidth:280,margin:"0 auto 20px"}}>
              Showtimes aren't available yet. Check back closer to release day, or favorite this movie to stay in the loop!
            </p>
            {favs && toggleFav && (
              <button className={favs.includes(movie.id)?"btn-outline":"btn-gold"} onClick={e=>toggleFav(e,movie.id)} style={{padding:"11px 24px",fontSize:13,borderRadius:10,display:"inline-flex",alignItems:"center",gap:7}}>
                <span style={{fontSize:15}}>{favs.includes(movie.id)?"♥":"♡"}</span>
                {favs.includes(movie.id)?"Saved to Favorites":"Add to Favorites"}
              </button>
            )}
          </div>
        </div>
      )}
      {tab==="showtimes" && !isComingSoon && (
        <div className="fade" style={{padding:"16px 16px"}}>

          {/* Date rail — underbar selection, no pill backgrounds */}
          <div className="hscroll" style={{marginBottom:14,gap:0,display:"flex"}}>
            {dates.map((d,i)=>{
              const on = selectedDate === i;
              return (
                <button key={d} onClick={()=>setSelectedDate(i)} style={{
                  flexShrink:0,minWidth:52,
                  display:"flex",flexDirection:"column",alignItems:"center",
                  padding:"4px 8px 8px",
                  background:"none",border:"none",cursor:"pointer",
                  fontFamily:"'Outfit',sans-serif",
                  position:"relative",
                }}>
                  <span style={{
                    fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",
                    color:on?T.goldLight:T.grey,
                    marginBottom:4,transition:"color .18s"
                  }}>{i===0?"TODAY":d.split(" ")[0].toUpperCase()}</span>
                  <span style={{
                    fontSize:15,fontWeight:on?800:600,
                    color:on?T.white:T.offwhite,
                    fontVariantNumeric:"tabular-nums",
                    transition:"color .18s"
                  }}>{i===0?"Now":d.split(" ")[1]}</span>
                  <div aria-hidden="true" style={{
                    position:"absolute",bottom:0,left:"50%",
                    transform:`translateX(-50%) scaleX(${on?1:0})`,
                    transformOrigin:"center",
                    width:24,height:2,borderRadius:1,background:T.gold,
                    transition:"transform .22s ease"
                  }}/>
                </button>
              );
            })}
          </div>

          {/* Format dropdown — full-width button (matches Theater Detail's movie filter) */}
          <div style={{display:"flex",gap:8,marginBottom:12}}>
            <button onClick={()=>setFmtSheetOpen(true)} aria-label="Filter by format" style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,padding:"8px 0",borderRadius:10,background:T.navyLight,border:`1px solid ${T.glassBorder}`,cursor:"pointer"}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M8 4v16M16 4v16M2 9h4M18 9h4"/></svg>
              <span style={{fontSize:11,fontWeight:600,color:T.offwhite,fontFamily:"'Outfit',sans-serif"}}>{fmtFilter}</span>
              <span style={{fontSize:10,color:T.grey}}>▾</span>
            </button>
          </div>

          {/* Thin divider separating top bar from results */}
          <div aria-hidden="true" style={{height:1,background:T.glassBorder,marginBottom:14}}/>

          {/* Single all-in pricing disclosure for the whole tab — gold accent for visibility */}
          <div style={{display:"flex",alignItems:"center",gap:9,padding:"11px 14px",background:`linear-gradient(135deg,rgba(201,168,76,.10),rgba(201,168,76,.02))`,border:`1px solid ${T.goldBorder}`,borderRadius:10,marginBottom:14}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.goldLight} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="12" y1="8" x2="12" y2="8"/></svg>
            <div style={{fontSize:11,color:T.offwhite,lineHeight:1.4,fontFamily:"'Outfit',sans-serif",fontWeight:500}}>All prices include <span style={{color:T.goldLight,fontWeight:700}}>${SERVICE_FEE_GLOBAL.toFixed(2)}</span> service fee · <span style={{color:T.goldLight,fontWeight:700}}>No hidden charges</span></div>
          </div>

          {/* Theaters */}
          {THEATERS.map(th=>{
            const thData = SHOWTIMES_DATA[th.id] || { formats:[{ fmt:"Standard", tagline:"", amenities:"Heated Recliners · Reserved Seating", times:["2:00 PM","5:00 PM","8:00 PM"] }] };
            const filteredFormats = fmtFilter==="All Formats" ? thData.formats : thData.formats.filter(f=>f.fmt===fmtFilter);
            if(filteredFormats.length===0) return null;
            return (
              <div key={th.id} className="glass-card" style={{marginBottom:14,overflow:"hidden"}}>
                {/* Theater header */}
                <div onClick={()=>{setTheater(th);setTheaterInitTab("info");nav("theater-detail");}} style={{padding:"14px 16px 10px",position:"relative",cursor:"pointer"}} role="button" tabIndex={0} aria-label={`View details for ${th.name}`}>
                  <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${T.gold},rgba(201,168,76,.2))`}} role="presentation"/>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:T.white,lineHeight:1.25,marginBottom:4}}>{th.name}</div>
                      <div style={{fontSize:11,color:T.gold}}>{th.distance} · {th.address}</div>
                    </div>
                    <span style={{color:T.grey,fontSize:14}}>›</span>
                  </div>
                </div>
                {/* Format sections */}
                {filteredFormats.map((fmt,fi)=>{
                  const previewKey=`${th.id}-${fi}`;
                  return (
                    <div key={fi} style={{borderTop:`1px solid ${T.glassBorder}`,padding:"14px 16px"}}>
                      {/* Format header — serif name, prominent tagline, showroom + amenities on separate lines, right-aligned from-price */}
                      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:12}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700,color:T.goldLight,letterSpacing:".01em",lineHeight:1.1}}>{fmt.fmt}</div>
                          {fmt.tagline && (
                            <div style={{fontSize:9,color:T.goldLight,fontWeight:800,letterSpacing:".14em",textTransform:"uppercase",marginTop:4,lineHeight:1.2}}>{fmt.tagline}</div>
                          )}
                          {SHOWROOMS[fmt.showroom] && (
                            <div style={{fontSize:10,color:T.offwhite,marginTop:6,lineHeight:1.35,fontWeight:500}}>{SHOWROOMS[fmt.showroom].name} · {SHOWROOMS[fmt.showroom].seats} seats</div>
                          )}
                          {fmt.amenities && (
                            <div style={{fontSize:10,color:T.grey,marginTop:2,lineHeight:1.4}}>{fmt.amenities.split(" · ").slice(0,3).join(" · ")}</div>
                          )}
                        </div>
                        <div style={{flexShrink:0,display:"flex",flexDirection:"column",alignItems:"flex-start",gap:1}}>
                          <div style={{fontSize:8,color:T.grey,fontWeight:600,letterSpacing:".08em",textTransform:"uppercase"}}></div>
                          <div style={{display:"flex",alignItems:"center",gap:5,fontVariantNumeric:"tabular-nums"}}>
                            <span style={{fontSize:15,color:T.white,fontWeight:700,lineHeight:1,fontFamily:"'Outfit',sans-serif"}}>${((FORMAT_PRICING[fmt.fmt]||16.50) + SERVICE_FEE_GLOBAL).toFixed(2)}</span>
                            <button onClick={(e)=>{e.stopPropagation();setPriceInfoOpen(priceInfoOpen===previewKey?null:previewKey);}} style={{background:"none",border:`1px solid ${T.greyDim}`,borderRadius:"50%",width:14,height:14,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",padding:0,flexShrink:0}} aria-label="Pricing details">
                              <span style={{fontSize:8,fontWeight:700,color:T.offwhite,lineHeight:1}}>i</span>
                            </button>
                          </div>
                        </div>
                      </div>
                      {priceInfoOpen===previewKey&&(
                        <div style={{background:T.navyMid,border:`1px solid ${T.goldBorder}`,borderRadius:10,padding:"10px 14px",marginBottom:10}}>
                          <div style={{fontSize:11,fontWeight:700,color:T.white,marginBottom:4}}>Pricing Info</div>
                          <div style={{fontSize:10,color:T.grey,lineHeight:1.5}}>Displayed price is the standard <span style={{color:T.offwhite,fontWeight:600}}>adult ticket</span> rate. Child and senior discounts vary.</div>
                          <div style={{display:"flex",justifyContent:"flex-end",marginTop:6}}><button onClick={(e)=>{e.stopPropagation();setPriceInfoOpen(null);}} style={{background:"none",border:"none",color:T.gold,fontSize:10,fontWeight:600,cursor:"pointer",padding:0,fontFamily:"'Outfit',sans-serif"}}>Got it</button></div>
                        </div>
                      )}
                      {/* Matinee note — shown once per format if any matinee times */}
                      {fmt.times.some(t=>isMatinee(t)) && (
                        <div style={{fontSize:10,color:T.green,marginBottom:9,display:"flex",alignItems:"center",gap:6,fontFamily:"'Outfit',sans-serif"}}>
                          <span aria-hidden="true" style={{fontSize:11,lineHeight:1}}>✦</span>
                          <span>Matinee pricing before noon — save ${MATINEE_DISCOUNT.toFixed(2)}</span>
                        </div>
                      )}
                      {/* Time pills — time only; matinee glyph inline */}
                      <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                        {fmt.times.map(t=>{
                          const base = FORMAT_PRICING[fmt.fmt] || 16.50;
                          const mat = isMatinee(t);
                          const allIn = (mat ? base - MATINEE_DISCOUNT : base) + SERVICE_FEE_GLOBAL;
                          const left = fmt.seatsLeft?.[t];
                          return (
                            <button key={t} className="pill-time press" onClick={()=>handleTime(th,fmt,t)}
                              style={{fontSize:13,padding:"9px 14px",display:"flex",alignItems:"center",justifyContent:"center",gap:5,fontVariantNumeric:"tabular-nums",fontWeight:600}}
                              aria-label={`${t} showtime, $${allIn.toFixed(2)}${mat?" matinee pricing":""}${left!=null?`, ${left} seats left`:""}`}>
                              <span>{t}</span>
                              {mat && <span aria-hidden="true" style={{color:T.green,fontSize:11,lineHeight:1}}>✦</span>}
                            </button>
                          );
                        })}
                      </div>
                      {/* Check Seats */}
                      <button className={`check-seats${openPreview===previewKey?" open":""}`} onClick={()=>setOpenPreview(openPreview===previewKey?null:previewKey)} aria-expanded={openPreview===previewKey} aria-label="Toggle seat availability preview" style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:12,background:openPreview===previewKey?T.goldDim:"rgba(255,255,255,.03)",border:`1px solid ${openPreview===previewKey?T.goldBorder:"rgba(138,148,168,.55)"}`,borderRadius:8,padding:"6px 12px",cursor:"pointer"}}>
                        <svg className="check-seats__ico" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={openPreview===previewKey?T.goldLight:T.grey} strokeWidth="2" style={{transition:"stroke .2s ease"}}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        <span className="check-seats__lbl" style={{fontSize:11,color:openPreview===previewKey?T.goldLight:T.grey,fontWeight:600,fontFamily:"'Outfit',sans-serif",transition:"color .2s ease"}}>Check Seats</span>
                        <span className="check-seats__caret" style={{fontSize:9,color:openPreview===previewKey?T.goldLight:T.grey,transform:openPreview===previewKey?"rotate(180deg)":"none",display:"inline-block",transition:"transform .2s,color .2s ease"}}>▾</span>
                      </button>
                      {openPreview===previewKey&&<InlineSeatPreview showtimeKey={`${th.id}-${movie.id}-${fmt.fmt}`} times={fmt.times} seatsLeft={fmt.seatsLeft} showroom={fmt.showroom}/>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* ── DETAILS TAB ── */}
      {tab==="details" && (
        <div className="fade" style={{padding:"18px 16px"}}>

          {/* Critical Reception — horizontal strip instead of AMC grid cards */}
          {(movie.rtFresh!=null || movie.rtAudience!=null) ? (
          <div style={{marginBottom:22}}>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Critical Reception</div>
            <div style={{background:`linear-gradient(135deg,${T.navyCard},${T.navyLight})`,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:"14px 16px",display:"flex",alignItems:"center",gap:0}}>
              {/* Tomatometer */}
              <div style={{flex:1,display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:42,height:42,borderRadius:10,background:movie.rtFresh>=60?"rgba(201,168,76,.12)":"rgba(224,75,61,.12)",border:`1.5px solid ${movie.rtFresh>=60?T.goldBorder:"rgba(224,75,61,.45)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>🍅</div>
                <div>
                  <div style={{fontSize:16,fontWeight:800,color:movie.rtFresh>=60?T.goldLight:T.red}}>{movie.rtFresh}%</div>
                  <div style={{fontSize:9,color:T.grey}}>Tomatometer</div>
                </div>
              </div>
              {/* Divider */}
              <div style={{width:1,height:36,background:T.glassBorder,margin:"0 12px"}}/>
              {/* Audience */}
              <div style={{flex:1,display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:42,height:42,borderRadius:10,background:movie.rtAudience>=60?"rgba(201,168,76,.12)":"rgba(224,75,61,.12)",border:`1.5px solid ${movie.rtAudience>=60?T.goldBorder:"rgba(224,75,61,.35)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>🍿</div>
                <div>
                  <div style={{fontSize:16,fontWeight:800,color:movie.rtAudience>=60?T.goldLight:T.red}}>{movie.rtAudience}%</div>
                  <div style={{fontSize:9,color:T.grey}}>Audience Score</div>
                </div>
              </div>
            </div>
            {/* User score */}
            {movie.score && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:8}}>
                <span style={{fontSize:10,color:T.grey}}>IMDb</span>
                <span style={{fontSize:13,fontWeight:800,color:T.goldLight}}>{movie.score}</span>
                <span style={{fontSize:10,color:T.grey}}>/ 10</span>
              </div>
            )}
          </div>
          ) : (
          <div style={{marginBottom:22}}>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Critical Reception</div>
            <div style={{background:`linear-gradient(135deg,${T.navyCard},${T.navyLight})`,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:"18px 16px",textAlign:"center"}}>
              <div style={{fontSize:12,color:T.grey,marginBottom:2}}>No ratings yet</div>
              <div style={{fontSize:10,color:T.grey}}>Ratings will appear after release</div>
            </div>
          </div>
          )}

          {/* Synopsis — pull-quote editorial style */}
          <div style={{marginBottom:22}}>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Synopsis</div>
            <div style={{borderLeft:`2px solid ${T.gold}`,paddingLeft:14}}>
              <p style={{fontSize:14,color:T.offwhite,lineHeight:1.75,fontStyle:"italic",marginBottom:6}}>{descExpanded?movie.desc:movie.desc.slice(0,130)+"…"}</p>
              <button onClick={()=>setDescExpanded(!descExpanded)} style={{background:"none",border:"none",cursor:"pointer",color:T.gold,fontSize:12,fontWeight:700,fontFamily:"'Outfit',sans-serif",padding:0,display:"flex",alignItems:"center",gap:4}}>
                {descExpanded?"Show less ▴":"Read more ▾"}
              </button>
            </div>
          </div>

          {/* Cast & Crew — compact list cards instead of circular avatars */}
          {(movie.director || movie.cast) ? (
          <div style={{marginBottom:22}}>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Cast & Crew</div>
            {/* Director featured */}
            {movie.director && (
            <div style={{background:`linear-gradient(135deg,rgba(201,168,76,.08),rgba(201,168,76,.02))`,border:`1px solid ${T.goldBorder}`,borderRadius:12,padding:"10px 14px",marginBottom:8,display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:40,height:40,borderRadius:10,background:`linear-gradient(135deg,${T.navyLight},${T.greyDim})`,border:`1.5px solid ${T.goldBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,color:T.gold}}>{movie.director[0]}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:T.white}}>{movie.director}</div>
                <div style={{fontSize:10,color:T.gold,fontWeight:600}}>Director</div>
              </div>
            </div>
            )}
            {/* Cast in compact 2-col grid */}
            {movie.cast && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
              {movie.cast.map(name=>(
                <div key={name} style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:10,padding:"8px 10px",display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:30,height:30,borderRadius:8,background:`linear-gradient(135deg,${T.navyLight},${T.greyDim})`,border:`1px solid ${T.glassBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:600,color:T.offwhite,flexShrink:0}}>{name[0]}</div>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:11,fontWeight:600,color:T.white,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{name.split(" ")[0]}</div>
                    <div style={{fontSize:8,color:T.grey}}>{name.split(" ").slice(1).join(" ")}</div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
          ) : (
          <div style={{marginBottom:22}}>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Cast & Crew</div>
            <div style={{background:`linear-gradient(135deg,${T.navyCard},${T.navyLight})`,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:"18px 16px",textAlign:"center"}}>
              <div style={{fontSize:12,color:T.grey}}>Cast details coming soon</div>
            </div>
          </div>
          )}

          {/* Movie Stills */}
          <div>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Stills</div>
            <div className="hscroll" style={{gap:10}}>
              {[0,1,2].map(i=>(
                <div key={i} style={{flexShrink:0,width:168,height:95,borderRadius:10,overflow:"hidden",border:`1px solid ${T.glassBorder}`,opacity:.8+i*.07,position:"relative"}}><MoviePoster movieId={movie.id} width={168} height={95} mini title={movie.title}/><div style={{position:"absolute",inset:0,background:`linear-gradient(${90+i*40}deg,transparent 40%,rgba(0,0,0,.3))`}}/></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── VIDEOS TAB ── */}
      {tab==="videos" && (
        <div className="fade" style={{padding:"16px"}}>
          {[{title:`${movie.title} — Official Trailer`,age:"1 month ago",dur:"2:18",label:"TRAILER"},{title:`${movie.title} — Behind the Camera`,age:"3 weeks ago",dur:"4:05",label:"FEATURETTE"},{title:`${movie.title} — Director's Cut Commentary`,age:"2 weeks ago",dur:"6:41",label:"EXCLUSIVE"}].map((v,i)=>(
            <div key={i} className="glass-card card-lift" style={{marginBottom:12,overflow:"hidden",cursor:"pointer"}}>
              {/* Thumbnail */}
              <div style={{position:"relative",height:160,background:`linear-gradient(${145+i*18}deg,${movie.color} 0%,#060C1C 100%)`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                {POSTER_IMAGES[movie.id] && <img src={POSTER_IMAGES[movie.id]} alt="" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",objectPosition:"center 30%",opacity:.45}} loading="lazy"/>}
                {/* Vignette */}
                <div style={{position:"absolute",inset:0,background:"radial-gradient(ellipse at center,transparent 30%,rgba(0,0,0,.55))"}}/>
                {/* Gold-ring play button */}
                <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:48,height:48,borderRadius:"50%",border:`2px solid ${T.gold}`,background:"rgba(4,8,26,.55)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 0 20px ${T.goldDim}`}}>
                  <div style={{width:0,height:0,borderTop:"9px solid transparent",borderBottom:"9px solid transparent",borderLeft:`15px solid ${T.gold}`,marginLeft:2}}/>
                </div>
                {/* Duration badge */}
                <div style={{position:"absolute",bottom:8,right:10,background:"rgba(4,8,26,.8)",border:`1px solid ${T.glassBorder}`,borderRadius:5,padding:"2px 8px",fontSize:10,fontWeight:700,color:T.white}}>{v.dur}</div>
                {/* Label badge */}
                <span className="tag" style={{position:"absolute",top:8,left:10}}>{v.label}</span>
              </div>
              {/* Meta */}
              <div style={{padding:"10px 14px 12px",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:T.white,marginBottom:3,lineHeight:1.3}}>{v.title}</div>
                  <div style={{fontSize:10,color:T.grey}}>{v.age}</div>
                </div>
                <button style={{background:"none",border:"none",cursor:"pointer",color:T.grey,padding:"2px 4px",flexShrink:0}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════
// SEAT MAP
// ══════════════════════════════════════════════════════
const SeatMapScreen = ({ showtime, nav, goBack, setSeatData, seatData, seatTimer, setSeatTimer, purchasedSeats, rewardPoints, onReleaseSeat }) => {
  // Layout from showroom config — adapts seat map to auditorium size
  const room = SHOWROOMS[showtime?.showroom] || SHOWROOMS["Standard-M"];
  const ROWS = room.rows;
  const COLS = room.cols;
  const ACCESSIBLE_ROW = room.accRow;
  const roomSize = room.size; // "small", "medium", "large"
  // Deterministic PRNG — same algorithm as InlineSeatPreview so "Check Seats" matches
  const hashStr = s => { let h=0; for(let i=0;i<s.length;i++) h=((h<<5)-h)+s.charCodeAt(i)|0; return Math.abs(h); };
  const showtimeKey = `${showtime?.theater?.id}-${showtime?.movie?.id}-${showtime?.fmt}`;
  const seed = hashStr(`${showtimeKey}-${showtime?.time}`);
  const prng = i => { let h=(seed+i*374761393)|0; h=Math.imul(h^(h>>>15),0x1a85ec53); h=Math.imul(h^(h>>>13),0xc4ae3d47); return ((h^(h>>>16))>>>0)/4294967296; };
  const allSeats = ROWS.flatMap(r=>Array.from({length:COLS},(_,i)=>`${r}${i+1}`));
  const left = showtime?.seatsLeft?.[showtime?.time];
  const fillPct = left!=null ? Math.min(0.82, Math.max(0.25, 1-left/(ROWS.length*COLS))) : 0.12+prng(999)*0.28;
  const TAKEN = new Set();
  allSeats.forEach((s,i) => { if(prng(i+1)<fillPct) TAKEN.add(s); });
  // Add seats purchased this session
  const pKey = `${showtime?.theater?.id}-${showtime?.movie?.id}-${showtime?.fmt}-${showtime?.time}`;
  const pSeats = purchasedSeats?.[pKey];
  if(pSeats && Array.isArray(pSeats)) pSeats.forEach(s => TAKEN.add(s));
  const CENTER_ROWS = room.premRows;
  const CENTER = CENTER_ROWS.flatMap(r => Array.from({length:COLS},(_,i)=>`${r}${i+1}`));
  const [selected, setSelected] = useState(seatData && Array.isArray(seatData) ? seatData : []);
  const [povSeat, setPovSeat] = useState(null);
  const [expandedPOV, setExpandedPOV] = useState(null);
  const [releaseModal, setReleaseModal] = useState(false);
  const scrollRef = useRef(null);

  // Start timer on first mount if not already running
  useEffect(() => { if (seatTimer === null) setSeatTimer(600); }, []);

  // Auto-scroll the seat grid so the just-tapped seat stays visible after the
  // POV strip mounts and shrinks the scroll viewport.
  useEffect(() => {
    if (!povSeat || !scrollRef.current) return;
    // Defer one frame so the POV strip has finished resizing the container.
    const raf = requestAnimationFrame(() => {
      const container = scrollRef.current;
      if (!container) return;
      const seatEl = container.querySelector(`[data-seat-id="${povSeat}"]`);
      if (!seatEl) return;
      const seatRect = seatEl.getBoundingClientRect();
      const contRect = container.getBoundingClientRect();
      const margin = 24;
      if (seatRect.bottom > contRect.bottom - margin) {
        container.scrollBy({ top: seatRect.bottom - contRect.bottom + margin, behavior: "smooth" });
      } else if (seatRect.top < contRect.top + margin) {
        container.scrollBy({ top: seatRect.top - contRect.top - margin, behavior: "smooth" });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [povSeat, selected.length]);
  const timeLeft = seatTimer || 0;
  const timerMins = Math.floor(timeLeft / 60);
  const timerSecs = timeLeft % 60;
  const timerStr = `${timerMins}:${String(timerSecs).padStart(2,'0')}`;
  const timerUrgent = timeLeft < 120;

  const isPairGap = col => col > 0 && col % 2 === 0 && col < COLS; // small gap between every pair
  const rowZone = r => r <= room.frontEnd ? "Front" : r <= room.midEnd ? "Center" : "Rear";
  const sectionFor = r => room.sections[r] || null;

  const toggle = id => {
    if(TAKEN.has(id)) return;
    setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : s.length < 8 ? [...s,id] : s);
    setPovSeat(id);
  };

  if(!showtime) return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:12,borderBottom:`1px solid ${T.glassBorder}`,flexShrink:0}}>
        <button aria-label="Go back" onClick={goBack} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:36,height:36,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:16}}>←</button>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:16,color:T.white,margin:0}}>Select Seats</h1>
      </div>
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:T.grey,fontSize:13}}>Please select a showtime first.</div>
    </div>
  );

  const seatPrice = showtime?.basePrice || FORMAT_PRICING[showtime?.fmt] || 16.50;
  const seatFee = SERVICE_FEE_GLOBAL * selected.length;
  const totalEst = (selected.length * seatPrice) + seatFee;

  // Mini POV view — section-varied perspective with distance labels
  const distFor = z => z==="Front"?"~15 ft":z==="Center"?"~35 ft":"~55 ft";
  const distNum = z => z==="Front"?15:z==="Center"?35:55;
  const angleFor = z => z==="Front"?"Front rows — close-up view":z==="Center"?"Center rows — balanced view":"Elevated — full-screen view";
  const MiniPOV = ({ seatId, isActive, large }) => {
    const zone = rowZone(seatId[0]);
    const seatCol = parseInt(seatId.slice(1));
    // Screen size varies by zone: Front = huge & close, Middle = moderate, Back = small & far
    const screenPct = zone==="Front"?95:zone==="Center"?65:42;
    const screenTop = zone==="Front"?(large?6:3):zone==="Center"?(large?14:7):(large?18:9);
    const screenH2 = zone==="Front"?(large?32:16):zone==="Center"?(large?22:11):(large?16:8);
    // Seat row vertical position varies: front = higher (closer), back = lower
    const seatYpct = zone==="Front"?60:zone==="Center"?72:82;
    const dist = distFor(zone);
    const h = large?200:74;
    const seatH = large?50:26;
    const backH = large?10:5;
    const cushH = large?28:15;
    const hiBackH = large?14:7;
    const hiCushH = large?34:18;
    // Ceiling height varies by zone (parallax)
    const ceilH = zone==="Front"?(large?50:25):zone==="Center"?(large?35:18):(large?24:12);
    // Row guide lines — more visible in front, fainter in back
    const guideAlpha = zone==="Front"?.06:zone==="Center"?.04:.025;
    return (
      <div style={{position:"relative",height:h,background:`linear-gradient(180deg,${zone==="Front"?"#080e1a":"#05101d"} 0%,#0c1929 55%,#111f30 100%)`,overflow:"hidden"}}>
        {/* "View from your seat" label — left side */}
        <div style={{position:"absolute",top:large?6:2,left:large?8:4,zIndex:2,display:"flex",alignItems:"center",gap:large?4:2,background:"rgba(4,8,26,.75)",border:`1px solid ${T.glassBorder}`,borderRadius:large?5:3,padding:large?"3px 7px":"1px 4px"}}>
          <svg width={large?10:6} height={large?10:6} viewBox="0 0 24 24" fill="none" stroke={T.offwhite} strokeWidth="2.5"><circle cx="12" cy="12" r="3"/><path d="M2 12s4-8 10-8 10 8 10 8-4 8-10 8-10-8-10-8z"/></svg>
          <span style={{fontSize:large?9:5,color:T.offwhite,fontWeight:600,letterSpacing:".02em"}}>View from seat</span>
        </div>
        {/* Ceiling lights */}
        {[12,30,50,70,88].map((x,i)=>(
          <div key={i} style={{position:"absolute",top:0,left:`${x}%`,width:1,height:large?ceilH:ceilH,background:`linear-gradient(to bottom,rgba(201,168,76,.45),transparent)`}}/>
        ))}
        {/* Screen — size changes dramatically by zone */}
        <div style={{position:"absolute",top:screenTop,left:"50%",transform:"translateX(-50%)",width:`${screenPct}%`,height:screenH2,background:`linear-gradient(90deg,transparent 2%,rgba(210,225,255,${zone==="Front"?.25:.14}) 15%,rgba(210,225,255,${zone==="Front"?.3:.18}) 85%,transparent 98%)`,borderRadius:zone==="Front"?3:2,boxShadow:zone==="Front"?`0 0 ${large?20:8}px rgba(210,225,255,.1)`:"none"}}/>
        <div style={{position:"absolute",top:screenTop+(large?screenH2+6:screenH2+3),left:"50%",transform:"translateX(-50%)",fontSize:large?10:6,color:`rgba(190,205,230,${zone==="Front"?.85:.65})`,letterSpacing:".14em",fontWeight:600}}>SCREEN</div>
        {/* Row guide lines */}
        {[75,64,55,47].map((y,i)=>(
          <div key={i} style={{position:"absolute",top:`${y}%`,left:`${8+i*2}%`,right:`${8+i*2}%`,height:large?2.5:1.5,background:`rgba(201,168,76,${guideAlpha+i*.015})`,borderRadius:1}}/>
        ))}
        {/* Seats at bottom */}
        <div style={{position:"absolute",bottom:0,left:0,right:0,height:seatH,display:"flex",gap:0,padding:`0 ${large?16:6}px`,alignItems:"flex-end",justifyContent:"center"}}>
          {(() => {
            const totalCols = COLS;
            const start = Math.max(1, Math.min(seatCol - 2, totalCols - 4));
            const end = Math.min(totalCols, start + 4);
            const seats = [];
            const isAccessibleSeat = seatId[0] === ACCESSIBLE_ROW;
            for (let col = start; col <= end; col++) {
              const isYou = col === seatCol;
              const accColor = isYou ? "rgba(100,160,255,.6)" : "rgba(35,50,72,.9)";
              const clr = isAccessibleSeat ? accColor : (isYou ? "rgba(201,168,76,.5)" : "rgba(35,50,72,.9)");
              const bdr = isAccessibleSeat && isYou
                ? `1px solid rgba(100,160,255,.6)`
                : `1px solid rgba(201,168,76,${isYou ? .5 : .1})`;
              const bH = isYou ? hiBackH : backH;
              const cH = isYou ? hiCushH : cushH;
              const rd = large ? 3 : 2;
              const idx = col - start;
              const pairGap = idx > 0 && col % 2 === 1 ? (large ? 5 : 3) : 0;
              const isLeftOfPair = col % 2 === 1;
              const w = large ? 40 : 18;
              seats.push(
                <div key={col} style={{width:w,display:"flex",flexDirection:"column",alignItems:"center",marginLeft:pairGap,marginRight:isLeftOfPair?(large?-2:-1):0,position:"relative"}}>
                  <div style={{width:"72%",height:bH,borderRadius:`${rd}px ${rd}px 0 0`,background:clr,border:bdr,borderBottom:"none"}}/>
                  <div style={{width:"100%",height:cH,borderRadius:isLeftOfPair?`1px 0 0 ${rd}px`:`0 1px ${rd}px 0`,background:`linear-gradient(180deg,${clr} 0%,rgba(18,28,46,.95) 100%)`,border:bdr,borderTop:"none",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {isAccessibleSeat && isYou && <span style={{fontSize:large?14:7,color:"rgba(180,210,255,.9)"}}>♿</span>}
                  </div>
                </div>
              );
            }
            return seats;
          })()}
        </div>
      </div>
    );
  };

  return (
    // Flex column fills the phone height — header top, scrollable middle, fixed bottom
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>

      {/* ── Header (sticky at top of flex column) ── */}
      <div style={{flexShrink:0,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`}}>
        <div style={{padding:"11px 16px",display:"flex",alignItems:"flex-start",gap:10}}>
          <button aria-label="Go back" onClick={()=>setReleaseModal(true)} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0,marginTop:1}}>←</button>
          <div style={{flex:1}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:15,color:T.white,lineHeight:1.2}}>{showtime.movie?.title}</div>
            <div style={{fontSize:10,color:T.grey,marginTop:2}}>{showtime.theater?.name} · {showtime.fmt} · {showtime.date} · {showtime.time}</div>
            <div style={{background:"rgba(255,255,255,.04)",border:`1px solid ${T.glassBorder}`,borderRadius:4,padding:"2px 6px",marginTop:4,display:"inline-block"}}>
              <div style={{fontSize:8,color:T.offwhite,letterSpacing:".04em",lineHeight:1.4}}>{(showtime?.showroom==="IMAX"||showtime?.showroom==="Dolby Atmos")?`SCREEN ${room.screenNum} · ${room.name.toUpperCase()}`:`SCREEN ${room.screenNum}`}</div>
              <div style={{fontSize:8,color:T.grey,letterSpacing:".04em",lineHeight:1.4}}>{room.tag} · {room.seats} seats</div>
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3,flexShrink:0}}>
            <div style={{
              background:timerUrgent?"rgba(192,57,43,.12)":"rgba(201,168,76,.06)",
              border:`1px solid ${timerUrgent?"rgba(231,76,60,.35)":T.goldBorder}`,
              borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700,
              color:timerUrgent?T.red:T.goldLight,
              display:"flex",alignItems:"center",gap:3,minWidth:52,justifyContent:"center",
              animation:timerUrgent?"pulse 1s ease-in-out infinite":"none",
              transition:"all .3s ease"
            }} aria-live="polite" aria-label={"Seat hold timer: " + timerStr + " remaining"}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={timerUrgent?T.red:T.grey} strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              {timerStr}
            </div>
            <button aria-label="Share" title="Share" style={{background:"none",border:"none",cursor:"pointer",color:T.grey,display:"flex",alignItems:"center",justifyContent:"center",padding:4}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── Scrollable seat grid + legend ── */}
      <div ref={scrollRef} style={{flex:1,overflowY:"auto"}}>
        {/* Screen arc — scales with auditorium size */}
        {(() => {
          const screenW = roomSize==="large"?310:roomSize==="small"?200:260;
          const screenH = roomSize==="large"?30:roomSize==="small"?18:24;
          const sw = roomSize==="large"?3:roomSize==="small"?2:2.5;
          return (
            <div style={{padding:"12px 10px 4px",textAlign:"center"}}>
              <svg width={screenW} height={screenH} viewBox={`0 0 ${screenW} ${screenH}`} fill="none" style={{display:"block",margin:"0 auto"}}>
                <path d={`M6 ${screenH-2} Q${screenW/2} 2 ${screenW-6} ${screenH-2}`} stroke={T.gold} strokeWidth={sw} fill="none" opacity=".7" strokeLinecap="round"/>
                <path d={`M6 ${screenH-2} Q${screenW/2} 2 ${screenW-6} ${screenH-2}`} stroke={T.goldLight} strokeWidth="1" fill="none" opacity=".3" strokeLinecap="round"/>
              </svg>
              <div style={{fontSize:roomSize==="large"?10:roomSize==="small"?8:9,color:T.grey,letterSpacing:".2em",marginTop:2}}>S C R E E N</div>
            </div>
          );
        })()}

        {/* Seat grid with section labels */}
        {(() => {
          // Scale seat dimensions based on room size
          const sW = roomSize==="large"?14:roomSize==="small"?19:17; // seat body width
          const sH = roomSize==="large"?12:roomSize==="small"?17:15; // seat body height
          const bW = roomSize==="large"?10:roomSize==="small"?15:13; // backrest width
          const bH2 = roomSize==="large"?3:roomSize==="small"?5:4;  // backrest height
          const baseW = roomSize==="large"?16:roomSize==="small"?22:20; // base width
          const pairG = roomSize==="large"?3:roomSize==="small"?6:5; // pair gap
          const rowGap = roomSize==="large"?3:roomSize==="small"?5:4;
          const minW = roomSize==="large"?340:roomSize==="small"?240:290;
          const lblSz = roomSize==="large"?8:9;
          return (
        <div style={{padding:"6px 6px 10px",overflowX:"auto"}}>
          <div style={{display:"flex",flexDirection:"column",gap:0,alignItems:"center",minWidth:minW}}>
            {ROWS.flatMap(row => {
              const section = sectionFor(row);
              const seatRow = (
                <div key={row} style={{display:"flex",alignItems:"center",gap:roomSize==="large"?1:2,marginBottom:rowGap}}>
                  <span style={{fontSize:lblSz,color:T.offwhite,width:10,flexShrink:0,marginRight:2,alignSelf:"stretch",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>{row}</span>
                  {row===ACCESSIBLE_ROW ? (
                    <div style={{display:"flex",gap:0,alignItems:"flex-end"}}>
                      {Array.from({length:COLS},(_,i)=>{
                        const id=`${row}${i+1}`;
                        const isTaken=TAKEN.has(id);
                        const isSel=selected.includes(id);
                        const isPOV=povSeat===id;
                        const isLeftOfPair = i % 2 === 0;
                        const pairStart = isPairGap(i);
                        const accBlue = "rgba(100,160,255,.7)";
                        const accBlueDim = "rgba(100,160,255,.4)";
                        return (
                          <div key={i} style={{display:"flex",alignItems:"flex-end",marginLeft:pairStart?pairG:0}}>
                            <div data-seat-id={id} role="checkbox" aria-checked={isSel} aria-disabled={isTaken} aria-label={`Seat ${id}${isTaken?" taken":isSel?" selected":" available"}`} tabIndex={isTaken?-1:0} onClick={()=>toggle(id)} onKeyDown={e=>{if(!isTaken&&(e.key===" "||e.key==="Enter")){e.preventDefault();toggle(id);}}} style={{cursor:isTaken?"default":"pointer",display:"flex",flexDirection:"column",alignItems:"center",opacity:isTaken?.45:1,transition:"transform .15s ease,opacity .15s ease",transform:isSel?"scale(1.15)":"scale(1)",marginRight:isLeftOfPair?-1:0}}>
                              <div style={{width:bW,height:bH2,borderRadius:"3px 3px 0 0",background:isSel?T.gold:accBlue,border:`1px solid ${isSel?T.gold:isPOV?T.goldBorder:accBlueDim}`,borderBottom:"none"}}/>
                              <div style={{width:sW,height:sH,borderRadius:isLeftOfPair?"2px 0 0 3px":"0 2px 3px 0",marginTop:-1,background:isSel?T.gold:isTaken?"rgba(255,255,255,.06)":"rgba(100,160,255,.25)",border:`1px solid ${isSel?T.gold:isTaken?"rgba(255,255,255,.10)":isPOV?T.goldBorder:accBlueDim}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                                {isTaken
                                  ? <svg width={sW-2} height={sH-2} viewBox={`0 0 ${sW-2} ${sH-2}`} style={{display:"block"}}><line x1="3.5" y1="3" x2={sW-5.5} y2={sH-5} stroke="rgba(200,205,215,.5)" strokeWidth="1.2"/><line x1={sW-5.5} y1="3" x2="3.5" y2={sH-5} stroke="rgba(200,205,215,.5)" strokeWidth="1.2"/></svg>
                                  : <span style={{fontSize:isSel?6:roomSize==="large"?5:7,color:isSel?T.navy:"rgba(120,180,255,.95)",fontWeight:700,lineHeight:1}}>{isSel?"✓":"♿"}</span>
                                }
                              </div>
                              <div style={{width:isLeftOfPair?baseW-1:baseW,height:3,borderRadius:isLeftOfPair?"0 0 0 2px":"0 0 2px 0",background:isSel?"rgba(201,168,76,.4)":"rgba(100,160,255,.3)"}}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{display:"flex",gap:0,alignItems:"flex-end"}}>
                      {Array.from({length:COLS},(_,i)=>{
                        const id=`${row}${i+1}`;
                        const isTaken=TAKEN.has(id);
                        const isSel=selected.includes(id);
                        const isPOV=povSeat===id;
                        const isLeftOfPair = i % 2 === 0;
                        const pairStart = isPairGap(i);
                        return (
                          <div key={i} style={{display:"flex",alignItems:"flex-end",marginLeft:pairStart?pairG:0}}>
                            <div data-seat-id={id} role="checkbox" aria-checked={isSel} aria-disabled={isTaken} aria-label={`Seat ${id}${isTaken?" taken":isSel?" selected":" available"}`} tabIndex={isTaken?-1:0} onClick={()=>toggle(id)} onKeyDown={e=>{if(!isTaken&&(e.key===" "||e.key==="Enter")){e.preventDefault();toggle(id);}}} style={{cursor:isTaken?"default":"pointer",display:"flex",flexDirection:"column",alignItems:"center",opacity:isTaken?.45:1,transition:"transform .15s ease,opacity .15s ease",transform:isSel?"scale(1.15)":"scale(1)",marginRight:isLeftOfPair?-1:0}}>
                              <div style={{width:bW,height:bH2,borderRadius:"3px 3px 0 0",background:isSel?T.gold:"rgba(201,168,76,.18)",border:`1px solid ${isSel?T.gold:isPOV?T.goldBorder:"rgba(201,168,76,.32)"}`,borderBottom:"none"}}/>
                              <div style={{width:sW,height:sH,borderRadius:isLeftOfPair?"2px 0 0 3px":"0 2px 3px 0",marginTop:-1,background:isSel?T.gold:isTaken?"rgba(255,255,255,.06)":"rgba(201,168,76,.12)",border:`1px solid ${isSel?T.gold:isTaken?"rgba(255,255,255,.10)":isPOV?T.goldBorder:"rgba(201,168,76,.32)"}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                                {isTaken
                                  ? <svg width={sW-2} height={sH-2} viewBox={`0 0 ${sW-2} ${sH-2}`} style={{display:"block"}}><line x1="3.5" y1="3" x2={sW-5.5} y2={sH-5} stroke="rgba(200,205,215,.5)" strokeWidth="1.2"/><line x1={sW-5.5} y1="3" x2="3.5" y2={sH-5} stroke="rgba(200,205,215,.5)" strokeWidth="1.2"/></svg>
                                  : <span style={{fontSize:isSel?(roomSize==="large"?6:8):(roomSize==="large"?4:5),color:isSel?T.navy:"rgba(201,168,76,.85)",fontWeight:700,lineHeight:1}}>{isSel?"✓":`${row}${i+1}`}</span>
                                }
                              </div>
                              <div style={{width:isLeftOfPair?baseW-1:baseW,height:3,borderRadius:isLeftOfPair?"0 0 0 2px":"0 0 2px 0",background:isSel?"rgba(201,168,76,.4)":"rgba(201,168,76,.18)"}}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <span aria-hidden="true" style={{width:10,flexShrink:0,marginLeft:2}}/>
                </div>
              );
              if (section) {
                return [
                  <div key={`sec-${row}`} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px 4px",width:"100%"}}>
                    <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${T.glassBorder},transparent)`}}/>
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:8,fontWeight:700,color:T.goldLight,letterSpacing:".14em"}}>{section.label}</div>
                      <div style={{fontSize:7,color:T.grey,marginTop:1}}>{section.sub}</div>
                    </div>
                    <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${T.glassBorder},transparent)`}}/>
                  </div>,
                  seatRow,
                ];
              }
              return [seatRow];
            })}
          </div>
          {/* Exit doors at back of theater */}
          <div style={{marginTop:10,display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"0 14px"}}>
            {roomSize==="large" && (
              <div style={{display:"flex",alignItems:"center",gap:4}}>
                <div style={{width:14,height:18,borderRadius:3,background:"rgba(39,174,96,.12)",border:"1.5px solid rgba(39,174,96,.35)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="rgba(39,174,96,.8)" strokeWidth="2.5"><path d="M15 21H19a2 2 0 002-2V5a2 2 0 00-2-2H15M8 17l-5-5 5-5M3 12h12"/></svg>
                </div>
                <span style={{fontSize:7,color:T.green,fontWeight:700,letterSpacing:".08em"}}>EXIT</span>
              </div>
            )}
            <div style={{flex:1,borderTop:`1px dashed ${T.greyDim}`,margin:"0 4px"}}/>
            <span style={{fontSize:7,color:T.grey,letterSpacing:".12em"}}>BACK OF THEATER</span>
            <div style={{flex:1,borderTop:`1px dashed ${T.greyDim}`,margin:"0 4px"}}/>
            <div style={{display:"flex",alignItems:"center",gap:4}}>
              <span style={{fontSize:7,color:T.green,fontWeight:700,letterSpacing:".08em"}}>EXIT</span>
              <div style={{width:14,height:18,borderRadius:3,background:"rgba(39,174,96,.12)",border:"1.5px solid rgba(39,174,96,.35)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="rgba(39,174,96,.8)" strokeWidth="2.5"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
              </div>
            </div>
          </div>
        </div>
          );
        })()}

        {/* Legend */}
        <div style={{padding:"10px 16px 16px",display:"flex",flexWrap:"wrap",gap:10,justifyContent:"center",borderTop:`1px solid ${T.glassBorder}`}}>
          {[
            {color:"rgba(201,168,76,.12)",border:"rgba(201,168,76,.32)",label:"Available"},
            {color:T.gold,border:T.gold,label:"Selected",isSel:true},
            {color:"rgba(255,255,255,.06)",border:"rgba(255,255,255,.10)",label:"Occupied",isOcc:true},
            {color:"rgba(100,160,255,.25)",border:"rgba(100,160,255,.4)",label:"Accessible",isAcc:true},
            {color:"rgba(39,174,96,.12)",border:"rgba(39,174,96,.35)",label:"Exit",isExit:true},
          ].map(({color,border,label,isAcc,isExit,isOcc,isSel})=>(
            <div key={label} style={{display:"flex",alignItems:"center",gap:4}}>
              <div style={{width:isExit?10:13,height:isExit?12:10,borderRadius:isExit?"2px":"3px 3px 2px 2px",background:color,border:`1px solid ${border}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                {isExit && <svg width="6" height="6" viewBox="0 0 24 24" fill="none" stroke="rgba(39,174,96,.8)" strokeWidth="3"><path d="M16 17l5-5-5-5M21 12H9"/></svg>}
                {isOcc && <svg width="9" height="6" viewBox="0 0 9 6" style={{display:"block"}}><line x1="2" y1="1.5" x2="7" y2="4.5" stroke="rgba(180,185,195,.5)" strokeWidth="1"/><line x1="7" y1="1.5" x2="2" y2="4.5" stroke="rgba(180,185,195,.5)" strokeWidth="1"/></svg>}
                {isAcc && <span style={{fontSize:7,color:"rgba(130,185,255,.95)"}}>♿</span>}
                {isSel && <span style={{fontSize:8,color:T.navy,fontWeight:800,lineHeight:1}}>✓</span>}
              </div>
              <span style={{fontSize:10,color:T.offwhite}}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Per-seat POV card strip — one compact card per selected seat, horizontally scrollable ── */}
      {selected.length > 0 && (
        <div style={{flexShrink:0,borderTop:`1px solid ${T.goldBorder}`,background:T.navyMid}}>
          <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
            <div style={{display:"flex",gap:8,padding:"10px 12px",width:"max-content"}}>
              {selected.map(seatId => {
                const isActive = povSeat === seatId;
                const zone = rowZone(seatId[0]);
                return (
                  <div key={seatId} onClick={()=>setPovSeat(seatId)}
                    style={{width:108,flexShrink:0,borderRadius:10,overflow:"hidden",border:`2px solid ${isActive?T.gold:T.glassBorder}`,cursor:"pointer",background:T.navyLight,transition:"border-color .15s",boxShadow:isActive?`0 0 10px rgba(201,168,76,.3)`:"none"}}>
                    <div onClick={e=>{e.stopPropagation();setExpandedPOV(seatId);}} style={{cursor:"zoom-in",position:"relative"}}>
                      <MiniPOV seatId={seatId} isActive={isActive}/>
                      <div style={{position:"absolute",top:3,right:3,width:14,height:14,borderRadius:3,background:"rgba(4,8,26,.7)",border:`1px solid ${T.glassBorder}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={T.offwhite} strokeWidth="2.5"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                      </div>
                    </div>
                    <div style={{padding:"6px 8px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:isActive?T.gold:T.white,display:"flex",alignItems:"center",gap:4}}>Seat {seatId}{seatId[0]===ACCESSIBLE_ROW&&<span style={{fontSize:8,color:"rgba(120,175,255,.95)"}}>♿</span>}</div>
                        <div style={{fontSize:8,color:T.grey}}>{zone} · {distFor(zone)}</div>
                        {seatId[0]===ACCESSIBLE_ROW&&<div style={{fontSize:7,color:"rgba(120,175,255,.9)",marginTop:1}}>Accessible · Wheelchair</div>}
                      </div>
                      <button
                        onClick={e=>{e.stopPropagation();setSelected(s=>s.filter(x=>x!==seatId));if(povSeat===seatId)setPovSeat(selected.filter(x=>x!==seatId)[0]||null);}}
                        style={{background:"rgba(192,57,43,.15)",border:"1px solid rgba(192,57,43,.3)",borderRadius:6,width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"#e74c3c",fontSize:13,flexShrink:0,lineHeight:1}}>×</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Expanded POV overlay — enlarged version of MiniPOV ── */}
      {expandedPOV && (
        <div onClick={()=>setExpandedPOV(null)} style={{position:"absolute",inset:0,zIndex:80,background:"rgba(2,4,8,.85)",backdropFilter:"blur(10px)",display:"flex",alignItems:"center",justifyContent:"center",animation:"fadeIn .2s ease",cursor:"pointer"}}>
          <div style={{width:"90%",maxWidth:320,borderRadius:16,overflow:"hidden",border:`2px solid ${T.gold}`,boxShadow:`0 0 30px rgba(201,168,76,.2)`}} onClick={e=>e.stopPropagation()}>
            <MiniPOV seatId={expandedPOV} isActive={true} large={true}/>
            <div style={{background:T.navyLight,padding:"10px 14px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:T.gold,display:"flex",alignItems:"center",gap:5}}>Seat {expandedPOV}{expandedPOV[0]===ACCESSIBLE_ROW&&<span style={{fontSize:12,color:"rgba(100,160,255,.9)"}}>♿</span>}</div>
                  <div style={{fontSize:10,color:T.grey,marginTop:2}}>{rowZone(expandedPOV[0])} · {distFor(rowZone(expandedPOV[0]))}</div>
                </div>
                <button onClick={()=>setExpandedPOV(null)} style={{background:T.navyMid,border:`1px solid ${T.greyDim}`,borderRadius:8,width:30,height:30,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:14}}>×</button>
              </div>
              {expandedPOV[0]===ACCESSIBLE_ROW&&(
                <div style={{marginTop:8,background:"rgba(100,160,255,.08)",border:"1px solid rgba(100,160,255,.2)",borderRadius:8,padding:"8px 10px",display:"flex",alignItems:"flex-start",gap:8}}>
                  <span style={{fontSize:14,flexShrink:0,marginTop:1}}>♿</span>
                  <div style={{fontSize:10,color:"rgba(180,210,255,.85)",lineHeight:1.45}}>This seat is reserved for guests requiring accessibility accommodations. Features extra legroom and aisle access.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Footer — ALWAYS visible ── */}
      <div style={{flexShrink:0,borderTop:`1px solid ${T.glassBorder}`,background:T.navyGlass,backdropFilter:"blur(20px)",padding:"12px 16px 16px"}} aria-live="polite">
        {selected.length > 0 ? (
          <>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:T.white}}>{selected.length} Seat{selected.length>1?"s":""} Selected</div>
                <div style={{fontSize:10,color:T.grey,marginTop:1}}>{selected.join(", ")}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:16,fontWeight:700,color:T.gold}}>${totalEst.toFixed(2)}</div>
                <div style={{fontSize:9,color:T.grey}}>${seatPrice.toFixed(2)}/ea + ${SERVICE_FEE_GLOBAL.toFixed(2)} fee</div>
                {showtime?.isMatinee && <div style={{fontSize:8,color:T.green,fontWeight:700,marginTop:1}}>✦ Matinee pricing</div>}
              </div>
            </div>
            <div style={{fontSize:9,color:T.grey,marginBottom:10,display:"flex",alignItems:"center",gap:4}}>
              <span style={{color:T.goldLight}}>⭐</span>
              Earn <span style={{color:T.goldLight,fontWeight:700}}>{Math.round(selected.length*seatPrice*10)} pts</span> on this purchase
              <span style={{color:T.greyDim,margin:"0 2px"}}>·</span>
              5,000 pts = $5 reward
            </div>
            <button className="btn-gold" style={{width:"100%",padding:13,fontSize:14}} onClick={()=>{setSeatData(selected);nav("ticket-qty");}}>
              Continue to Tickets →
            </button>
          </>
        ) : null}
      </div>

      {/* Release seats modal */}
      {releaseModal && (
        <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(6px)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:18,padding:"28px 22px 22px",maxWidth:280,width:"100%",textAlign:"center"}}>
            <div style={{fontSize:28,marginBottom:12}}>🎬</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:16,color:T.white,marginBottom:8}}>Release Your Seats?</div>
            <div style={{fontSize:12,color:T.grey,lineHeight:1.5,marginBottom:20}}>Going back will release your held seats.</div>
            <button className="btn-gold" style={{width:"100%",padding:12,fontSize:13,borderRadius:50,marginBottom:8}} onClick={()=>{
              setReleaseModal(false);
              onReleaseSeat();
            }}>
              Yes, Release Seats
            </button>
            <button onClick={()=>setReleaseModal(false)} style={{width:"100%",padding:10,fontSize:12,color:T.grey,background:"none",border:`1px solid ${T.greyDim}`,borderRadius:50,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Keep My Seats</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// TICKET QUANTITY
// ══════════════════════════════════════════════════════
const TicketQtyScreen = ({ showtime, seats, nav, goBack, setTicketOrder, seatTimer, onReleaseSeat }) => {
  const [releaseModal, setReleaseModal] = useState(false);
  const tLeft = seatTimer||0;
  const tStr = `${Math.floor(tLeft/60)}:${String(tLeft%60).padStart(2,'0')}`;
  const tUrgent = tLeft < 120;
  const AP = showtime?.basePrice || FORMAT_PRICING[showtime?.fmt] || 16.50;
  const CP = Math.max(AP - 5.50, 8.00); // child discount off adult
  const SP = Math.max(AP - 3.50, 10.00); // senior discount off adult
  const FEE = SERVICE_FEE_GLOBAL;
  const maxTickets = seats?.length || 0;
  // Pre-select 1 Adult for single-seat bookings — the overwhelmingly common case.
  // Multi-seat bookings start empty but surface Adult as the suggested default via
  // the gold accent bar in TicketRow (see `isSuggested` prop usage below).
  const [adult, setAdult] = useState(maxTickets === 1 ? 1 : 0);
  const [child, setChild] = useState(0);
  const [senior, setSenior] = useState(0);
  const totalTickets = adult+child+senior;
  const remaining = maxTickets - totalTickets;
  const atMax = totalTickets >= maxTickets;
  const sub = adult*AP + child*CP + senior*SP;
  const feeTotal = FEE * totalTickets;
  const isR = showtime?.movie?.rating === "R";
  // Children (ages 2–12) cannot purchase a ticket solo — they must be accompanied
  // by an adult or senior. This applies to every rating; the rule is simply made
  // more prominent on R-rated titles, where MPAA also requires an adult guardian.
  const needsGuardian = child > 0 && (adult + senior) === 0;

  const TicketRow = ({label, subLabel, price, val, setVal, note, warning, isSuggested}) => {
    const active = val > 0;
    return (
    <div style={{
      margin:"0 16px 10px",
      background:active?`linear-gradient(135deg,rgba(201,168,76,.06),rgba(201,168,76,.02))`:T.navyCard,
      border:`1px solid ${active?T.goldBorder:T.glassBorder}`,
      borderRadius:14,
      padding:"14px 16px",
      transition:"all .2s",
      position:"relative",
      overflow:"hidden"
    }}>
      {/* Suggested-default accent bar — only when idle and suggested */}
      {isSuggested && !active && (
        <div style={{position:"absolute",left:0,top:12,bottom:12,width:3,background:T.gold,borderRadius:"0 2px 2px 0"}}/>
      )}
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        {/* Label column — name on top, age range below */}
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:15,fontWeight:700,color:T.white,fontFamily:"'Outfit',sans-serif",letterSpacing:".01em",lineHeight:1.2}}>{label}</div>
          {subLabel && <div style={{fontSize:10,color:T.grey,marginTop:3,letterSpacing:".02em"}}>{subLabel}</div>}
        </div>
        {/* Price — single figure, no /ticket suffix */}
        <div style={{fontSize:15,fontWeight:700,color:active?T.gold:T.offwhite,fontVariantNumeric:"tabular-nums",flexShrink:0,fontFamily:"'Outfit',sans-serif"}}>${price.toFixed(2)}</div>
        {/* Borderless stepper — two circular buttons flanking a plain number */}
        <div style={{display:"flex",alignItems:"center",gap:9,flexShrink:0,marginLeft:4}}>
          <button
            onClick={()=>setVal(Math.max(0,val-1))}
            aria-label={`Remove one ${label} ticket`}
            disabled={val===0}
            style={{
              width:28,height:28,borderRadius:"50%",
              background:"transparent",
              border:`1.5px solid ${val>0?T.goldBorder:T.greyDim}`,
              cursor:val>0?"pointer":"default",
              color:val>0?T.gold:T.greyDim,
              fontSize:16,fontWeight:600,
              display:"flex",alignItems:"center",justifyContent:"center",
              lineHeight:1,transition:"all .15s",
              fontFamily:"'Outfit',sans-serif",padding:0
            }}>−</button>
          <span style={{minWidth:14,textAlign:"center",fontSize:16,fontWeight:700,color:val>0?T.gold:T.white,fontVariantNumeric:"tabular-nums",fontFamily:"'Outfit',sans-serif"}}>{val}</span>
          <button
            onClick={()=>{if(!atMax)setVal(val+1);}}
            aria-label={`Add one ${label} ticket`}
            disabled={atMax}
            style={{
              width:28,height:28,borderRadius:"50%",
              background:"transparent",
              border:`1.5px solid ${atMax?T.greyDim:T.goldBorder}`,
              cursor:atMax?"default":"pointer",
              color:atMax?T.greyDim:T.gold,
              fontSize:16,fontWeight:600,
              display:"flex",alignItems:"center",justifyContent:"center",
              lineHeight:1,transition:"all .15s",
              fontFamily:"'Outfit',sans-serif",padding:0
            }}>+</button>
        </div>
      </div>
      {note && <div style={{fontSize:10,color:warning?T.red:T.gold,marginTop:10,paddingTop:10,borderTop:`1px solid ${warning?"rgba(192,57,43,.25)":T.glassBorder}`,display:"flex",alignItems:"flex-start",gap:5,fontWeight:warning?600:400,lineHeight:1.4}}>{warning?"⚠️":"ⓘ"} {note}</div>}
    </div>
    );
  };

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>

      {/* ── Context header ── */}
      <div style={{flexShrink:0,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`}}>
        <div style={{padding:"12px 16px",display:"flex",alignItems:"flex-start",gap:10}}>
          <button aria-label="Go back" onClick={goBack} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0,marginTop:2}}>←</button>
          <div style={{flex:1}}>
            <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:16,color:T.white,margin:0}}>{showtime?.movie?.title}</h1>
            <div style={{fontSize:11,color:T.grey,marginTop:2}}>{showtime?.theater?.name} · {showtime?.date} · {showtime?.time}</div>
            <div style={{fontSize:11,color:T.grey,marginTop:1}}>{showtime?.fmt}{seats?.length>0?` · Seat${seats.length>1?"s":""} ${seats.join(", ")}`:""}</div>
          </div>
          <button onClick={()=>setReleaseModal(true)} style={{background:"none",border:"none",color:T.grey,fontSize:20,cursor:"pointer",flexShrink:0}}>×</button>
        </div>
        {/* Timer row */}
        <div style={{borderTop:`1px solid ${T.glassBorder}`,padding:"5px 16px 6px",display:"flex",alignItems:"center",justifyContent:"space-between",background:tUrgent?"rgba(192,57,43,.08)":"rgba(201,168,76,.04)"}}>
          <span style={{fontSize:9,color:T.grey}}>Seats held for</span>
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={tUrgent?T.red:T.grey} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            <span style={{fontSize:11,fontWeight:700,color:tUrgent?T.red:T.goldLight,fontVariantNumeric:"tabular-nums",animation:tUrgent?"pulse 1s ease-in-out infinite":"none"}}>{tStr}</span>
          </div>
        </div>
        {/* Matinee discount banner */}
        {showtime?.isMatinee && (
          <div style={{background:"rgba(39,174,96,.08)",borderTop:`1px solid rgba(39,174,96,.2)`,padding:"6px 16px",display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:10,color:T.green,fontWeight:700}}>✦ MATINEE PRICING</span>
            <span style={{fontSize:10,color:T.grey}}>— $3.00 off each ticket</span>
          </div>
        )}
      </div>

      {/* ── Scrollable body ── */}
      <div style={{flex:1,overflowY:"auto"}}>

        <div style={{paddingTop:16}}/>
        <TicketRow
          label="Adult"
          subLabel="Ages 13–59"
          price={AP}
          val={adult}
          setVal={setAdult}
          isSuggested={totalTickets===0}
        />
        <TicketRow
          label="Child"
          subLabel="Ages 2–12"
          price={CP}
          val={child}
          setVal={setChild}
          warning={needsGuardian}
          note={
            needsGuardian
              ? (isR
                  ? "R-Rated: Children must be accompanied by an adult."
                  : "Children must be accompanied by an adult.")
              : null
          }
        />
        <TicketRow label="Senior" subLabel="Ages 60+" price={SP} val={senior} setVal={setSenior}/>

        {/* ── Consolidated fine print ── */}
        <div style={{margin:"14px 16px 20px",paddingTop:14,borderTop:`1px solid ${T.glassBorder}`}}>
          <div style={{fontSize:10,color:T.grey,display:"flex",alignItems:"flex-start",gap:6,lineHeight:1.5}}>
            <span style={{lineHeight:1}}>ⓘ</span>
            <span>Prices include <strong style={{color:T.offwhite,fontWeight:600}}>${FEE.toFixed(2)}</strong> service fee · No hidden charges</span>
          </div>
          {isR && (
            <div style={{fontSize:10,color:T.gold,fontWeight:600,display:"flex",alignItems:"flex-start",gap:6,lineHeight:1.5,marginTop:10,paddingTop:10,borderTop:`1px solid ${T.glassBorder}`}}>
              <span style={{lineHeight:1}}>ⓘ</span>
              <span>
                R-Rated: ID required.
                <span style={{display:"block",fontSize:9,fontWeight:400,opacity:.75,marginTop:2}}>Attendees under 17 must be accompanied by a parent or adult guardian.</span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer — always visible ── */}
      <div style={{flexShrink:0,borderTop:`1px solid ${T.glassBorder}`,background:T.navyGlass,backdropFilter:"blur(20px)",padding:"12px 16px 16px"}}>
        {totalTickets>0 ? (
          <>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
              <span style={{fontSize:12,fontWeight:700,color:T.offwhite,letterSpacing:".05em"}}>{totalTickets}/{maxTickets} TICKET{totalTickets>1?"S":""}</span>
              <span style={{fontSize:16,fontWeight:700,color:T.gold}}>${(sub+feeTotal).toFixed(2)}</span>
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <span style={{fontSize:9,color:T.grey}}>Seats: {seats?.join(", ")}</span>
              <span style={{fontSize:9,color:T.grey}}>All fees included · No surprises</span>
            </div>
            <button className="btn-gold" disabled={remaining!==0 || needsGuardian}
              style={{width:"100%",padding:13,fontSize:14,opacity:(remaining!==0||needsGuardian)?.45:1,cursor:(remaining!==0||needsGuardian)?"default":"pointer"}}
              onClick={()=>{if(remaining===0 && !needsGuardian){setTicketOrder({adult,child,senior,total:sub,feeTotal});nav("ticket-checkout");}}}>
              {needsGuardian
                ? "Add an adult ticket"
                : remaining>0
                  ? `Assign ${remaining} more ticket${remaining>1?"s":""}`
                  : "Continue to Checkout →"}
            </button>
          </>
        ) : (
          <div style={{textAlign:"center",padding:"4px 0"}}>
            <div style={{fontSize:12,color:T.grey}}>Assign a ticket type to each of your {maxTickets} seat{maxTickets>1?"s":""}</div>
          </div>
        )}
      </div>

      {/* Release seats modal */}
      {releaseModal && (
        <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(6px)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:18,padding:"28px 22px 22px",maxWidth:280,width:"100%",textAlign:"center"}}>
            <div style={{fontSize:28,marginBottom:12}}>🎬</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:16,color:T.white,marginBottom:8}}>Release Your Seats?</div>
            <div style={{fontSize:12,color:T.grey,lineHeight:1.5,marginBottom:20}}>Going back will release your held seats.</div>
            <button className="btn-gold" style={{width:"100%",padding:12,fontSize:13,borderRadius:50,marginBottom:8}} onClick={()=>{setReleaseModal(false);onReleaseSeat();}}>
              Yes, Release Seats
            </button>
            <button onClick={()=>setReleaseModal(false)} style={{width:"100%",padding:10,fontSize:12,color:T.grey,background:"none",border:`1px solid ${T.greyDim}`,borderRadius:50,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Keep My Seats</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// TICKET CHECKOUT
// ══════════════════════════════════════════════════════
const TicketCheckoutScreen = ({ showtime, seats, ticketOrder, nav, goBack, seatTimer, addPurchasedTicket, setRewardPoints, setRewardHistory, rewardPoints, addPurchasedSeats, setNavStack, setPrevScreen, setScreen, setSeatTimer, onReleaseSeat }) => {
  const [releaseModal, setReleaseModal] = useState(false);
  const tLeft = seatTimer||0;
  const tStr = `${Math.floor(tLeft/60)}:${String(tLeft%60).padStart(2,'0')}`;
  const tUrgent = tLeft < 120;
  const [pay, setPay] = useState("apple");
  const [promo, setPromo] = useState("");
  const [promoOK, setPromoOK] = useState(false);
  const [feeSheetOpen, setFeeSheetOpen] = useState(false);
  const [rewardIncrements, setRewardIncrements] = useState(0);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const [email, setEmail] = useState("");

  const FEE = SERVICE_FEE_GLOBAL;
  const ticketCount = (ticketOrder?.adult||0)+(ticketOrder?.child||0)+(ticketOrder?.senior||0);
  const basePrice = showtime?.basePrice || FORMAT_PRICING[showtime?.fmt] || 16.50;
  const sub = ticketOrder?.total || (basePrice * ticketCount);
  const fees = FEE * Math.max(ticketCount,1);
  const rewardBalance = rewardPoints || 12480;
  const rewardValue = Math.floor(rewardBalance / 5000) * 5; // $5 per 5000 pts
  const maxIncrements = Math.floor(rewardValue / 5);
  const rewardDisc = rewardIncrements * 5;
  const disc = promoOK ? (sub*0.10) : 0;
  const total = Math.max(0, sub + fees - rewardDisc - disc);
  const ptsEarned = rewardIncrements > 0 ? 0 : Math.round(total * 10);

  const sortedSeats = seats ? [...seats].sort((a,b)=>{ const ra=a[0],rb=b[0]; if(ra!==rb) return ra.localeCompare(rb); return parseInt(a.slice(1))-parseInt(b.slice(1)); }) : [];
  const seatList = sortedSeats.join(", ") || "";
  const room = SHOWROOMS[showtime?.showroom] || SHOWROOMS["Standard-M"];
  const roomRows = room.rows;
  const roomCols = room.cols;
  const roomPrem = room.premRows || [];
  const roomAccRow = room.accRow;
  const zoneFor = id => { const r = id?.charAt(0); if(r===roomAccRow) return "Accessible"; if(r <= room.frontEnd) return "Front"; if(r <= room.midEnd) return "Center"; return "Rear"; };
  const distFor = z => z==="Front"?"~15 ft":z==="Accessible"?"~25 ft":z==="Center"?"~35 ft":"~55 ft";
  const angleFor = z => z==="Front"?"Front rows":z==="Accessible"?"Accessible row":z==="Center"?"Center rows":"Elevated";

  // Deterministic PRNG — same algorithm as SeatMapScreen so taken seats match
  const hashStr = s => { let h=0; for(let i=0;i<s.length;i++) h=((h<<5)-h)+s.charCodeAt(i)|0; return Math.abs(h); };
  const showtimeKey = `${showtime?.theater?.id}-${showtime?.movie?.id}-${showtime?.fmt}`;
  const seed = hashStr(`${showtimeKey}-${showtime?.time}`);
  const prng = i => { let h=(seed+i*374761393)|0; h=Math.imul(h^(h>>>15),0x1a85ec53); h=Math.imul(h^(h>>>13),0xc4ae3d47); return ((h^(h>>>16))>>>0)/4294967296; };
  const allCkSeats = roomRows.flatMap(r=>Array.from({length:roomCols},(_,i)=>`${r}${i+1}`));
  const leftCk = showtime?.seatsLeft?.[showtime?.time];
  const fillCk = leftCk!=null ? Math.min(0.82, Math.max(0.25, 1-leftCk/(roomRows.length*roomCols))) : 0.12+prng(999)*0.28;
  const TAKEN_CK = new Set();
  allCkSeats.forEach((s,i) => { if(prng(i+1)<fillCk) TAKEN_CK.add(s); });

  const PAYMENT_OPTS = [
    {id:"apple", label:"Apple Pay", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill={T.offwhite}><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>},
    {id:"gpay",  label:"Google Pay", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12.24 10.28V14.1h5.05c-.23 1.26-.85 2.32-1.8 3.03l2.75 2.13c1.6-1.48 2.52-3.65 2.52-6.23 0-.57-.05-1.12-.14-1.65h-8.38z" fill="#4285F4"/><path d="M5.43 14.28l-.63.48-2.23 1.73C4.24 19.77 7.84 22 12.24 22c2.97 0 5.46-.98 7.28-2.66l-2.75-2.13c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53l-.47.54z" fill="#34A853"/><path d="M2.57 6.51C1.96 7.72 1.6 9.06 1.6 10.5s.36 2.78.97 3.99l2.86-2.22c-.22-.66-.34-1.36-.34-2.09s.12-1.43.34-2.09L2.57 6.51z" fill="#FBBC05"/><path d="M12.24 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.69 1.33 15.19.25 12.24.25 7.84.25 4.24 2.48 2.57 5.77l2.86 2.22c.87-2.6 3.3-4.49 6.81-4.49z" fill="#EA4335"/></svg>},
    {id:"paypal",label:"PayPal", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M7.02 21.5L7.8 17h-2L9.64 2.5h5.56c2.5 0 4.14 1.58 3.77 4.1-.52 3.57-3.15 5.18-6.07 5.18H11l-1 5.4h-2l-.48 2.82-.5 1.5z" fill="#003087"/><path d="M9.72 16.22L10.68 11h2.22c2.14 0 4.11-1.22 4.5-3.87.28-1.9-.76-3.13-2.6-3.13h-3.6L8.2 17.5h2l-.48 2.72h2z" fill="#009CDE"/></svg>},
    {id:"venmo", label:"Venmo", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="#3D95CE"><path d="M19.78 3.38c.66 1.09.96 2.21.96 3.64 0 4.53-3.87 10.42-7.01 14.55H7.23L4.22 3.88l5.55-.53 1.81 14.49c1.68-2.74 3.76-7.06 3.76-10 0-1.36-.23-2.29-.62-3.05l5.06-1.41z"/></svg>},
    {id:"card",  label:"Credit / Debit Card", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.offwhite} strokeWidth="1.8"><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/><path d="M5 15h4M14 15h5"/></svg>},
  ];

  // Mini seat map for checkout — compact pure SVG for scroll performance
  const miniMapJSX = useMemo(() => {
    const sz = room.size;
    const sw = sz==="large"?7:sz==="small"?10:9;
    const sh = sz==="large"?6:sz==="small"?8:7;
    const bw = sz==="large"?5:sz==="small"?8:7;
    const bh = sz==="large"?2:sz==="small"?3:2.5;
    const pg = sz==="large"?2:sz==="small"?4:3;
    const rg = sz==="large"?3:sz==="small"?4:3.5;
    const lblW = 10;
    const pad = 8;
    const seatStepX = sw + 0.5;
    const pairsPerRow = Math.floor(roomCols / 2);
    const gridW = roomCols * seatStepX + (pairsPerRow - 1) * pg;
    const totalW = lblW + pad + gridW + pad;
    const secH = 14;
    const rowH = bh + sh + 1.5 + rg;
    const screenArcH = 22;
    let totalH = screenArcH;
    roomRows.forEach(r => { if (room.sections[r]) totalH += secH; totalH += rowH; });
    totalH += 4;
    const els = [];
    const scrW = gridW + 20;
    const scrX = (totalW - scrW) / 2;
    els.push(<path key="scr1" d={`M${scrX+4} ${screenArcH-4} Q${totalW/2} 2 ${scrX+scrW-4} ${screenArcH-4}`} stroke={T.gold} strokeWidth={sz==="large"?2:1.5} fill="none" opacity=".65" strokeLinecap="round"/>);
    els.push(<text key="scrt" x={totalW/2} y={screenArcH} textAnchor="middle" fill={T.grey} fontSize={sz==="large"?6:7} letterSpacing=".18em" fontFamily="'Outfit',sans-serif">S C R E E N</text>);
    let cy = screenArcH + 4;
    roomRows.forEach((row) => {
      const sec = room.sections[row];
      if (sec) {
        const ly = cy + secH / 2;
        els.push(<line key={`sl-${row}`} x1={pad} y1={ly} x2={totalW-pad} y2={ly} stroke={T.glassBorder} strokeWidth=".5" opacity=".6"/>);
        const lw = sec.label.length * 4.5 + 8;
        els.push(<rect key={`sb-${row}`} x={totalW/2-lw/2} y={ly-5.5} width={lw} height={11} fill="rgba(4,8,15,.6)"/>);
        els.push(<text key={`st-${row}`} x={totalW/2} y={ly+3.5} textAnchor="middle" fill={T.goldLight} fontSize="6" fontWeight="700" letterSpacing=".12em" fontFamily="'Outfit',sans-serif">{sec.label}</text>);
        cy += secH;
      }
      const isAcc = row === room.accRow;
      const rowMidY = cy + (bh + sh) / 2 + 1;
      els.push(<text key={`rl-${row}`} x={pad} y={rowMidY + 2} textAnchor="middle" fill={T.offwhite} fontSize={sz==="large"?5.5:6.5} fontFamily="'Outfit',sans-serif">{row}</text>);
      let sx = lblW + pad;
      for (let i = 0; i < roomCols; i++) {
        if (i > 0 && i % 2 === 0) sx += pg;
        const id = `${row}${i+1}`;
        const isMine = seats?.includes(id);
        const isTaken = TAKEN_CK.has(id);
        const op = isTaken && !isMine ? 0.35 : 1;
        const fill = isMine ? T.gold : isAcc ? "rgba(100,160,255,.35)" : isTaken ? "rgba(255,255,255,.07)" : "rgba(201,168,76,.22)";
        const stroke = isMine ? T.gold : isAcc ? "rgba(100,160,255,.5)" : isTaken ? "rgba(255,255,255,.1)" : "rgba(201,168,76,.4)";
        els.push(<rect key={`b-${id}`} x={sx+(sw-bw)/2} y={cy} width={bw} height={bh} rx="1.5" fill={fill} stroke={stroke} strokeWidth=".5" opacity={op}/>);
        els.push(<rect key={`s-${id}`} x={sx} y={cy+bh-0.5} width={sw} height={sh} rx="1" fill={fill} stroke={stroke} strokeWidth=".5" opacity={op}/>);
        if (isTaken && !isMine) {
          const cx = sx + sw/2;
          const ccy = cy + bh + sh/2;
          const d = sz==="large"?1:1.4;
          els.push(<line key={`x1-${id}`} x1={cx-d} y1={ccy-d} x2={cx+d} y2={ccy+d} stroke="rgba(200,205,215,.7)" strokeWidth=".6" opacity={op}/>);
          els.push(<line key={`x2-${id}`} x1={cx+d} y1={ccy-d} x2={cx-d} y2={ccy+d} stroke="rgba(200,205,215,.7)" strokeWidth=".6" opacity={op}/>);
        }
        if (isMine) {
          els.push(<rect key={`g-${id}`} x={sx-1} y={cy-1} width={sw+2} height={bh+sh+2} rx="2" fill="none" stroke={T.gold} strokeWidth=".5" opacity=".35"/>);
        }
        sx += seatStepX;
      }
      cy += rowH;
    });
    return (
      <div role="img" aria-label={`Seat map showing your selected seats: ${seatList}. ${room.name}, ${roomRows.length} rows, ${roomCols} seats per row.`}
        style={{background:"rgba(4,8,15,.6)",borderRadius:10,padding:"8px 52px",marginBottom:0,contain:"content"}}>
        <svg width="100%" viewBox={`0 0 ${totalW} ${totalH}`} preserveAspectRatio="xMidYMid meet" style={{display:"block"}}>
          {els}
        </svg>
      </div>
    );
  }, [seats, showtime?.showroom]);

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0,position:"relative"}}>
      {/* Fee explanation sheet */}
      {feeSheetOpen && (
        <div style={{position:"absolute",inset:0,zIndex:400}} onClick={()=>setFeeSheetOpen(false)}>
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.6)"}}/>
          <div onClick={e=>e.stopPropagation()} style={{position:"absolute",bottom:0,left:0,right:0,background:"#fff",borderRadius:"20px 20px 0 0",padding:"24px 20px 40px",overflow:"hidden",overscrollBehavior:"contain",boxShadow:"0 -8px 40px rgba(0,0,0,.5)"}}>
            <div style={{textAlign:"center",marginBottom:16}}>
              <button onClick={()=>setFeeSheetOpen(false)} aria-label="Close" style={{display:"block",width:48,height:16,padding:"6px 6px 6px 6px",margin:"0 auto 10px",background:"none",border:"none",cursor:"pointer"}}>
                <div style={{width:36,height:4,borderRadius:2,background:"#ddd",margin:"0 auto"}}/>
              </button>
              <div style={{fontSize:16,fontWeight:800,color:"#111",letterSpacing:".06em"}}>SERVICE FEE</div>
            </div>
            <p style={{fontSize:14,color:"#444",lineHeight:1.6,marginBottom:20}}>Includes service and transaction fees for secure credit card processing and mobile ticket delivery.</p>
            <div style={{borderTop:"1px solid #eee",paddingTop:16}}>
              <div style={{fontSize:12,fontWeight:800,color:"#111",letterSpacing:".08em",marginBottom:12}}>DETAILS</div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #eee"}}>
                <span style={{fontSize:14,color:"#444"}}>Tickets ({ticketCount})</span>
                <span style={{fontSize:14,color:"#111",fontWeight:600}}>${fees.toFixed(2)}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",paddingTop:12}}>
                <span style={{fontSize:15,fontWeight:800,color:"#111"}}>Total Fees</span>
                <span style={{fontSize:15,fontWeight:800,color:"#111"}}>${fees.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── "Before You Buy" confirmation sheet ── */}
      {summaryOpen && (
        <div style={{position:"absolute",inset:0,zIndex:400}} onClick={()=>setSummaryOpen(false)}>
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.55)"}}/>
          <div onClick={e=>e.stopPropagation()} style={{position:"absolute",bottom:0,left:0,right:0,background:T.navyMid,borderRadius:"20px 20px 0 0",padding:"20px 20px 32px",border:`1px solid ${T.glassBorder}`,borderBottom:"none",overflow:"hidden",overscrollBehavior:"contain",boxShadow:"0 -8px 40px rgba(0,0,0,.5)"}}>
            <div style={{textAlign:"center",marginBottom:16}}>
              <button onClick={()=>setSummaryOpen(false)} aria-label="Close" style={{display:"block",width:48,height:16,padding:"6px",margin:"0 auto 8px",background:"none",border:"none",cursor:"pointer"}}>
                <div style={{width:36,height:4,borderRadius:2,background:T.greyDim,margin:"0 auto"}}/>
              </button>
              <div style={{fontSize:14,fontWeight:700,color:T.white,fontFamily:"'Playfair Display',serif"}}>Before You Buy</div>
              <div style={{fontSize:10,color:T.grey,marginTop:3}}>Please review your order</div>
            </div>
            {/* Summary grid */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
              {[
                ["🎬",showtime?.movie?.title||"Movie"],
                ["📅",`${showtime?.date||"Today"} · ${showtime?.time||""}`],
                ["logo",showtime?.theater?.short||showtime?.theater?.name||"Theater",`Screen ${room.screenNum}`],
                ["💺",`${seatList} (${ticketCount} ticket${ticketCount!==1?"s":""})`],
                ["📽️",showtime?.fmt||"Standard"],
                ["💰",`$${total.toFixed(2)} total`],
              ].map(([icon,val,sub],i)=>(
                <div key={i} style={{background:T.navyCard,borderRadius:10,padding:"10px 12px",border:`1px solid ${T.glassBorder}`}}>
                  <div style={{fontSize:14,marginBottom:3}}>{icon==="logo"?<Logo size={0.55}/>:icon}</div>
                  <div style={{fontSize:11,color:T.white,fontWeight:500,lineHeight:1.4}}>{val}</div>
                  {sub&&<div style={{fontSize:10,color:T.grey,lineHeight:1.4}}>{sub}</div>}
                </div>
              ))}
            </div>
            {rewardIncrements>0&&<div style={{textAlign:"center",fontSize:11,color:T.gold,fontWeight:600,marginBottom:12}}>🎁 ${rewardDisc.toFixed(2)} reward applied</div>}
            <button className="btn-gold" style={{width:"100%",padding:14,fontSize:14,borderRadius:50}} onClick={()=>{
              setSummaryOpen(false);
              const ptsEarned = Math.round(total * 10);
              addPurchasedTicket({
                title:showtime?.movie?.title,
                date:showtime?.date||"Today",
                time:showtime?.time,
                theater:showtime?.theater?.name||"ReelView Grand Cineplex",
                theaterShort:showtime?.theater?.short||showtime?.theater?.name||"Theater",
                seats:seats||[],
                fmt:showtime?.fmt||"Standard",
                showroom:showtime?.showroom||"Standard-M",
                movie:showtime?.movie,
                theaterObj:showtime?.theater,
                upcoming:true,
                total
              });
              if(rewardIncrements>0){
                const ptsSpent = rewardIncrements * 5000;
                setRewardPoints(p=>p - ptsSpent + ptsEarned);
                setRewardHistory(h=>[
                  ...(ptsEarned>0?[{l:`Movie Ticket — ${showtime?.movie?.title}`,p:`+${ptsEarned}`,d:"Today"}]:[]),
                  {l:`Reward Redeemed — ${showtime?.movie?.title}`,p:`-${ptsSpent.toLocaleString()}`,d:"Today"},
                  ...h
                ]);
              } else {
                setRewardPoints(p=>p+ptsEarned);
                setRewardHistory(h=>[{l:`Movie Ticket — ${showtime?.movie?.title}`,p:`+${ptsEarned}`,d:"Today"},...h]);
              }
              setSeatTimer(null);
              addPurchasedSeats(showtime, seats||[]);
              setNavStack(["movies","ticket-confirm"]);
              setPrevScreen("movies");
              setScreen("ticket-confirm");
            }}>
              Confirm Purchase · ${total.toFixed(2)}
            </button>
            <button onClick={()=>setSummaryOpen(false)} style={{width:"100%",padding:10,fontSize:12,color:T.grey,background:"none",border:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",marginTop:6}}>Go Back</button>
          </div>
        </div>
      )}

      {/* ── Context header ── */}
      <div style={{flexShrink:0,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`}}>
        <div style={{padding:"12px 16px",display:"flex",alignItems:"flex-start",gap:10}}>
          <button aria-label="Go back" onClick={goBack} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0,marginTop:2}}>←</button>
          <div style={{flex:1}}>
            <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:16,color:T.white,margin:0}}>{showtime?.movie?.title}</h1>
            <div style={{fontSize:11,color:T.grey,marginTop:2}}>{showtime?.theater?.name} · {showtime?.date} · {showtime?.time}</div>
            <div style={{fontSize:11,color:T.grey,marginTop:1}}>{showtime?.fmt} · Screen {room.screenNum}</div>
          </div>
          <button onClick={()=>setReleaseModal(true)} style={{background:"none",border:"none",color:T.grey,fontSize:20,cursor:"pointer",flexShrink:0}}>×</button>
        </div>
        {/* Timer bar */}
        <div style={{padding:"5px 16px 6px",borderTop:`1px solid ${T.glassBorder}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:tUrgent?"rgba(192,57,43,.08)":"rgba(201,168,76,.04)"}}>
          <span style={{fontSize:9,color:T.grey}}>Seats held for</span>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={tUrgent?T.red:T.grey} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            <span style={{fontSize:10,fontWeight:700,color:tUrgent?T.red:T.goldLight,fontVariantNumeric:"tabular-nums",animation:tUrgent?"pulse 1s ease-in-out infinite":"none"}}>{tStr}</span>
          </div>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div style={{flex:1,overflowY:"auto"}}>
      <div style={{padding:"16px 16px 8px"}}>

        {/* ── Ticket Summary ── */}
        <div style={{marginBottom:22}}>
          <div style={{fontSize:13,fontWeight:700,color:T.white,marginBottom:10,fontFamily:"'Outfit',sans-serif"}}>Ticket Summary</div>
          <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:14}}>
            {/* Seat map */}
            {miniMapJSX}
            {/* Ticket + seat details */}
            <div style={{marginTop:12,padding:"10px 0 8px",borderTop:`1px solid ${T.glassBorder}`}}>
              <div style={{fontSize:12,fontWeight:700,color:T.white}}>{ticketCount} Ticket{ticketCount!==1?"s":""}</div>
              <div style={{fontSize:11,color:T.grey,marginTop:2}}>Seat{ticketCount>1?"s":""} {seatList}</div>
            </div>
            {/* Trailers First */}
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:8,background:"rgba(130,170,220,.08)",border:"1px solid rgba(130,170,220,.20)"}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(150,185,230,.8)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span style={{fontSize:10,color:T.offwhite,lineHeight:1.4}}>Trailers play first — the feature starts ~20 min after showtime.</span>
            </div>
          </div>
        </div>

        {/* ── Payment ── */}
        <div style={{marginBottom:22}}>
          <div style={{fontSize:13,fontWeight:700,color:T.white,marginBottom:10,fontFamily:"'Outfit',sans-serif"}}>Payment</div>
          {/* Prototype disclaimer */}
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:10,background:"rgba(201,168,76,.06)",border:`1px solid ${T.goldBorder}`,marginBottom:10}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span style={{fontSize:9,color:T.grey,lineHeight:1.4}}><strong style={{color:T.goldLight}}>Prototype</strong> — No real payments. Do not enter real information.</span>
          </div>
          {/* Quick-pay row */}
          <div style={{display:"flex",gap:8,marginBottom:8}}>
            {PAYMENT_OPTS.filter(o=>o.id!=="card").map(opt=>(
              <button key={opt.id} onClick={()=>setPay(opt.id)} style={{flex:1,padding:"10px 4px",borderRadius:10,background:pay===opt.id?T.goldDim:T.navyCard,border:`1.5px solid ${pay===opt.id?T.goldBorder:T.glassBorder}`,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3,transition:"all .15s"}}>
                <span style={{fontSize:16}}>{opt.icon}</span>
                <span style={{fontSize:8,fontWeight:600,color:pay===opt.id?T.goldLight:T.grey,fontFamily:"'Outfit',sans-serif"}}>{opt.label.split(" ")[0]}</span>
              </button>
            ))}
          </div>
          {/* Card option */}
          <button onClick={()=>setPay("card")} style={{width:"100%",padding:"10px 14px",borderRadius:10,background:pay==="card"?T.goldDim:T.navyCard,border:`1.5px solid ${pay==="card"?T.goldBorder:T.glassBorder}`,cursor:"pointer",display:"flex",alignItems:"center",gap:10,transition:"all .15s"}}>
            <span style={{fontSize:16}}>💳</span>
            <span style={{fontSize:12,fontWeight:500,color:pay==="card"?T.goldLight:T.offwhite,fontFamily:"'Outfit',sans-serif"}}>Credit / Debit Card</span>
          </button>
          {pay==="card"&&(
            <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:8}}>
              <input className="inp" aria-label="Card number" placeholder="Card Number"/>
              <div style={{display:"flex",gap:8}}>
                <input className="inp" aria-label="Card expiration date" placeholder="MM / YY" style={{flex:1}}/>
                <input className="inp" aria-label="Card security code" placeholder="CVV" style={{flex:1}}/>
              </div>
              <input className="inp" aria-label="Name on card" placeholder="Name on Card"/>
            </div>
          )}
        </div>

        {/* ── Rewards + Promo — single expand-on-tap row ── */}
        <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:12,marginBottom:22,overflow:"hidden"}}>
          <button onClick={()=>setRewardsOpen(!rewardsOpen)} style={{width:"100%",background:"none",border:"none",cursor:"pointer",padding:"12px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",fontFamily:"'Outfit',sans-serif"}}>
            <div style={{display:"flex",alignItems:"center",gap:9,minWidth:0}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={(rewardDisc>0||promoOK)?T.goldLight:T.grey} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
              <span aria-live="polite" style={{fontSize:12,color:(rewardDisc>0||promoOK)?T.white:T.offwhite,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                {(rewardDisc>0||promoOK)
                  ? <>{rewardDisc>0&&<span style={{color:T.gold,fontWeight:600}}>${rewardDisc} reward</span>}{rewardDisc>0&&promoOK&&<span style={{color:T.grey}}> · </span>}{promoOK&&<span style={{color:T.green,fontWeight:600}}>Promo applied</span>}</>
                  : "Add a reward or promo"}
              </span>
            </div>
            <span style={{color:T.grey,fontSize:11,transform:rewardsOpen?"rotate(180deg)":"none",transition:"transform .2s",flexShrink:0,marginLeft:8}}>▾</span>
          </button>
          {rewardsOpen&&(
            <div style={{padding:"0 14px 14px",borderTop:`1px solid ${T.glassBorder}`}}>
              {/* Reward stepper */}
              <div style={{padding:"12px 0",borderBottom:`1px solid ${T.glassBorder}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.white}}>{rewardBalance.toLocaleString()} pts</div>
                  <div style={{fontSize:9,color:T.grey,marginTop:1}}>${rewardValue.toFixed(2)} available · 5,000 pts = $5</div>
                </div>
                {maxIncrements>0?(
                  <div role="group" aria-label="Reward amount" style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                    <button onClick={()=>setRewardIncrements(Math.max(0,rewardIncrements-1))} aria-label="Decrease reward" style={{width:26,height:26,borderRadius:"50%",background:rewardIncrements>0?T.navyLight:"transparent",border:`1.5px solid ${rewardIncrements>0?T.goldBorder:T.greyDim}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:rewardIncrements>0?T.gold:T.greyDim,fontSize:14,fontWeight:700}}>−</button>
                    <span aria-live="polite" aria-atomic="true" style={{fontSize:13,fontWeight:700,color:rewardIncrements>0?T.gold:T.grey,minWidth:30,textAlign:"center",fontVariantNumeric:"tabular-nums"}}>${rewardDisc}</span>
                    <button onClick={()=>setRewardIncrements(Math.min(maxIncrements,rewardIncrements+1))} aria-label="Increase reward" style={{width:26,height:26,borderRadius:"50%",background:rewardIncrements<maxIncrements?T.navyLight:"transparent",border:`1.5px solid ${rewardIncrements<maxIncrements?T.goldBorder:T.greyDim}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:rewardIncrements<maxIncrements?T.gold:T.greyDim,fontSize:14,fontWeight:700}}>+</button>
                  </div>
                ):(
                  <span style={{fontSize:9,color:T.grey,flexShrink:0}}>Need 5,000+ pts</span>
                )}
              </div>
              {/* Promo input */}
              <div style={{paddingTop:12,display:"flex",gap:8}}>
                <input className="inp" aria-label="Promo code" value={promo} onChange={e=>setPromo(e.target.value)} placeholder="Promo code" style={{flex:1,padding:"9px 12px",fontSize:12}}/>
                <button className="btn-outline" style={{padding:"9px 12px",fontSize:11,whiteSpace:"nowrap"}} onClick={()=>promo.length>2&&setPromoOK(true)}>Apply</button>
              </div>
            </div>
          )}
        </div>

        {/* ── Contact ── */}
        <div style={{marginBottom:22}}>
          <div style={{fontSize:13,fontWeight:700,color:T.white,marginBottom:10,fontFamily:"'Outfit',sans-serif"}}>Contact</div>
          {/* Prototype disclaimer */}
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:10,background:"rgba(201,168,76,.06)",border:`1px solid ${T.goldBorder}`,marginBottom:10}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span style={{fontSize:9,color:T.grey,lineHeight:1.4}}><strong style={{color:T.goldLight}}>Prototype</strong> — Do not enter your real email address.</span>
          </div>
          <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:14}}>
            <div style={{fontSize:11,fontWeight:600,color:T.offwhite,marginBottom:2}}>Email Address</div>
            <div style={{fontSize:10,color:T.grey,lineHeight:1.4,marginBottom:10}}>We use your email address to send you your e-ticket and order confirmation receipt.</div>
            <input className="inp" type="email" aria-label="Email address" value={email} onChange={e=>setEmail(e.target.value)} placeholder="email@example.com" style={{width:"100%",padding:"11px 14px",fontSize:12}}/>
          </div>
        </div>

        {/* ── Order Summary — always visible ── */}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:700,color:T.white,marginBottom:10,fontFamily:"'Outfit',sans-serif"}}>Order Summary</div>
          <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:"12px 14px"}}>
            {ticketOrder?.adult>0&&(
              <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12}}>
                <span style={{color:T.offwhite}}>Adult × {ticketOrder.adult}</span>
                <span style={{color:T.white,fontWeight:600,fontVariantNumeric:"tabular-nums"}}>${(basePrice*ticketOrder.adult).toFixed(2)}</span>
              </div>
            )}
            {ticketOrder?.child>0&&(
              <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12}}>
                <span style={{color:T.offwhite}}>Child × {ticketOrder.child}</span>
                <span style={{color:T.white,fontWeight:600,fontVariantNumeric:"tabular-nums"}}>${((basePrice-5.50)*ticketOrder.child).toFixed(2)}</span>
              </div>
            )}
            {ticketOrder?.senior>0&&(
              <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12}}>
                <span style={{color:T.offwhite}}>Senior × {ticketOrder.senior}</span>
                <span style={{color:T.white,fontWeight:600,fontVariantNumeric:"tabular-nums"}}>${((basePrice-3.50)*ticketOrder.senior).toFixed(2)}</span>
              </div>
            )}
            <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:11}}>
              <span style={{color:T.grey}}>Service Fee ({ticketCount}×${FEE.toFixed(2)})</span>
              <span style={{color:T.grey,fontVariantNumeric:"tabular-nums"}}>${fees.toFixed(2)}</span>
            </div>
            {rewardIncrements>0&&(
              <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:11}}>
                <span style={{color:T.gold}}>Reward</span>
                <span style={{color:T.gold,fontVariantNumeric:"tabular-nums"}}>−${rewardDisc.toFixed(2)}</span>
              </div>
            )}
            {promoOK&&(
              <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:11}}>
                <span style={{color:T.green}}>Promo (10% off)</span>
                <span style={{color:T.green,fontVariantNumeric:"tabular-nums"}}>−${disc.toFixed(2)}</span>
              </div>
            )}
            <div style={{height:1,background:T.glassBorder,margin:"8px 0 6px"}}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
              <span style={{fontSize:13,fontWeight:700,color:T.white}}>Total</span>
              <span style={{fontSize:16,fontWeight:800,color:T.gold,fontVariantNumeric:"tabular-nums"}}>${total.toFixed(2)}</span>
            </div>
            {ptsEarned>0&&<div style={{fontSize:9,color:T.gold,textAlign:"right",marginTop:2}}>+{ptsEarned} pts earned</div>}
          </div>
        </div>

        {/* ── Terms ── */}
        <div style={{fontSize:9,color:T.grey,textAlign:"center",marginBottom:14,lineHeight:1.6}}>
          No refunds after showtime. By purchasing, I agree to the <span style={{color:T.gold,textDecoration:"underline",cursor:"pointer"}}>ReelView Terms of Use</span>.
        </div>
      </div>

      {/* ── Sticky purchase footer ── */}
      </div>{/* end scrollable body */}

      <div style={{flexShrink:0,borderTop:`1px solid ${T.glassBorder}`,background:T.navyGlass,backdropFilter:"blur(20px)",padding:"12px 16px 18px"}}>
        {rewardIncrements>0&&<div style={{fontSize:9,color:T.gold,textAlign:"center",marginBottom:6,fontWeight:600}}>⭐ ${rewardDisc.toFixed(2)} reward applied</div>}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
            <div>
              <div style={{fontSize:10,color:T.grey}}>{ticketCount} ticket{ticketCount!==1?"s":""} · {seatList}</div>
              <div style={{fontSize:15,fontWeight:700,color:T.gold}}>${total.toFixed(2)}</div>
            </div>
          </div>
          <button className="btn-gold" style={{padding:"13px 28px",fontSize:14,borderRadius:50}} onClick={()=>setSummaryOpen(true)}>
            Purchase
          </button>
        </div>
      </div>

      {/* Release seats modal */}
      {releaseModal && (
        <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(6px)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:18,padding:"28px 22px 22px",maxWidth:280,width:"100%",textAlign:"center"}}>
            <div style={{fontSize:28,marginBottom:12}}>🎬</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:16,color:T.white,marginBottom:8}}>Release Your Seats?</div>
            <div style={{fontSize:12,color:T.grey,lineHeight:1.5,marginBottom:20}}>Going back will release your held seats.</div>
            <button className="btn-gold" style={{width:"100%",padding:12,fontSize:13,borderRadius:50,marginBottom:8}} onClick={()=>{setReleaseModal(false);onReleaseSeat();}}>
              Yes, Release Seats
            </button>
            <button onClick={()=>setReleaseModal(false)} style={{width:"100%",padding:10,fontSize:12,color:T.grey,background:"none",border:`1px solid ${T.greyDim}`,borderRadius:50,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Keep My Seats</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// TICKET CONFIRMATION
// ══════════════════════════════════════════════════════
const TicketConfirmScreen = ({ showtime, seats, nav, goBack, prevScreen, autoInvite, setAutoInvite }) => {
  const seatList = seats?.join(", ") || "F6, F7";
  const movieTitle = showtime?.movie?.title || "Wuthering Heights";
  const theaterShort = showtime?.theater?.short || "ReelView Grand";
  const timeStr = showtime?.time || "7:00 PM";
  const dateStr = showtime?.date || "Today";
  const room = SHOWROOMS[showtime?.showroom] || SHOWROOMS["Standard-M"];
  const shareMsg = `Hey! I just got tickets to ${movieTitle} — ${dateStr} at ${timeStr}, ${theaterShort}. I'm in seat${seats?.length>1?"s":""} ${seatList}. Grab a seat next to me! 🎬🍿`;
  const [inviteOpen, setInviteOpen] = useState(autoInvite||false);
  const [invited, setInvited] = useState([]);
  const [sentConfirm, setSentConfirm] = useState(false);
  const [shareTicketIdx, setShareTicketIdx] = useState(null); // which individual seat is being shared
  const [splitOpen, setSplitOpen] = useState(false); // split & share panel
  // Clear autoInvite flag after it's consumed
  useEffect(()=>{ if(autoInvite && setAutoInvite) setAutoInvite(false); },[]);
  const code = "RV-"+Math.random().toString(36).substr(2,8).toUpperCase();
  const CONTACTS = [
    {name:"Jordan Kim",initials:"JK",phone:"(213) 555-0194"},
    {name:"Maya Thompson",initials:"MT",phone:"(310) 555-0287"},
    {name:"Aiden Park",initials:"AP",phone:"(323) 555-0143"},
    {name:"Sofia Reyes",initials:"SR",phone:"(213) 555-0361"},
    {name:"Daniel Wu",initials:"DW",phone:"(310) 555-0428"},
  ];
  const toggleInv = n => setInvited(s => s.includes(n) ? s.filter(x=>x!==n) : [...s,n]);

  return (
    <div style={{flex:1,minHeight:0,display:"flex",flexDirection:"column",position:"relative",background:T.navy}}>
    <div style={{flex:1,minHeight:0,overflowY:"auto",paddingBottom:40}}>
      <div style={{padding:"14px 16px",borderBottom:`1px solid ${T.glassBorder}`,display:"flex",alignItems:"center",gap:10}}>
        <button aria-label="Go back" onClick={goBack} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0}}>←</button>
        <div style={{flex:1,display:"flex",justifyContent:"center"}}><Logo size={0.9}/></div>
        <div style={{width:34}}/>
      </div>
      <div style={{padding:"16px 16px"}}>

        {/* ── Success header (only on fresh purchase, not when viewing later) ── */}
        {prevScreen!=="myreelview" && (
          <div style={{textAlign:"center",marginBottom:18}}>
            <div style={{width:48,height:48,borderRadius:"50%",background:`linear-gradient(135deg,${T.gold},${T.goldLight})`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 10px",boxShadow:`0 4px 20px ${T.goldDim}`}}>
              <span style={{fontSize:22,color:T.navy}}>✓</span>
            </div>
            <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:T.white,marginBottom:4,margin:"0 0 4px"}}>You're all set!</h1>
            <div style={{fontSize:12,color:T.grey}}>Confirmation sent to your email</div>
            <div style={{fontSize:11,color:T.gold,fontWeight:600,marginTop:4}}>+{Math.round((showtime?.basePrice||16.50)*(seats?.length||1)*10)} reward points earned ⭐</div>
          </div>
        )}

        {/* ── Combined E-Ticket ── */}
        <div style={{background:`linear-gradient(145deg,${T.navyLight},${T.navyMid})`,border:`1px solid ${T.glassBorder}`,borderRadius:20,padding:20,marginBottom:18,position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:-28,right:-28,width:100,height:100,borderRadius:"50%",background:T.goldDim,pointerEvents:"none"}}/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
            <Logo size={0.72}/>
            <span className="tag">E-TICKET</span>
          </div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:T.white,marginBottom:2}}>{movieTitle}</div>
          <div style={{fontSize:11,color:T.grey,marginBottom:14}}>{showtime?.movie?.genre||"Sci-Fi"} · {showtime?.movie?.rating||"PG-13"}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
            {[["Date",dateStr],["Time",timeStr],["Screen",String(room.screenNum)],["Seats",seatList],["Format",showtime?.fmt||"Standard"],["Theater",theaterShort]].map(([k,v])=>(
              <div key={k}><div style={{fontSize:9,color:T.grey,marginBottom:1,letterSpacing:".07em",textTransform:"uppercase"}}>{k}</div><div style={{fontSize:12,fontWeight:600,color:T.white}}>{v}</div></div>
            ))}
          </div>
          {/* Tear line */}
          <div style={{display:"flex",alignItems:"center",gap:0,margin:"14px -20px"}}>
            <div style={{width:18,height:18,borderRadius:"50%",background:T.navy,flexShrink:0}}/>
            <div style={{flex:1,borderTop:`2px dashed ${T.greyDim}`}}/>
            <div style={{width:18,height:18,borderRadius:"50%",background:T.navy,flexShrink:0}}/>
          </div>
          {/* Barcode */}
          <div style={{textAlign:"center",paddingTop:2}}>
            <div style={{display:"flex",gap:1.5,justifyContent:"center",marginBottom:5}}>
              {Array.from({length:34},(_,i)=><div key={i} style={{width:i%5===0?3:i%3===0?2:1.5,height:40,background:T.white,borderRadius:1,opacity:.3+((i*17)%7)*.1}}/>)}
            </div>
            <div style={{fontSize:9,color:T.grey,letterSpacing:".18em"}}>{code}</div>
          </div>
        </div>

        {/* ── Split & Share (only for multi-seat purchases) ── */}
        {(seats||[]).length>1&&(
          <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:14,overflow:"hidden",marginBottom:14}}>
            <button onClick={()=>setSplitOpen(!splitOpen)} className="press" style={{width:"100%",padding:"12px 16px",background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",fontFamily:"'Outfit',sans-serif"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
                <div style={{textAlign:"left"}}>
                  <div style={{fontSize:13,fontWeight:600,color:T.white}}>Split and Share Tickets</div>
                  <div style={{fontSize:10,color:T.grey}}>Send individual tickets to friends</div>
                </div>
              </div>
              <span style={{color:T.grey,fontSize:12,transform:splitOpen?"rotate(180deg)":"none",transition:"transform .2s"}}>▾</span>
            </button>
            {splitOpen&&(
              <div style={{padding:"0 16px 14px"}}>
                {(seats||[]).map((seat,idx)=>(
                  <div key={seat} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderTop:`1px solid ${T.glassBorder}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:32,height:32,borderRadius:8,background:T.goldDim,border:`1px solid ${T.goldBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:T.gold}}>{seat}</div>
                      <div>
                        <div style={{fontSize:12,fontWeight:600,color:T.white}}>Seat {seat}</div>
                        <div style={{fontSize:9,color:T.grey}}>Row {seat.charAt(0)} · Screen {room.screenNum}</div>
                      </div>
                    </div>
                    <button onClick={()=>{setShareTicketIdx(idx);setInviteOpen(true);setSentConfirm(false);}} className="press" style={{background:T.navyLight,border:`1px solid ${T.goldBorder}`,borderRadius:8,padding:"6px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
                      <span style={{fontSize:10,color:T.gold,fontWeight:600,fontFamily:"'Outfit',sans-serif"}}>Share</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Tell a Friend ── */}
        <button className="btn-gold" style={{width:"100%",padding:"12px 14px",fontSize:13,marginBottom:14,display:"flex",alignItems:"center",justifyContent:"center",gap:8}} onClick={()=>{setInviteOpen(true);setSentConfirm(false);setInvited([]);setShareTicketIdx(null);}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
            Tell a Friend
        </button>

        {/* ── Concessions upsell ── */}
        <div style={{background:`linear-gradient(135deg,rgba(255,160,50,.08),rgba(201,168,76,.04))`,border:`1px solid rgba(255,160,50,.2)`,borderRadius:16,padding:"14px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:12}}>
          <div style={{fontSize:36,flexShrink:0}}>🍿</div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:T.white,marginBottom:2}}>Skip the line</div>
            <div style={{fontSize:10,color:T.grey,lineHeight:1.4,marginBottom:8}}>Pre-order snacks now and pick them up when you arrive — no waiting.</div>
            <button className="btn-outline" style={{padding:"8px 14px",fontSize:11}} onClick={()=>nav("concessions")}>
              Order Concessions →
            </button>
          </div>
        </div>

        {/* ── Secondary actions ── */}
        <div style={{display:"flex",gap:10,marginBottom:14}}>
          <button className="btn-outline" style={{flex:1,padding:11,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
            Add to Wallet
          </button>
          <button className="btn-outline" style={{flex:1,padding:11,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            📅 Add to Calendar
          </button>
        </div>

        {/* Back navigation handled by top-left arrow */}
      </div>
    </div>{/* end inner scrollable div */}

      {/* ── Native Share Sheet (simulated) — fixed to bottom of screen ── */}
      {inviteOpen && (
        <div style={{position:"absolute",inset:0,zIndex:300,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}>
          <div onClick={()=>{setInviteOpen(false);setSentConfirm(false);}} style={{position:"absolute",inset:0,background:"rgba(0,0,0,.55)"}}/>
          <div style={{background:"#1c1c1e",borderRadius:"14px 14px 0 0",maxHeight:"72vh",display:"flex",flexDirection:"column",animation:"slideUp .25s ease",position:"relative",overflowY:"auto",zIndex:1}}>

            {/* Drag handle — tap to dismiss */}
            <div style={{display:"flex",justifyContent:"center",padding:"8px 0 4px"}}>
              <button onClick={()=>{setInviteOpen(false);setSentConfirm(false);}} aria-label="Close" style={{width:48,height:16,padding:"6px",background:"none",border:"none",cursor:"pointer"}}>
                <div style={{width:36,height:4,borderRadius:2,background:"rgba(255,255,255,.25)",margin:"0 auto"}}/>
              </button>
            </div>

            {/* Message preview card */}
            <div style={{margin:"4px 16px 14px",background:"rgba(255,255,255,.08)",borderRadius:12,padding:"12px 14px",display:"flex",alignItems:"flex-start",gap:10}}>
              <div style={{width:40,height:40,borderRadius:8,background:shareTicketIdx!=null?"rgba(201,168,76,.15)":T.goldDim,border:`1px solid ${T.goldBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{shareTicketIdx!=null?"🎟️":"🎬"}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:T.white,lineHeight:1.3,marginBottom:3}}>{shareTicketIdx!=null?`Ticket · Seat ${(seats||[])[shareTicketIdx]||""}`:movieTitle}</div>
                <div style={{fontSize:10,color:T.grey,lineHeight:1.4,overflow:"hidden",textOverflow:"ellipsis",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{shareTicketIdx!=null?`${movieTitle} — ${dateStr} at ${timeStr}, Screen ${room.screenNum}. Seat ${(seats||[])[shareTicketIdx]||""}. Show this e-ticket at the door.`:shareMsg}</div>
              </div>
            </div>

            {/* Share destination grid — mimics native OS share sheet */}
            <div style={{padding:"0 16px 12px",display:"flex",gap:0,justifyContent:"space-between"}}>
              {[
                {icon:"💬",label:"Messages",color:"#34C759"},
                {icon:"📋",label:"Copy Link",color:"#8E8E93"},
                {icon:"📧",label:"Mail",color:"#007AFF"},
                {icon:"📱",label:"More…",color:"#636366"},
              ].map((app,i)=>(
                <div key={i} onClick={()=>setSentConfirm(true)}
                  style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,cursor:"pointer",width:68}}>
                  <div style={{width:50,height:50,borderRadius:12,background:app.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>{app.icon}</div>
                  <span style={{fontSize:10,color:T.grey,textAlign:"center",lineHeight:1.2}}>{app.label}</span>
                </div>
              ))}
            </div>

            {/* Divider */}
            <div style={{height:1,background:"rgba(255,255,255,.08)",margin:"0 16px"}}/>

            {/* Action list — mimics native share sheet actions */}
            <div style={{padding:"6px 16px 20px"}}>
              {[
                {icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EBEBF5" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>, label:"Share via Contacts", sub:"Send to people in your phone"},
                {icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EBEBF5" strokeWidth="1.8"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>, label:"Copy Ticket Text", sub:"Paste it anywhere you like"},
                {icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EBEBF5" strokeWidth="1.8"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/></svg>, label:"Share Link", sub:"Open in any app"},
              ].map((action,i)=>(
                <div key={i} onClick={()=>setSentConfirm(true)}
                  style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:i<2?`1px solid rgba(255,255,255,.06)`:"none",cursor:"pointer"}}>
                  <div style={{width:32,height:32,borderRadius:8,background:"rgba(255,255,255,.07)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{action.icon}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,color:T.white,fontWeight:500}}>{action.label}</div>
                    <div style={{fontSize:10,color:T.grey,marginTop:1}}>{action.sub}</div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.greyDim} strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                </div>
              ))}
            </div>

            {/* Cancel button */}
            <div style={{padding:"0 16px 20px"}}>
              <button onClick={()=>{setInviteOpen(false);setSentConfirm(false);}} style={{width:"100%",padding:12,background:"rgba(255,255,255,.08)",border:"none",borderRadius:12,color:T.white,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Cancel</button>
            </div>

            {sentConfirm && (
              <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(6px)",borderRadius:"14px 14px 0 0",display:"flex",alignItems:"center",justifyContent:"center",animation:"fadeIn .2s ease"}}>
                <div style={{textAlign:"center",padding:20}}>
                  <div style={{width:52,height:52,borderRadius:"50%",background:"rgba(39,174,96,.15)",border:`2px solid ${T.green}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",fontSize:22,animation:"scaleIn .3s ease"}}>✓</div>
                  <div style={{fontSize:15,fontWeight:700,color:T.white,marginBottom:4}}>{shareTicketIdx!=null?"Ticket ready to send!":"Almost there!"}</div>
                  <div style={{fontSize:11,color:T.grey,lineHeight:1.5,maxWidth:240,margin:"0 auto"}}>{shareTicketIdx!=null?`Your e-ticket for Seat ${(seats||[])[shareTicketIdx]||""} has been copied. Your sharing app will open — just hit send to deliver it.`:"Your invite has been copied. Your sharing app will open — just hit send to share it with your friends."}</div>
                  <button className="btn-outline" style={{marginTop:16,padding:"10px 28px",fontSize:12}} onClick={()=>{setInviteOpen(false);setSentConfirm(false);}}>Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// THEATERS
// ══════════════════════════════════════════════════════
// ── THEATER FILTER SHEET ──────────────────────────────
const TheaterFilterSheet = ({ onClose, selected, setSelected }) => {
  useModalDismiss(onClose);
  const OPTIONS = ["All","IMAX","Dolby Atmos","Recliner Seats","Bar & Lounge"];
  const toggle = v => {
    if(v==="All"){ setSelected(["All"]); return; }
    setSelected(s => { const n=s.filter(x=>x!=="All"); const r=n.includes(v)?n.filter(x=>x!==v):[...n,v]; return r.length===0?["All"]:r; });
  };
  const filterCount = selected.includes("All")?0:selected.length;
  return (
    <div style={{position:"absolute",inset:0,zIndex:300}} onClick={onClose}>
      <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.55)"}}/>
      <div onClick={e=>e.stopPropagation()} style={{position:"absolute",bottom:0,left:0,right:0,background:T.navyMid,borderRadius:"20px 20px 0 0",maxHeight:"78%",display:"flex",flexDirection:"column",overflow:"hidden",overscrollBehavior:"contain",boxShadow:"0 -8px 40px rgba(0,0,0,.5)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 12px",borderBottom:`1px solid ${T.glassBorder}`,flexShrink:0}}>
          <span style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700}}>Filter Theaters</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.grey,fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <div style={{overflowY:"auto",flex:1,minHeight:0}}>
          {OPTIONS.map(opt=>{
            const on = selected.includes(opt);
            return (
              <div key={opt} role="checkbox" aria-checked={on} tabIndex={0} onClick={()=>toggle(opt)} onKeyDown={e=>{if(e.key===" "||e.key==="Enter"){e.preventDefault();toggle(opt);}}} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 20px",borderBottom:`1px solid ${T.glassBorder}`,cursor:"pointer"}}>
                <span style={{fontSize:14,color:on?T.white:T.offwhite,fontWeight:on?600:400}}>{opt}</span>
                {on && <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8l4 4 6-7" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
            );
          })}
        </div>
        <div style={{flexShrink:0,padding:"12px 20px 24px",borderTop:`1px solid ${T.glassBorder}`,display:"flex",gap:10}}>
          <button onClick={()=>setSelected(["All"])} style={{background:"none",border:"none",color:T.gold,fontFamily:"'Outfit',sans-serif",fontWeight:600,fontSize:14,cursor:"pointer"}}>Clear All</button>
          <button className="btn-gold" onClick={onClose} style={{flex:1,padding:13,fontSize:14}}>Show Theaters{filterCount>0?` (${filterCount} filter${filterCount>1?"s":""})`:"" }</button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// THEATERS LIST SCREEN
// ══════════════════════════════════════════════════════
const TheatersScreen = ({ nav, setTheater, setTheaterInitTab }) => {
  const [tab, setTab] = useState("near");
  const [favTheaters, setFavTheaters] = useState([2]);
  const [showFilter, setShowFilter] = useState(false);
  const [theaterFilters, setTheaterFilters] = useState(["All"]);
  const toggleFav = (e,id) => { e.stopPropagation(); setFavTheaters(f=>f.includes(id)?f.filter(x=>x!==id):[...f,id]); };
  const hasTheaterFilters = !theaterFilters.includes("All");
  // Filter theaters by amenities
  const filteredTheaters = hasTheaterFilters
    ? THEATERS.filter(th=>theaterFilters.every(f=>th.amenities.some(a=>a.toLowerCase().includes(f.toLowerCase()))))
    : THEATERS;

  // Styled map node positions for each theater
  const PIN_POSITIONS = [[38,42],[62,28],[50,62]];

  return (
    <div style={{flex:1,minHeight:0,display:"flex",flexDirection:"column",position:"relative"}}>
      {showFilter && <TheaterFilterSheet onClose={()=>setShowFilter(false)} selected={theaterFilters} setSelected={setTheaterFilters}/>}

      {/* ── Header ── */}
      <div style={{padding:"14px 16px 0",position:"sticky",top:0,zIndex:50,background:T.navyGlass,backdropFilter:"blur(20px)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:20,color:T.white,margin:0}}>Theaters</h1>
          <div style={{display:"flex",gap:8}}>
            <button aria-label="Search" style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:4}}><svg viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="19" height="19"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg></button>
            <button onClick={()=>setShowFilter(true)} style={{background:hasTheaterFilters?T.goldDim:T.navyLight,border:`1px solid ${hasTheaterFilters?T.goldBorder:T.greyDim}`,borderRadius:8,width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",position:"relative"}}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M1.5 4h12M4 7.5h7M6 11h3" stroke={hasTheaterFilters?T.goldLight:T.offwhite} strokeWidth="1.5" strokeLinecap="round"/></svg>
              {hasTheaterFilters&&<div style={{position:"absolute",top:-3,right:-3,width:14,height:14,borderRadius:"50%",background:T.gold,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:800,color:T.navy}}>{theaterFilters.length}</div>}
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div style={{display:"flex",gap:0,borderBottom:`1px solid ${T.glassBorder}`}}>
          {[["near","Theaters Near You"],["fav","My Favorite Theaters"]].map(([id,label])=>(
            <button key={id} onClick={()=>setTab(id)} className={tab===id?"tab-on":"tab-off"} style={{flex:1,background:"none",border:"none",cursor:"pointer",padding:"10px 0",fontSize:12,fontWeight:700,letterSpacing:".02em",fontFamily:"'Outfit',sans-serif",textAlign:"center",transition:"color .2s"}}>{label}</button>
          ))}
        </div>
      </div>

      {/* ── CSS map ── */}
      <div style={{position:"relative",height:180,background:"#242F3E",borderBottom:`1px solid ${T.glassBorder}`,overflow:"hidden"}}>
        {/* Water / park regions */}
        <div style={{position:"absolute",left:"-5%",bottom:"-10%",width:"45%",height:"50%",background:"#17263C",borderRadius:"40% 60% 30% 50%",opacity:.7}}/>
        <div style={{position:"absolute",right:"10%",top:"5%",width:"22%",height:"30%",background:"#1B3A2A",borderRadius:"50% 40% 55% 45%",opacity:.5}}/>
        {/* Roads — each pin near its own road */}
        <svg style={{position:"absolute",inset:0,width:"100%",height:"100%"}} viewBox="0 0 360 180" preserveAspectRatio="none">
          {/* Major horizontal highway */}
          <path d="M0 95 Q60 88 140 92 Q200 96 260 85 Q320 78 360 82" stroke="#3D4D65" strokeWidth="4" fill="none"/>
          <path d="M0 95 Q60 88 140 92 Q200 96 260 85 Q320 78 360 82" stroke="#4A5C78" strokeWidth="2" fill="none"/>
          {/* Vertical road near pin 1 (left) */}
          <path d="M95 0 Q90 45 100 90 Q108 135 105 180" stroke="#3D4D65" strokeWidth="3" fill="none"/>
          <path d="M95 0 Q90 45 100 90 Q108 135 105 180" stroke="#4A5C78" strokeWidth="1.5" fill="none"/>
          {/* Diagonal road near pin 2 (center-top) */}
          <path d="M170 0 Q190 40 210 80 Q230 120 260 180" stroke="#3D4D65" strokeWidth="2.5" fill="none"/>
          <path d="M170 0 Q190 40 210 80 Q230 120 260 180" stroke="#4A5C78" strokeWidth="1.2" fill="none"/>
          {/* Curved road near pin 3 (right) */}
          <path d="M280 0 Q300 50 310 100 Q315 140 330 180" stroke="#3D4D65" strokeWidth="2.5" fill="none"/>
          <path d="M280 0 Q300 50 310 100 Q315 140 330 180" stroke="#4A5C78" strokeWidth="1.2" fill="none"/>
          {/* Secondary roads */}
          <path d="M0 40 Q80 55 160 42 Q240 30 360 50" stroke="#353F50" strokeWidth="1.5" fill="none"/>
          <path d="M0 145 Q100 155 200 140 Q300 130 360 148" stroke="#353F50" strokeWidth="1.5" fill="none"/>
        </svg>
        {/* "You are here" blue dot */}
        <div style={{position:"absolute",left:"10%",top:"58%",transform:"translate(-50%,-50%)",zIndex:2}}>
          <div style={{width:12,height:12,borderRadius:"50%",background:"#4285F4",border:"2px solid #fff",boxShadow:"0 0 8px rgba(66,133,244,.6)"}}/>
        </div>
        {/* Pins with name + distance labels */}
        {THEATERS.map((th,i)=>{
          const positions = [{left:"18%",top:"52%"},{left:"52%",top:"26%"},{left:"82%",top:"72%"}];
          return (
            <button key={th.id} onClick={()=>{setTheater(th);setTheaterInitTab("showtimes");nav("theater-detail");}} style={{position:"absolute",...positions[i],transform:"translate(-50%,-100%)",background:"none",border:"none",cursor:"pointer",padding:0,zIndex:2}}>
              <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                <div style={{width:28,height:28,background:T.gold,borderRadius:"50% 50% 50% 0",transform:"rotate(-45deg)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 3px 10px rgba(201,168,76,.4)`,border:`2px solid ${T.navy}`}}>
                  <span style={{transform:"rotate(45deg)",fontSize:12,fontWeight:800,color:T.navy}}>{i+1}</span>
                </div>
                <div style={{marginTop:4,background:"rgba(4,8,15,.8)",borderRadius:4,padding:"2px 6px",whiteSpace:"nowrap"}}>
                  <div style={{fontSize:9,fontWeight:700,color:T.offwhite,fontFamily:"'Outfit',sans-serif",lineHeight:1.3}}>{th.short}</div>
                  <div style={{fontSize:8,color:T.gold,fontFamily:"'Outfit',sans-serif",lineHeight:1.3}}>{th.distance}</div>
                </div>
              </div>
            </button>
          );
        })}
        {/* Location label */}
        <div style={{position:"absolute",bottom:8,left:12,display:"flex",alignItems:"center",gap:5,background:"rgba(36,47,62,.85)",backdropFilter:"blur(6px)",borderRadius:6,padding:"4px 8px",border:"1px solid rgba(255,255,255,.12)"}}>
          <div style={{width:6,height:6,borderRadius:"50%",background:"#4285F4"}}/>
          <span style={{fontSize:10,color:T.offwhite,fontWeight:500}}>Los Angeles, CA</span>
        </div>
        {/* Change button */}
        <button style={{position:"absolute",bottom:8,right:12,background:"rgba(36,47,62,.85)",backdropFilter:"blur(6px)",border:"1px solid rgba(255,255,255,.12)",borderRadius:6,padding:"4px 10px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>
          <span style={{fontSize:10,color:T.gold,fontWeight:700,fontFamily:"'Outfit',sans-serif",lineHeight:1}}>Change</span>
        </button>
      </div>

      {/* ── Theater venue cards ── */}
      <div style={{flex:1,overflowY:"auto",padding:"10px 16px",paddingBottom:80}}>
        {(tab==="near"?filteredTheaters:filteredTheaters.filter(t=>favTheaters.includes(t.id))).map((th)=>(
          <div key={th.id} className="glass-card" style={{marginBottom:14,overflow:"hidden",cursor:"pointer"}} onClick={()=>{setTheater(th);setTheaterInitTab("showtimes");nav("theater-detail");}}>
            {/* Card header with gradient accent — mirrors showtimes card language */}
            <div style={{padding:"14px 16px 12px",position:"relative"}}>
              <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${T.gold},rgba(201,168,76,.2))`}} role="presentation"/>
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:T.white,lineHeight:1.25,marginBottom:4}}>{th.name}</div>
                  <div style={{fontSize:11,color:T.gold,lineHeight:1.4}}>{th.distance} · {th.address}</div>
                  <div style={{fontSize:10,color:T.grey,marginTop:4,lineHeight:1.4}}>{th.screens} Screens · {th.amenities.join(" · ")}</div>
                </div>
                <button aria-label={favTheaters.includes(th.id)?`Remove ${th.name} from favorites`:`Add ${th.name} to favorites`} onClick={e=>toggleFav(e,th.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:17,color:favTheaters.includes(th.id)?T.gold:T.grey,lineHeight:1,padding:0,transition:"color .2s",flexShrink:0,marginTop:2}}>
                  {favTheaters.includes(th.id)?"♥":"♡"}
                </button>
              </div>
            </div>
            {/* Card footer actions — distinct buttons */}
            <div style={{display:"flex",gap:8,padding:"12px 16px 14px",borderTop:`1px solid ${T.glassBorder}`}}>
              <button className="btn-outline" style={{flex:1,padding:"9px 0",fontSize:11,fontWeight:600,borderRadius:8,fontFamily:"'Outfit',sans-serif"}} onClick={e=>{e.stopPropagation();setTheater(th);setTheaterInitTab("info");nav("theater-detail");}}>
                Details
              </button>
              <button className="btn-gold" style={{flex:1,padding:"9px 0",fontSize:11,fontWeight:700,borderRadius:8}} onClick={e=>{e.stopPropagation();setTheater(th);setTheaterInitTab("showtimes");nav("theater-detail");}}>
                Showtimes
              </button>
            </div>
          </div>
        ))}
        {tab==="near" && hasTheaterFilters && filteredTheaters.length===0 && (
          <div style={{textAlign:"center",padding:"36px 20px",color:T.grey}}>
            <div style={{fontSize:28,marginBottom:10}}>🎭</div>
            <div style={{fontSize:13,marginBottom:12}}>No theaters match your filters</div>
            <button className="btn-outline" style={{padding:"9px 20px",fontSize:12}} onClick={()=>setTheaterFilters(["All"])}>Clear Filters</button>
          </div>
        )}
        {tab==="fav" && filteredTheaters.filter(t=>favTheaters.includes(t.id)).length===0 && (
          <div style={{textAlign:"center",padding:"48px 24px",color:T.grey}}>
            <div style={{fontSize:28,marginBottom:10}}>♡</div>
            <div style={{fontSize:14}}>{hasTheaterFilters?"No favorite theaters match your filters":"No favorite theaters yet — tap ♡ on any theater to save it."}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// THEATER DETAIL SCREEN
// ══════════════════════════════════════════════════════
const TheaterDetailScreen = ({ theater, nav, goBack, setMovie, setShowtime, initTab, setSeatTimer, setSeatData, setTicketOrder }) => {
  const [tab, setTab] = useState(initTab || "showtimes");
  const [selectedDate, setSelectedDate] = useState(0);
  const [movieSheetOpen, setMovieSheetOpen] = useState(false);
  const [movieFilter, setMovieFilter] = useState("All Movies");
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [openPreview, setOpenPreview] = useState(null);
  const [priceInfoOpen, setPriceInfoOpen] = useState(null);

  // Esc closes inline popovers
  useEffect(() => {
    if (openPreview==null && priceInfoOpen==null && !movieSheetOpen) return;
    const onKey = e => { if (e.key === "Escape") { setOpenPreview(null); setPriceInfoOpen(null); setMovieSheetOpen(false); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openPreview, priceInfoOpen, movieSheetOpen]);

  if(!theater) return null;

  const dates = [
    {short:"Today", day:"", date:""},
    {short:"Tue",   day:"Tue", date:"6/3"},
    {short:"Wed",   day:"Wed", date:"6/4"},
    {short:"Thu",   day:"Thu", date:"6/5"},
    {short:"Fri",   day:"Fri", date:"6/6"},
    {short:"Sat",   day:"Sat", date:"6/7"},
  ];

  const AMENITY_GRID = [
    {icon:"🛋️", label:"Luxury Recliners"},
    {icon:"♿", label:"Wheelchair Access"},
    {icon:"🍔", label:"Dine-In Delivery"},
    {icon:"🎮", label:"Arcade Lounge"},
    {icon:"🍸", label:"Bar & Cocktails"},
    {icon:"🎧", label:"Audio Description"},
    {icon:"🚗", label:"Free Parking"},
    {icon:"👶", label:"Sensory Friendly"},
  ];

  // All-movies bottom sheet
  const MovieSheet = () => {
    useModalDismiss(()=>setMovieSheetOpen(false));
    const options = ["All Movies",...MOVIES.map(m=>m.title)];
    return (
    <div style={{position:"absolute",inset:0,zIndex:300}} onClick={()=>setMovieSheetOpen(false)}>
      <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.55)"}}/>
      <div onClick={e=>e.stopPropagation()} style={{position:"absolute",bottom:0,left:0,right:0,background:T.navyMid,borderRadius:"20px 20px 0 0",maxHeight:"80%",display:"flex",flexDirection:"column",overflow:"hidden",overscrollBehavior:"contain",boxShadow:"0 -8px 40px rgba(0,0,0,.5)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 12px",borderBottom:`1px solid ${T.glassBorder}`,flexShrink:0}}>
          <span style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700}}>Movies</span>
          <button onClick={()=>setMovieSheetOpen(false)} style={{background:"none",border:"none",color:T.grey,fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <div style={{overflowY:"auto",flex:1,minHeight:0}}>
          {options.map(title=>{
            const isSelected = title === movieFilter;
            return (
            <div key={title} onClick={()=>{setMovieFilter(title);setMovieSheetOpen(false);}} style={{padding:"14px 20px",borderBottom:`1px solid ${T.glassBorder}`,cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
              {isSelected
                ? <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8l4 4 6-7" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                : <span style={{width:14}}/>
              }
              <span style={{fontSize:14,color:isSelected?T.gold:T.white,fontWeight:isSelected?700:400}}>{title}</span>
            </div>
          );})}
        </div>
      </div>
    </div>
  );};

  return (
    <div style={{flex:1,minHeight:0,display:"flex",flexDirection:"column",position:"relative"}}>
      {movieSheetOpen && <MovieSheet/>}

      {/* ── Sticky header ── */}
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:10,position:"sticky",top:0,zIndex:50,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`,flexShrink:0}}>
        <button aria-label="Go back" onClick={goBack} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0}}>←</button>
        <div style={{flex:1,minWidth:0}}>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:14,color:T.white,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",margin:0}}>{theater.name}</h1>
          <div style={{fontSize:10,color:T.grey,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{theater.address} · {theater.city}</div>
        </div>
        <button style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.gold,flexShrink:0}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
        </button>
      </div>

      {/* ── Change Theatre + contact row ── */}
      <div style={{background:T.navyCard,borderBottom:`1px solid ${T.glassBorder}`,flexShrink:0}}>
        <div style={{padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <button onClick={()=>setInfoExpanded(!infoExpanded)} style={{background:"none",border:"none",cursor:"pointer",color:T.gold,fontSize:11,fontWeight:700,fontFamily:"'Outfit',sans-serif",display:"flex",alignItems:"center",gap:4}}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 3.5L5 6.5L8 3.5" stroke={T.gold} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Change Theater
            </button>
          </div>
          <div style={{display:"flex",gap:14}}>
            <button style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              <span style={{fontSize:11,color:T.grey}}>{theater.phone}</span>
            </button>
          </div>
        </div>

        {/* Mini inline map — zoomed-in view matching pin's overview position */}
        <div style={{position:"relative",height:120,background:"#242F3E",overflow:"hidden"}}>
          {/* Per-theater terrain */}
          {theater.id===1 && <>
            {/* Downtown — pin along highway, vertical road offset to the right */}
            <div style={{position:"absolute",left:"-15%",bottom:"-10%",width:"40%",height:"60%",background:"#17263C",borderRadius:"40% 55% 35% 50%",opacity:.6}}/>
            <svg style={{position:"absolute",inset:0,width:"100%",height:"100%"}} viewBox="0 0 360 120" preserveAspectRatio="none">
              {/* Main highway — pin sits on this */}
              <path d="M0 52 Q80 48 180 50 Q280 54 360 46" stroke="#3D4D65" strokeWidth="3.5" fill="none"/>
              <path d="M0 52 Q80 48 180 50 Q280 54 360 46" stroke="#4A5C78" strokeWidth="1.5" fill="none"/>
              {/* Vertical road — offset right of pin */}
              <path d="M260 0 Q265 40 262 80 Q258 100 264 120" stroke="#3D4D65" strokeWidth="2.5" fill="none"/>
              <path d="M260 0 Q265 40 262 80 Q258 100 264 120" stroke="#4A5C78" strokeWidth="1.2" fill="none"/>
              {/* Minor roads */}
              <path d="M0 22 Q100 28 200 20 Q300 14 360 22" stroke="#353F50" strokeWidth="1.2" fill="none"/>
              <path d="M0 90 Q120 96 240 88 Q320 82 360 90" stroke="#353F50" strokeWidth="1.2" fill="none"/>
              <path d="M100 0 Q105 50 98 120" stroke="#353F50" strokeWidth="1" fill="none"/>
            </svg>
            <span style={{position:"absolute",left:"8%",top:"10%",fontSize:7,color:"#5A6A80",fontFamily:"'Outfit',sans-serif",fontWeight:500,letterSpacing:1,textTransform:"uppercase"}}>Beverly Hills</span>
            <span style={{position:"absolute",right:"10%",top:"10%",fontSize:7,color:"#4A5A70",fontFamily:"'Outfit',sans-serif",fontWeight:500,letterSpacing:1,textTransform:"uppercase"}}>Downtown</span>
          </>}
          {theater.id===2 && <>
            {/* Hollywood — pin along diagonal road, highway below */}
            <div style={{position:"absolute",right:"12%",top:"8%",width:"20%",height:"28%",background:"#1B3A2A",borderRadius:"45% 55% 50% 40%",opacity:.5}}/>
            <svg style={{position:"absolute",inset:0,width:"100%",height:"100%"}} viewBox="0 0 360 120" preserveAspectRatio="none">
              {/* Diagonal road — pin sits on this */}
              <path d="M120 0 Q155 30 180 55 Q210 85 250 120" stroke="#3D4D65" strokeWidth="3.5" fill="none"/>
              <path d="M120 0 Q155 30 180 55 Q210 85 250 120" stroke="#4A5C78" strokeWidth="1.5" fill="none"/>
              {/* Highway — runs below pin */}
              <path d="M0 85 Q90 80 180 84 Q270 88 360 78" stroke="#3D4D65" strokeWidth="2.5" fill="none"/>
              <path d="M0 85 Q90 80 180 84 Q270 88 360 78" stroke="#4A5C78" strokeWidth="1.2" fill="none"/>
              {/* Minor roads */}
              <path d="M0 30 Q80 24 160 32 Q260 38 360 26" stroke="#353F50" strokeWidth="1.2" fill="none"/>
              <path d="M300 0 Q290 50 295 120" stroke="#353F50" strokeWidth="1" fill="none"/>
            </svg>
            <span style={{position:"absolute",left:"8%",top:"10%",fontSize:7,color:"#5A6A80",fontFamily:"'Outfit',sans-serif",fontWeight:500,letterSpacing:1,textTransform:"uppercase"}}>Hollywood</span>
            <span style={{position:"absolute",right:"8%",bottom:"18%",fontSize:7,color:"#4A5A70",fontFamily:"'Outfit',sans-serif",fontWeight:500,letterSpacing:1,textTransform:"uppercase"}}>Westside</span>
          </>}
          {theater.id===3 && <>
            {/* Marina Bay — pin along curved road, water to south/east */}
            <div style={{position:"absolute",right:"-10%",bottom:"-20%",width:"50%",height:"60%",background:"#17263C",borderRadius:"50% 40% 30% 55%",opacity:.65}}/>
            <svg style={{position:"absolute",inset:0,width:"100%",height:"100%"}} viewBox="0 0 360 120" preserveAspectRatio="none">
              {/* Curved road — pin sits on this */}
              <path d="M140 0 Q160 25 175 50 Q188 75 200 120" stroke="#3D4D65" strokeWidth="3.5" fill="none"/>
              <path d="M140 0 Q160 25 175 50 Q188 75 200 120" stroke="#4A5C78" strokeWidth="1.5" fill="none"/>
              {/* Highway — runs above pin */}
              <path d="M0 28 Q100 34 200 26 Q300 20 360 30" stroke="#3D4D65" strokeWidth="2.5" fill="none"/>
              <path d="M0 28 Q100 34 200 26 Q300 20 360 30" stroke="#4A5C78" strokeWidth="1.2" fill="none"/>
              {/* Minor roads */}
              <path d="M0 72 Q120 78 240 68 Q320 62 360 72" stroke="#353F50" strokeWidth="1.2" fill="none"/>
              <path d="M80 0 Q75 50 82 120" stroke="#353F50" strokeWidth="1" fill="none"/>
              <path d="M300 0 Q295 40 305 120" stroke="#353F50" strokeWidth="1" fill="none"/>
            </svg>
            <span style={{position:"absolute",left:"8%",top:"10%",fontSize:7,color:"#5A6A80",fontFamily:"'Outfit',sans-serif",fontWeight:500,letterSpacing:1,textTransform:"uppercase"}}>Marina del Rey</span>
            <span style={{position:"absolute",right:"10%",top:"10%",fontSize:7,color:"#4A5A70",fontFamily:"'Outfit',sans-serif",fontWeight:500,letterSpacing:1,textTransform:"uppercase"}}>Harbor</span>
          </>}
          {/* Pin — always centered */}
          <div style={{position:"absolute",left:"50%",top:"46%",transform:"translate(-50%,-100%)"}}>
            <div style={{width:24,height:24,background:T.gold,borderRadius:"50% 50% 50% 0",transform:"rotate(-45deg)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 3px 10px rgba(201,168,76,.4)`,border:`2px solid ${T.navy}`}}/>
          </div>
          <div style={{position:"absolute",bottom:6,right:8,fontSize:9,color:"#5A6A80",opacity:.8}}>Los Angeles, CA</div>
        </div>
      </div>

      {/* ── Pill tab switcher (mirrors Movie Detail) ── */}
      <div style={{padding:"12px 16px",background:T.navy,flexShrink:0}}>
        <div style={{display:"flex",background:T.navyLight,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:3,gap:2}}>
          {["Showtimes","Features","Info"].map(t=>{
            const key=t.toLowerCase();
            const on=tab===key;
            return (
              <button key={t} onClick={()=>setTab(key)} style={{flex:1,padding:"8px 0",borderRadius:9,background:on?T.gold:"transparent",color:on?T.navy:T.grey,border:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:700,fontSize:12,letterSpacing:".03em",transition:"all .2s"}}>{t}</button>
            );
          })}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div style={{flex:1,overflowY:"auto",paddingBottom:80}}>

        {/* ── SHOWTIMES ── */}
        {tab==="showtimes" && (
          <div className="fade">
            {/* Date rail — underbar selection, no pill backgrounds */}
            <div className="hscroll" style={{padding:"0 16px",marginBottom:14,gap:0,display:"flex"}}>
              {dates.map((d,i)=>{
                const on = selectedDate === i;
                return (
                  <button key={i} onClick={()=>setSelectedDate(i)} style={{
                    flexShrink:0,minWidth:52,
                    display:"flex",flexDirection:"column",alignItems:"center",
                    padding:"4px 8px 8px",
                    background:"none",border:"none",cursor:"pointer",
                    fontFamily:"'Outfit',sans-serif",
                    position:"relative",
                  }}>
                    <span style={{
                      fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",
                      color:on?T.goldLight:T.grey,
                      marginBottom:4,transition:"color .18s"
                    }}>{d.short==="Today"?"TODAY":d.day.toUpperCase()}</span>
                    <span style={{
                      fontSize:15,fontWeight:on?800:600,
                      color:on?T.white:T.offwhite,
                      fontVariantNumeric:"tabular-nums",
                      transition:"color .18s"
                    }}>{d.short==="Today"?"Now":d.date}</span>
                    <div aria-hidden="true" style={{
                      position:"absolute",bottom:0,left:"50%",
                      transform:`translateX(-50%) scaleX(${on?1:0})`,
                      transformOrigin:"center",
                      width:24,height:2,borderRadius:1,background:T.gold,
                      transition:"transform .22s ease"
                    }}/>
                  </button>
                );
              })}
            </div>

            {/* Filter row: Movie + Format */}
            <div style={{display:"flex",gap:8,padding:"0 16px",marginBottom:10}}>
              <button onClick={()=>setMovieSheetOpen(true)} style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,padding:"8px 0",borderRadius:10,background:T.navyLight,border:`1px solid ${T.glassBorder}`,cursor:"pointer"}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M8 4v16M16 4v16M2 9h4M18 9h4"/></svg>
                <span style={{fontSize:11,fontWeight:600,color:T.offwhite,fontFamily:"'Outfit',sans-serif"}}>{movieFilter}</span>
                <span style={{fontSize:10,color:T.grey}}>▾</span>
              </button>
            </div>

            {/* Single all-in pricing disclosure for the whole tab — gold accent for visibility */}
            <div style={{display:"flex",alignItems:"center",gap:9,padding:"11px 14px",margin:"0 16px 14px",background:`linear-gradient(135deg,rgba(201,168,76,.10),rgba(201,168,76,.02))`,border:`1px solid ${T.goldBorder}`,borderRadius:10}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.goldLight} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="12" y1="8" x2="12" y2="8"/></svg>
              <div style={{fontSize:11,color:T.offwhite,lineHeight:1.4,fontFamily:"'Outfit',sans-serif",fontWeight:500}}>All prices include <span style={{color:T.goldLight,fontWeight:700}}>${SERVICE_FEE_GLOBAL.toFixed(2)}</span> service fee · <span style={{color:T.goldLight,fontWeight:700}}>No hidden charges</span></div>
            </div>

            {/* Movie showtime cards — full detail matching movie detail screen */}
            <div style={{padding:"0 16px"}}>
              {(movieFilter==="All Movies"?MOVIES:MOVIES.filter(m=>m.title===movieFilter)).map(m=>{
                const thData = SHOWTIMES_DATA[theater.id] || { formats:[{ fmt:"Standard", tagline:"", amenities:"Recliner Seats · Reserved Seating", times:["2:00 PM","5:15 PM","8:30 PM"] }] };
                const dateLabel = dates[selectedDate].short==="Today"?"Today":`${dates[selectedDate].day} ${dates[selectedDate].date}`;

                const handleTheaterTime = (fmt, t) => {
                  const base = FORMAT_PRICING[fmt.fmt] || 16.50;
                  const mat = isMatinee(t);
                  const price = mat ? base - MATINEE_DISCOUNT : base;
                  setMovie(m);
                  setShowtime({ movie:m, theater, time:t, fmt:fmt.fmt, date:dateLabel, basePrice:price, isMatinee:mat, showroom:fmt.showroom, seatsLeft:fmt.seatsLeft });
                  setSeatTimer(null); setSeatData(null); setTicketOrder(null);
                  nav("seat-map");
                };

                return (
                  <div key={m.id} className="glass-card" style={{marginBottom:14,overflow:"hidden"}}>
                    {/* Movie header */}
                    <div style={{padding:"14px 16px 10px",position:"relative"}}>
                      <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${T.gold},rgba(201,168,76,.2))`}} role="presentation"/>
                      <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                        <div onClick={()=>{setMovie(m);nav("movie-detail");}} style={{width:44,height:62,borderRadius:7,overflow:"hidden",flexShrink:0,border:`1px solid ${T.glassBorder}`,cursor:"pointer"}} role="button" tabIndex={0} aria-label={`View details for ${m.title}`}>
                          <MoviePoster movieId={m.id} width={44} height={62} mini title={m.title}/>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:T.white,lineHeight:1.25,marginBottom:3}}>{m.title}</div>
                          <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                            <span style={{fontSize:9,fontWeight:700,color:T.grey,background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:3,padding:"1px 5px"}}>{m.rating}</span>
                            <span style={{fontSize:10,color:T.grey}}>{m.genre}</span>
                            <span style={{fontSize:10,color:T.grey}}>·</span>
                            <span style={{fontSize:10,color:T.grey}}>{m.runtime}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Format sections */}
                    {thData.formats.map((fmt,fi) => {
                      const previewKey=`${m.id}-${theater.id}-${fi}`;
                      return (
                      <div key={fi} style={{borderTop:`1px solid ${T.glassBorder}`,padding:"14px 16px"}}>
                        {/* Format header — serif name, prominent tagline, showroom + amenities on separate lines, right-aligned from-price */}
                        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:12}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700,color:T.goldLight,letterSpacing:".01em",lineHeight:1.1}}>{fmt.fmt}</div>
                            {fmt.tagline && (
                              <div style={{fontSize:9,color:T.goldLight,fontWeight:800,letterSpacing:".14em",textTransform:"uppercase",marginTop:4,lineHeight:1.2}}>{fmt.tagline}</div>
                            )}
                            {SHOWROOMS[fmt.showroom] && (
                              <div style={{fontSize:10,color:T.offwhite,marginTop:6,lineHeight:1.35,fontWeight:500}}>{SHOWROOMS[fmt.showroom].name} · {SHOWROOMS[fmt.showroom].seats} seats</div>
                            )}
                            {fmt.amenities && (
                              <div style={{fontSize:10,color:T.grey,marginTop:2,lineHeight:1.4}}>{fmt.amenities.split(" · ").slice(0,3).join(" · ")}</div>
                            )}
                          </div>
                          <div style={{flexShrink:0,display:"flex",flexDirection:"column",alignItems:"flex-start",gap:1}}>
                            <div style={{fontSize:8,color:T.grey,fontWeight:600,letterSpacing:".08em",textTransform:"uppercase"}}></div>
                            <div style={{display:"flex",alignItems:"center",gap:5,fontVariantNumeric:"tabular-nums"}}>
                              <span style={{fontSize:15,color:T.white,fontWeight:700,lineHeight:1,fontFamily:"'Outfit',sans-serif"}}>${((FORMAT_PRICING[fmt.fmt]||16.50) + SERVICE_FEE_GLOBAL).toFixed(2)}</span>
                              <button onClick={(e)=>{e.stopPropagation();setPriceInfoOpen(priceInfoOpen===previewKey?null:previewKey);}} style={{background:"none",border:`1px solid ${T.greyDim}`,borderRadius:"50%",width:14,height:14,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",padding:0,flexShrink:0}} aria-label="Pricing details">
                                <span style={{fontSize:8,fontWeight:700,color:T.offwhite,lineHeight:1}}>i</span>
                              </button>
                            </div>
                          </div>
                        </div>
                        {priceInfoOpen===previewKey&&(
                          <div style={{background:T.navyMid,border:`1px solid ${T.goldBorder}`,borderRadius:10,padding:"10px 14px",marginBottom:10}}>
                            <div style={{fontSize:11,fontWeight:700,color:T.white,marginBottom:4}}>Pricing Info</div>
                            <div style={{fontSize:10,color:T.grey,lineHeight:1.5}}>Displayed price is the standard <span style={{color:T.offwhite,fontWeight:600}}>adult ticket</span> rate. Child and senior discounts vary.</div>
                            <div style={{display:"flex",justifyContent:"flex-end",marginTop:6}}><button onClick={(e)=>{e.stopPropagation();setPriceInfoOpen(null);}} style={{background:"none",border:"none",color:T.gold,fontSize:10,fontWeight:600,cursor:"pointer",padding:0,fontFamily:"'Outfit',sans-serif"}}>Got it</button></div>
                          </div>
                        )}
                        {/* Matinee note — shown once per format if any matinee times */}
                        {fmt.times.some(t=>isMatinee(t)) && (
                          <div style={{fontSize:10,color:T.green,marginBottom:9,display:"flex",alignItems:"center",gap:6,fontFamily:"'Outfit',sans-serif"}}>
                            <span aria-hidden="true" style={{fontSize:11,lineHeight:1}}>✦</span>
                            <span>Matinee pricing before noon — save ${MATINEE_DISCOUNT.toFixed(2)}</span>
                          </div>
                        )}
                        {/* Time pills — time only; matinee glyph inline */}
                        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                          {fmt.times.map(t=>{
                            const base = FORMAT_PRICING[fmt.fmt] || 16.50;
                            const mat = isMatinee(t);
                            const allIn = (mat ? base - MATINEE_DISCOUNT : base) + SERVICE_FEE_GLOBAL;
                            const left = fmt.seatsLeft?.[t];
                            return (
                              <button key={t} className="pill-time press" onClick={()=>handleTheaterTime(fmt,t)}
                                style={{fontSize:13,padding:"9px 14px",display:"flex",alignItems:"center",justifyContent:"center",gap:5,fontVariantNumeric:"tabular-nums",fontWeight:600}}
                                aria-label={`${t} showtime, $${allIn.toFixed(2)}${mat?" matinee pricing":""}${left!=null?`, ${left} seats left`:""}`}>
                                <span>{t}</span>
                                {mat && <span aria-hidden="true" style={{color:T.green,fontSize:11,lineHeight:1}}>✦</span>}
                              </button>
                            );
                          })}
                        </div>
                        {/* Check Seats */}
                        <button className={`check-seats${openPreview===previewKey?" open":""}`} onClick={()=>setOpenPreview(openPreview===previewKey?null:previewKey)} aria-expanded={openPreview===previewKey} aria-label="Toggle seat availability preview" style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:12,background:openPreview===previewKey?T.goldDim:"rgba(255,255,255,.03)",border:`1px solid ${openPreview===previewKey?T.goldBorder:"rgba(138,148,168,.55)"}`,borderRadius:8,padding:"6px 12px",cursor:"pointer"}}>
                          <svg className="check-seats__ico" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={openPreview===previewKey?T.goldLight:T.grey} strokeWidth="2" style={{transition:"stroke .2s ease"}}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          <span className="check-seats__lbl" style={{fontSize:11,color:openPreview===previewKey?T.goldLight:T.grey,fontWeight:600,fontFamily:"'Outfit',sans-serif",transition:"color .2s ease"}}>Check Seats</span>
                          <span className="check-seats__caret" style={{fontSize:9,color:openPreview===previewKey?T.goldLight:T.grey,transform:openPreview===previewKey?"rotate(180deg)":"none",display:"inline-block",transition:"transform .2s,color .2s ease"}}>▾</span>
                        </button>
                        {openPreview===previewKey&&<InlineSeatPreview showtimeKey={`${theater.id}-${m.id}-${fmt.fmt}`} times={fmt.times} seatsLeft={fmt.seatsLeft} showroom={fmt.showroom}/>}
                      </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── FEATURES ── */}
        {tab==="features" && (
          <div className="fade" style={{padding:"16px"}}>
            {/* Featured Movies carousel */}
            <div style={{marginBottom:22}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                <div style={{width:3,height:16,borderRadius:2,background:T.gold}}/>
                <span style={{fontSize:11,fontWeight:700,color:T.offwhite,letterSpacing:".08em",textTransform:"uppercase"}}>Featured Movies</span>
              </div>
              <div className="hscroll" style={{gap:12}}>
                {MOVIES.slice(0,5).map(m=>(
                  <div key={m.id} className="press card-lift" onClick={()=>{setMovie(m);nav("movie-detail");}} style={{flexShrink:0,width:130,borderRadius:12,overflow:"hidden",border:`1px solid ${T.glassBorder}`,background:T.navyCard}}>
                    <div style={{height:110,position:"relative",overflow:"hidden"}}>
                      <MoviePoster movieId={m.id} width={130} height={110} mini title={m.title}/>
                      {/* Play overlay */}
                      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.25)"}}>
                        <div style={{width:32,height:32,borderRadius:"50%",border:`2px solid ${T.gold}`,background:"rgba(4,8,26,.5)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                          <div style={{width:0,height:0,borderTop:"7px solid transparent",borderBottom:"7px solid transparent",borderLeft:`11px solid ${T.gold}`,marginLeft:2}}/>
                        </div>
                      </div>
                      {/* Get Tickets badge */}
                      <div style={{position:"absolute",bottom:0,left:0,right:0,background:"linear-gradient(to top,rgba(4,8,15,.85) 0%,rgba(4,8,15,.4) 60%,transparent 100%)",padding:"18px 8px 6px",textAlign:"center"}}>
                        <span style={{fontSize:9,fontWeight:800,color:T.gold,letterSpacing:".06em"}}>GET TICKETS</span>
                      </div>
                    </div>
                    <div style={{padding:"8px 10px 10px"}}>
                      <div style={{fontSize:11,fontWeight:600,color:T.white,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.title}</div>
                      <div style={{fontSize:10,color:T.grey,marginTop:2}}>{m.rating} · {m.runtime}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Theater Amenities grid */}
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                <div style={{width:3,height:16,borderRadius:2,background:T.gold}}/>
                <span style={{fontSize:11,fontWeight:700,color:T.offwhite,letterSpacing:".08em",textTransform:"uppercase"}}>Theater Amenities</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {AMENITY_GRID.slice(0, theater.amenities.length + 2 > 8 ? 8 : theater.amenities.length + 2).map(({icon,label})=>(
                  <div key={label} style={{background:T.navyLight,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:"14px 12px",display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:38,height:38,borderRadius:10,background:T.goldDim,border:`1px solid ${T.goldBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{icon}</div>
                    <span style={{fontSize:12,fontWeight:600,color:T.white,lineHeight:1.3}}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── INFO ── */}
        {tab==="info" && (
          <div className="fade" style={{padding:"16px"}}>
            <div className="glass-card" style={{padding:16,marginBottom:14}}>
              {[
                ["Address", theater.address+", "+theater.city],
                ["Phone", theater.phone],
                ["Screens", `${theater.screens} auditoriums`],
                ["Hours", "Mon–Sun  10:00 AM – 12:00 AM"],
                ["Parking", "Free validated parking"],
                ["Accessibility", "Wheelchair access · Audio description · Open captions"],
              ].map(([k,v])=>(
                <div key={k} style={{padding:"10px 0",borderBottom:`1px solid ${T.glassBorder}`}}>
                  <div style={{fontSize:10,color:T.grey,marginBottom:3,letterSpacing:".06em",textTransform:"uppercase"}}>{k}</div>
                  <div style={{fontSize:13,color:T.white,lineHeight:1.5}}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:10}}>
              <button className="btn-outline" style={{flex:1,padding:"11px 0",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                Call
              </button>
              <button className="btn-outline" style={{flex:1,padding:"11px 0",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                Directions
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// MY REALVIEW
// ══════════════════════════════════════════════════════
const MyReelViewScreen = ({ nav, setShowtime, setSeatData, purchasedConcessions, purchasedTickets, rewardPoints, rewardHistory, setConcessionPreselect, setAutoInvite, tab, setTab }) => {
  const [expandedConcession,setExpandedConcession] = useState(null); // tickets tab expand
  const [expandedConcTab,setExpandedConcTab] = useState(null); // concessions tab expand (only one at a time)
  const pts = rewardPoints || 12480;
  const ptsValue = Math.floor(pts / 5000) * 5;
  const nextTier = (Math.floor(pts / 5000) + 1) * 5000;
  const ptsToNext = nextTier - pts;
  const pctProgress = ((pts % 5000) / 5000 * 100).toFixed(1);
  return (
    <div className="screen" style={{paddingBottom:80}}>
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:50,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`}}>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:18,color:T.white,margin:0}}>My ReelView</h1>
        <button style={{background:"none",border:"none",cursor:"pointer",color:T.grey,fontSize:13,fontFamily:"'Outfit',sans-serif",fontWeight:600}}>Settings</button>
      </div>
      {/* Profile */}
      <div style={{padding:"18px 16px 14px",background:`linear-gradient(180deg,${T.navyLight} 0%,${T.navy} 100%)`,marginBottom:2,display:"flex",gap:14,alignItems:"center"}}>
        <div style={{width:58,height:58,borderRadius:"50%",background:`linear-gradient(135deg,${T.goldDim},${T.navyLight})`,border:`2px solid ${T.goldBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>🎬</div>
        <div>
          <div style={{fontSize:17,fontWeight:700,color:T.white}}>Alex Rivera</div>
          <div style={{fontSize:12,color:T.grey}}>Member since Jan 2023</div>
          <span className="tag" style={{marginTop:5,display:"inline-block"}}>⭐ Gold Member</span>
        </div>
      </div>
      {/* Equally spaced tabs */}
      <div style={{display:"flex",borderBottom:`1px solid ${T.glassBorder}`,marginBottom:14}}>
        {[["rewards","Rewards"],["tickets","Tickets"],["concessions","Concessions"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} className={tab===id?"tab-on":"tab-off"}
            style={{flex:1,background:"none",border:"none",cursor:"pointer",padding:"12px 0",fontSize:13,fontWeight:600,fontFamily:"'Outfit',sans-serif",textAlign:"center"}}>
            {label}
          </button>
        ))}
      </div>

      <div style={{padding:"0 16px"}}>
        {tab==="rewards"&&(
          <div className="fade">
            {/* Points balance card */}
            <div style={{background:`linear-gradient(135deg,#1a1200,${T.navyLight})`,border:`1px solid ${T.goldBorder}`,borderRadius:18,padding:20,marginBottom:18,position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",top:-28,right:-28,width:110,height:110,borderRadius:"50%",background:T.goldDim}}/>
              <div style={{fontSize:10,color:T.goldLight,letterSpacing:".1em",textTransform:"uppercase",marginBottom:6}}>Reward Points</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:44,fontWeight:900,color:T.gold,marginBottom:3}}>{pts.toLocaleString()}</div>
              <div style={{fontSize:12,color:T.grey,marginBottom:4}}>= <span style={{color:T.white,fontWeight:700}}>${ptsValue.toFixed(2)}</span> available reward value</div>
              <div style={{fontSize:10,color:T.grey,marginBottom:12}}>{ptsToNext.toLocaleString()} pts to next ${(ptsValue+5).toFixed(2)} reward</div>
              <div style={{height:6,borderRadius:3,background:T.navyMid,overflow:"hidden"}}>
                <div style={{width:`${pctProgress}%`,height:"100%",borderRadius:3,background:`linear-gradient(90deg,${T.gold},${T.goldLight})`}}/>
              </div>
              <div style={{fontSize:9,color:T.grey,marginTop:6}}>5,000 pts = $5</div>
            </div>
            <div style={{fontSize:11,fontWeight:700,color:T.grey,letterSpacing:".08em",textTransform:"uppercase",marginBottom:10}}>Points History</div>
            {[...(rewardHistory||[]),{l:"Movie Ticket — The Bride!",p:"+250",d:"Mar 7"},{l:"Concessions Purchase",p:"+85",d:"Mar 7"},{l:"Bonus: Double Points Weekend",p:"+400",d:"Feb 28"},{l:"Reward Applied — $5.00 off",p:"−5,000",d:"Feb 20"},{l:"Movie Ticket — Wuthering Heights",p:"+250",d:"Feb 22"}].map((item,i)=>(
              <div key={i} className="glass-card" style={{padding:12,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div><div style={{fontSize:13,color:T.white,fontWeight:500}}>{item.l}</div><div style={{fontSize:10,color:T.grey,marginTop:2}}>{item.d}</div></div>
                <div style={{fontSize:14,fontWeight:700,color:item.p.startsWith("+")?T.gold:T.red}}>{item.p}</div>
              </div>
            ))}
          </div>
        )}
        {tab==="tickets"&&(()=>{
          const TICKET_DATA = [
              {title:"Peaky Blinders: The Immortal Man",date:"Mar 15, 2026",time:"7:30 PM",theater:"ReelView Grand Cineplex",theaterShort:"Grand Cineplex",seats:["E5","E6"],fmt:"IMAX",showroom:"IMAX",movie:MOVIES[3],theaterObj:THEATERS[0],upcoming:true,
                concession:{items:["Large Popcorn","Fountain Drink (L)"],pickup:"15 min before",code:"C-XK92M1"}},
              {title:"Hoppers",date:"Mar 22, 2026",time:"2:00 PM",theater:"ReelView Marina Bay",theaterShort:"Marina Bay",seats:["F4","F5","F6"],fmt:"Standard",showroom:"Standard-L",movie:MOVIES[1],theaterObj:THEATERS[2],upcoming:true},
              {title:"Wuthering Heights",date:"Feb 22, 2026",time:"8:00 PM",theater:"ReelView Westside 10",theaterShort:"Westside 10",seats:["G7","G8"],fmt:"Dolby Atmos",showroom:"Dolby Atmos",movie:MOVIES[0],theaterObj:THEATERS[1],upcoming:false},
          ];
          // Merge purchased tickets (this session) with hardcoded examples
          const allTickets = [...(purchasedTickets||[]), ...TICKET_DATA];
          // Link ALL matching concession orders to each ticket by movie title
          const tickets = allTickets.map((t)=>{
            const orders = [];
            // Include hardcoded concession if present
            if(t.concession) orders.push({items:t.concession.items,pickup:t.concession.pickup,code:t.concession.code});
            // Include all purchased concession orders matching this movie
            (purchasedConcessions||[]).filter(c => c.pickupMovie?.title === t.title).forEach(c => {
              orders.push({items:c.items.map(x=>x.name),pickup:c.pickupTime,code:c.code});
            });
            return {...t, concessions:orders};
          });
          return (
          <div className="fade">
            {tickets.map((t,i)=>{
              const tRoom = SHOWROOMS[t.showroom] || SHOWROOMS["Standard-M"];
              const isExpanded = expandedConcession === i;
              return (
              <div key={i} style={{marginBottom:10}}>
                <div className="glass-card" style={{padding:14,borderLeft:`3px solid ${t.upcoming?T.gold:T.greyDim}`,borderRadius:t.concessions?.length>0?"14px 14px 0 0":undefined,borderBottom:t.concessions?.length>0?"none":undefined}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:14,fontWeight:700,color:T.white,flex:1,paddingRight:8}}>{t.title}</div>
                    <span className="tag" style={{background:t.upcoming?T.goldDim:"rgba(255,255,255,.04)",color:t.upcoming?T.goldLight:T.grey,flexShrink:0}}>{t.upcoming?"Upcoming":"Past"}</span>
                  </div>
                  <div style={{fontSize:11,color:T.grey,marginBottom:1}}>{t.theater} · Screen {tRoom.screenNum}</div>
                  <div style={{fontSize:11,color:T.grey,marginBottom:t.upcoming?12:0}}>{t.date} · {t.time} · Seats: {t.seats.join(", ")}</div>
                  {t.upcoming&&(
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      <div style={{display:"flex",gap:8}}>
                        <button className="btn-outline" style={{flex:1,padding:"9px 6px",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",gap:5}}
                          onClick={()=>{
                            setShowtime({movie:t.movie,theater:t.theaterObj,fmt:t.fmt,time:t.time,date:t.date,basePrice:FORMAT_PRICING[t.fmt]||16.50,showroom:t.showroom});
                            setSeatData(t.seats);
                            nav("ticket-confirm");
                          }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3l-4 4-4-4"/></svg>
                          View Ticket
                        </button>
                        <button className="btn-outline" style={{flex:1,padding:"9px 6px",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",gap:5}} onClick={()=>{
                          if(t.concessions && t.concessions.length>0){setExpandedConcession(isExpanded?null:i);}else{setConcessionPreselect(t.title);nav("concessions");}
                        }}>
                          🍿 {t.concessions && t.concessions.length>0?"View Order":"Add Concessions"}
                        </button>
                      </div>
                      <button className="btn-gold" style={{width:"100%",padding:"10px 0",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:7}}
                        onClick={()=>{
                          setShowtime({movie:t.movie,theater:t.theaterObj,fmt:t.fmt,time:t.time,date:t.date,basePrice:FORMAT_PRICING[t.fmt]||16.50,showroom:t.showroom});
                          setSeatData(t.seats);
                          setAutoInvite(true);
                          nav("ticket-confirm");
                        }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
                        Tell a Friend
                      </button>
                    </div>
                  )}
                </div>
                {/* Linked concessions — seamless attached row */}
                {t.concessions?.length>0 && (
                  <div style={{borderLeft:`3px solid ${t.upcoming?T.gold:T.greyDim}`,borderRight:`1px solid rgba(255,160,50,.15)`,background:`linear-gradient(135deg,rgba(255,160,50,.06),rgba(201,168,76,.03))`,padding:"0 14px"}}>
                    <div style={{borderTop:`1px solid rgba(255,255,255,.06)`,width:"100%"}}/>
                  </div>
                )}
                {t.concessions?.length>0 && (
                  <button onClick={()=>setExpandedConcession(isExpanded?null:i)}
                    style={{width:"100%",background:`linear-gradient(135deg,rgba(255,160,50,.06),rgba(201,168,76,.03))`,borderLeft:`3px solid ${t.upcoming?T.gold:T.greyDim}`,borderRight:`1px solid rgba(255,160,50,.15)`,borderBottom:isExpanded?"none":`1px solid rgba(255,160,50,.15)`,borderTop:"none",borderRadius:isExpanded?"0":"0 0 14px 14px",padding:"8px 14px",cursor:"pointer",fontFamily:"'Outfit',sans-serif",textAlign:"left",display:"flex",alignItems:"center",gap:8,marginTop:0}}>
                    <span style={{fontSize:13}}>🍿</span>
                    <div style={{flex:1}}>
                      <div style={{fontSize:10,color:T.white,fontWeight:600}}>Concession{t.concessions.length>1?"s":""} Order{t.concessions.length>1?"s":""}</div>
                      <div style={{fontSize:9,color:T.grey}}>{t.concessions.length} order{t.concessions.length>1?"s":""}</div>
                    </div>
                    <span style={{fontSize:10,color:T.grey,transform:isExpanded?"rotate(180deg)":"none",transition:"transform .2s"}}>▼</span>
                  </button>
                )}
                {t.concessions?.length>0 && isExpanded && (
                  <div style={{background:"rgba(255,160,50,.04)",borderLeft:`3px solid ${t.upcoming?T.gold:T.greyDim}`,borderRight:`1px solid rgba(255,160,50,.1)`,borderBottom:`1px solid rgba(255,160,50,.1)`,borderRadius:"0 0 14px 14px",padding:"10px 14px"}}>
                    {t.concessions.map((order,oi)=>(
                      <div key={oi}>
                        {oi>0 && <div style={{borderTop:`1px solid rgba(255,255,255,.06)`,margin:"10px 0"}}/>}
                        <div style={{fontSize:9,color:T.grey,letterSpacing:".08em",textTransform:"uppercase",marginBottom:6}}>ORDER {order.code}</div>
                        {order.items.map((item,j)=>(
                          <div key={j} style={{fontSize:11,color:T.white,padding:"3px 0"}}>{item}</div>
                        ))}
                        <div style={{fontSize:9,color:T.grey,marginTop:4}}>Pickup {order.pickup}</div>
                        <div style={{marginTop:8,textAlign:"center"}}>
                          <div style={{display:"flex",gap:1.5,justifyContent:"center",marginBottom:4}}>
                            {Array.from({length:28},(_,k)=><div key={k} style={{width:k%5===0?3:k%3===0?2:1.5,height:30,background:T.white,borderRadius:1,opacity:.3+((k*17)%7)*.1}}/>)}
                          </div>
                          <div style={{fontSize:8,color:T.grey,letterSpacing:".14em"}}>{order.code}</div>
                        </div>
                      </div>
                    ))}
                    <button className="btn-outline" style={{width:"100%",padding:"9px 0",fontSize:11,marginTop:10,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",gap:6}} onClick={()=>{setConcessionPreselect(t.title);nav("concessions");}}>
                      🍿 Order More
                    </button>
                  </div>
                )}
              </div>
            );})}
          </div>
        );})()}
        {tab==="concessions"&&(()=>{
          // Collect all concession orders: session purchases + hardcoded example
          const exampleOrder = {title:"Peaky Blinders: The Immortal Man",theater:"ReelView Grand",pickup:"15 min before",code:"C-XK92M1",date:"Mar 15, 2026",time:"7:30 PM",
            items:[{name:"Large Popcorn",id:1,emoji:"🍿",qty:1,price:9.50},{name:"Fountain Drink (L)",id:7,emoji:"🥤",qty:1,price:5.50}],total:15.00};
          const orders = [];
          // Add all purchased concession orders from this session
          (purchasedConcessions||[]).forEach(c => orders.push({...c, isSession:true}));
          orders.push(exampleOrder);
          return (
          <div className="fade">
            {orders.map((order,oi)=>{
              const isOpen = expandedConcTab === oi;
              const orderTitle = order.isSession?(order.pickupMovie?.title||"Concession Order"):order.title;
              const orderPickup = order.isSession?order.pickupTime:order.pickup;
              const orderTheater = order.isSession?(order.pickupMovie?.theater||"ReelView"):order.theater;
              const orderDate = order.isSession?(order.pickupMovie?.date||"Today"):order.date;
              const orderTime = order.isSession?(order.pickupMovie?.time||""):order.time;
              const orderTotal = order.isSession?order.totalAmt:order.total;
              const itemCount = (order.items||[]).reduce((s,it)=>s+(it.qty||1),0);
              return (
              <div key={oi} style={{marginBottom:12}}>
                {/* Collapsed header — always visible */}
                <button onClick={()=>setExpandedConcTab(isOpen?null:oi)}
                  className="glass-card" style={{width:"100%",padding:14,borderLeft:`3px solid ${T.gold}`,borderRadius:isOpen?"14px 14px 0 0":"14px",borderBottom:isOpen?"none":undefined,cursor:"pointer",fontFamily:"'Outfit',sans-serif",textAlign:"left",display:"block"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:14,fontWeight:700,color:T.white,flex:1,paddingRight:8}}>{orderTitle}</div>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                      <span className="tag">Upcoming</span>
                      <span style={{fontSize:10,color:T.grey,transform:isOpen?"rotate(180deg)":"none",transition:"transform .2s"}}>▼</span>
                    </div>
                  </div>
                  <div style={{fontSize:11,color:T.grey,marginBottom:2}}>{orderDate}{orderTime?" · "+orderTime:""} · {orderTheater}</div>
                  <div style={{fontSize:11,color:T.grey,marginBottom:2}}>Pickup: {orderPickup}</div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginTop:4}}>
                    <span style={{fontSize:10,color:T.grey}}>{itemCount} item{itemCount!==1?"s":""}</span>
                    <span style={{fontSize:12,fontWeight:700,color:T.gold}}>${orderTotal.toFixed(2)}</span>
                  </div>
                </button>
                {/* Expanded details — items + barcode */}
                {isOpen && (
                  <div className="glass-card" style={{padding:"0 14px 14px",borderLeft:`3px solid ${T.gold}`,borderRadius:"0 0 14px 14px",borderTop:"none",marginTop:0}}>
                    <div style={{borderTop:`1px solid rgba(255,255,255,.06)`,paddingTop:10,marginBottom:6}}>
                      <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".08em",textTransform:"uppercase",marginBottom:8}}>ORDER {order.code}</div>
                      {(order.items||[]).map((item,j)=>(
                        <div key={j} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:j<(order.items||[]).length-1?`1px solid rgba(255,255,255,.04)`:"none"}}>
                          <div style={{width:28,height:28,borderRadius:6,overflow:"hidden",flexShrink:0,background:T.navyLight}}>
                            {CONCESSION_IMAGES[item.id] ? <img src={CONCESSION_IMAGES[item.id]} alt={item.name} style={{width:"100%",height:"100%",objectFit:"cover"}} loading="lazy"/> : <span style={{fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",width:"100%",height:"100%"}}>{item.emoji}</span>}
                          </div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:11,color:T.white,fontWeight:500}}>{item.name}</div>
                            <div style={{fontSize:9,color:T.grey}}>Qty: {item.qty}</div>
                          </div>
                          <span style={{fontSize:11,color:T.white,fontWeight:600}}>${(item.price*item.qty).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{marginTop:10,textAlign:"center"}}>
                      <div style={{display:"flex",gap:1.5,justifyContent:"center",marginBottom:4}}>
                        {Array.from({length:28},(_,k)=><div key={k} style={{width:k%5===0?3:k%3===0?2:1.5,height:30,background:T.white,borderRadius:1,opacity:.3+((k*17)%7)*.1}}/>)}
                      </div>
                      <div style={{fontSize:8,color:T.grey,letterSpacing:".14em"}}>{order.code}</div>
                      <div style={{fontSize:8,color:T.grey,marginTop:2}}>Show this barcode at the counter</div>
                    </div>
                  </div>
                )}
              </div>
            );})}
            <button className="btn-outline" style={{width:"100%",padding:12,fontSize:12,marginTop:4}} onClick={()=>nav("concessions")}>Order More Concessions</button>
          </div>
        );})()}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// CONCESSIONS
// ══════════════════════════════════════════════════════
const ConcessionsScreen = ({ nav, setConcessionOrder, existingOrder, purchasedTickets, purchasedConcessions, concessionPreselect, setConcessionPreselect }) => {
  const [tab,setTab] = useState("All");
  const [favIds,setFavIds] = useState([1,7]);

  // Build pickup movies from hardcoded defaults + any purchased tickets
  const DEFAULT_PICKUP = [
    {title:"Peaky Blinders: The Immortal Man",time:"7:30 PM",date:"Mar 15, 2026",theater:"ReelView Grand"},
    {title:"Hoppers",time:"2:00 PM",date:"Mar 22, 2026",theater:"ReelView Marina Bay"},
  ];
  const sessionPickup = (purchasedTickets||[]).map(t=>({title:t.title,time:t.time,date:t.date||"Today",theater:t.theater||t.theaterObj?.name||"ReelView"}));
  // Merge: session tickets first, then defaults (skip duplicates by title)
  const seenTitles = new Set();
  const PICKUP_MOVIES = [];
  [...sessionPickup,...DEFAULT_PICKUP].forEach(m=>{if(!seenTitles.has(m.title)){seenTitles.add(m.title);PICKUP_MOVIES.push(m);}});
  const PICKUP_SLOTS_INIT = ["At showtime","15 min before","30 min before"];

  // Rehydrate cart from a saved in-progress order (e.g. when the user backs out of
  // checkout and returns). Each saved item carries its original cartKey ("id" or
  // "id:size"), so we can rebuild the cart map directly. Each size is its own
  // independent cart line, so users can have M + L popcorn at the same time.
  const initialCart = {};
  const initialViewedSizes = {};
  if (existingOrder?.items) {
    existingOrder.items.forEach(it => {
      if (!it?.cartKey) return;
      initialCart[it.cartKey] = it.qty;
      const [idStr, sz] = it.cartKey.split(":");
      // Remember the last size we saw for this item so we can initially surface it.
      if (sz) initialViewedSizes[+idStr] = sz;
    });
  }
  const [cart,setCart] = useState(initialCart);
  // Per-item "currently viewed" size for sized concessions. Pills switch this
  // view only — tapping a different pill never transfers qty. Each size has
  // its own cart line (`id:S`, `id:M`, `id:L`) and its own Add/stepper state
  // independently. Defaults to "M" if nothing's been seen yet.
  const [viewedSize,setViewedSize] = useState(initialViewedSizes);
  // Suppress the cart footer's slide-up animation when we restored a saved cart —
  // otherwise the footer replays its entrance every time the user backs out of checkout,
  // causing a visible layout shift that looks janky.
  const cartPreloadedRef = useRef(Object.keys(initialCart).length > 0);

  // Pre-select movie: saved order > "Add Concessions" preselect > default 0
  const savedMovieIdx = existingOrder?.pickupMovie?.title
    ? PICKUP_MOVIES.findIndex(m=>m.title===existingOrder.pickupMovie.title)
    : -1;
  const preselIdx = concessionPreselect ? PICKUP_MOVIES.findIndex(m=>m.title===concessionPreselect) : -1;
  const [pickupMovie,setPickupMovie] = useState(
    savedMovieIdx>=0 ? savedMovieIdx : (preselIdx>=0 ? preselIdx : 0)
  );

  // Pre-select pickup time from saved order, defaulting to 0
  const savedTimeIdx = existingOrder?.pickupTime
    ? PICKUP_SLOTS_INIT.indexOf(existingOrder.pickupTime)
    : -1;
  const [pickupTime,setPickupTime] = useState(savedTimeIdx>=0 ? savedTimeIdx : 0);
  // Clear the preselect after it's been consumed
  // eslint-disable-next-line
  useEffect(()=>{if(concessionPreselect){setConcessionPreselect(null);}},[]); // run once on mount
  const PICKUP_SLOTS = PICKUP_SLOTS_INIT;
  const RECENT_IDS = [1,7];

  const cats = ["All","Snacks","Drinks","Recent","Favorites"];
  const toggleFav = id => setFavIds(f => f.includes(id) ? f.filter(x=>x!==id) : [...f,id]);

  const filtered = tab==="Favorites"
    ? CONCESSIONS.filter(c=>favIds.includes(c.id))
    : tab==="Recent"
      ? CONCESSIONS.filter(c=>RECENT_IDS.includes(c.id))
      : tab==="All" ? CONCESSIONS : CONCESSIONS.filter(c=>c.cat===tab);

  // Cart helpers. Flat-priced items use key "id"; sized items use key "id:S|M|L"
  // so multiple sizes of the same item (M + L popcorn) can coexist as separate lines.
  const totalAmt = Object.entries(cart).reduce((s,[k,q])=>s+getCartItemPrice(k)*q,0);
  const totalItems = Object.values(cart).reduce((a,b)=>a+b,0);
  // Flat-item helpers
  const addFlat = (item) => setCart(c=>({...c,[item.id]:(c[item.id]||0)+1}));
  const remFlat = (item) => setCart(c=>{const n={...c};if(n[item.id]>1)n[item.id]--;else delete n[item.id];return n;});
  const flatQty = (item) => cart[item.id] || 0;
  // Sized-item helpers — each size is its own cart line
  const sizeQty = (itemId, sz) => cart[`${itemId}:${sz}`] || 0;
  const addSize = (itemId, sz) => setCart(c=>{const k=`${itemId}:${sz}`;return{...c,[k]:(c[k]||0)+1};});
  const remSize = (itemId, sz) => setCart(c=>{const k=`${itemId}:${sz}`;const n={...c};if(n[k]>1)n[k]--;else delete n[k];return n;});

  // Bottom-sheet picker for movie + pickup time
  const [pickerOpen, setPickerOpen] = useState(false);
  // Cart review sheet — tapping the cart footer opens this sheet first so the
  // user can see everything, edit quantities, and remove items before proceeding.
  const [cartOpen, setCartOpen] = useState(false);
  // Auto-close the cart sheet if the user empties it.
  useEffect(()=>{ if(cartOpen && totalItems===0) setCartOpen(false); /* eslint-disable-next-line */ },[totalItems]);

  // Build a flat list of cart line items for the review sheet — each entry
  // carries the concession row, the qty, the resolved price/name, and the
  // original cartKey so we can wire up the +/− buttons to the right helpers.
  const cartLines = Object.entries(cart).map(([key,qty])=>{
    const [idStr,sz] = key.split(":");
    const it = CONCESSIONS.find(c=>c.id===+idStr);
    if(!it) return null;
    const price = it.sizes ? (it.sizes[sz]||it.sizes.M) : it.price;
    return { key, item:it, size:sz||null, qty, price };
  }).filter(Boolean);

  // Current movie + whether there's already a concession order for this showtime
  // (drives the small gold dot on the context chip).
  const currentMovie = PICKUP_MOVIES[pickupMovie];
  const sessionConcMatch = (purchasedConcessions||[]).find(c => c.pickupMovie?.title === currentMovie?.title);
  const exampleConcMatch = currentMovie?.title === "Peaky Blinders: The Immortal Man" ? { totalItems:2, totalAmt:15.00 } : null;
  const existingForMovie = sessionConcMatch ? { totalItems:sessionConcMatch.totalItems, totalAmt:sessionConcMatch.totalAmt } : exampleConcMatch;


  return (
    <div className="screen" style={{display:"flex",flexDirection:"column",paddingBottom:0,position:"relative"}}>

      {/* ── Bottom sheet: movie + pickup time picker ── */}
      {pickerOpen && (
        <div style={{position:"absolute",inset:0,zIndex:400}} onClick={()=>setPickerOpen(false)}>
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.6)"}}/>
          <div onClick={e=>e.stopPropagation()} style={{position:"absolute",bottom:0,left:0,right:0,maxHeight:"85%",display:"flex",flexDirection:"column",background:T.navyMid,borderRadius:"20px 20px 0 0",border:`1px solid ${T.glassBorder}`,borderBottom:"none",boxShadow:"0 -8px 40px rgba(0,0,0,.5)",overflow:"hidden"}}>
            {/* Grip handle — tap to dismiss */}
            <div style={{flexShrink:0,padding:"14px 20px 6px",textAlign:"center"}}>
              <button onClick={()=>setPickerOpen(false)} aria-label="Close" style={{display:"block",width:48,height:16,padding:"6px",margin:"0 auto 6px",background:"none",border:"none",cursor:"pointer"}}>
                <div style={{width:36,height:4,borderRadius:2,background:T.greyDim,margin:"0 auto"}}/>
              </button>
              <div style={{fontSize:14,fontWeight:700,color:T.white,fontFamily:"'Playfair Display',serif"}}>Ordering For</div>
              <div style={{fontSize:10,color:T.grey,marginTop:3}}>Pick a movie and when you want your order ready</div>
            </div>

            {/* Movie list — scrollable */}
            <div style={{flex:1,overflowY:"auto",padding:"14px 20px 8px"}}>
              <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Movie</div>
              {PICKUP_MOVIES.map((m,i)=>{
                const selected = pickupMovie===i;
                const movieObj = MOVIES.find(mv => mv.title === m.title);
                const posterUrl = movieObj ? POSTER_IMAGES[movieObj.id] : null;
                return (
                  <button key={i} onClick={()=>{setPickupMovie(i);setPickupTime(0);}}
                    style={{
                      width:"100%",display:"flex",alignItems:"center",gap:12,
                      padding:"11px 12px",marginBottom:8,borderRadius:12,
                      background:selected?`linear-gradient(135deg,rgba(201,168,76,.10),rgba(201,168,76,.03))`:T.navyCard,
                      border:`1px solid ${selected?T.goldBorder:T.glassBorder}`,
                      cursor:"pointer",textAlign:"left",fontFamily:"'Outfit',sans-serif",
                      transition:"all .18s"
                    }}>
                    {/* Poster — real artwork if available, stylized letter block otherwise */}
                    <div style={{
                      width:42,height:60,borderRadius:6,flexShrink:0,overflow:"hidden",
                      background:posterUrl?T.navyLight:`linear-gradient(145deg,${T.goldDim},${T.navyLight})`,
                      border:`1px solid ${selected?T.goldBorder:T.glassBorder}`,
                      boxShadow:selected?"0 2px 10px rgba(201,168,76,.18)":"0 1px 4px rgba(0,0,0,.35)",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:700,
                      color:selected?T.goldLight:T.gold
                    }}>
                      {posterUrl
                        ? <img src={posterUrl} alt={m.title} loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                        : (m.title?.charAt(0) || "?")}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:700,color:selected?T.goldLight:T.white,lineHeight:1.3}}>{m.title}</div>
                      <div style={{fontSize:10,color:T.grey,marginTop:3}}>{m.time} · {m.theater}</div>
                    </div>
                    {selected && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Pickup time — segmented control */}
            <div style={{flexShrink:0,padding:"6px 20px 16px"}}>
              <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Pickup Time</div>
              <div style={{display:"flex",background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:10,padding:3,gap:3}}>
                {PICKUP_SLOTS.map((s,i)=>(
                  <button key={s} onClick={()=>setPickupTime(i)}
                    style={{
                      flex:1,padding:"9px 4px",borderRadius:8,
                      background:pickupTime===i?T.goldDim:"transparent",
                      border:`1px solid ${pickupTime===i?T.goldBorder:"transparent"}`,
                      color:pickupTime===i?T.goldLight:T.grey,
                      fontSize:10,fontWeight:600,cursor:"pointer",
                      fontFamily:"'Outfit',sans-serif",transition:"all .18s",
                      whiteSpace:"nowrap"
                    }}>{s}</button>
                ))}
              </div>
            </div>

            {/* Done */}
            <div style={{flexShrink:0,padding:"0 20px 24px"}}>
              <button className="btn-gold" style={{width:"100%",padding:13,fontSize:13,borderRadius:50}} onClick={()=>setPickerOpen(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bottom sheet: cart review ── */}
      {cartOpen && totalItems>0 && (
        <div style={{position:"absolute",inset:0,zIndex:400}} onClick={()=>setCartOpen(false)}>
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.6)"}}/>
          <div onClick={e=>e.stopPropagation()} style={{position:"absolute",bottom:0,left:0,right:0,maxHeight:"85%",display:"flex",flexDirection:"column",background:T.navyMid,borderRadius:"20px 20px 0 0",border:`1px solid ${T.glassBorder}`,borderBottom:"none",boxShadow:"0 -8px 40px rgba(0,0,0,.5)",overflow:"hidden"}}>
            {/* Grip + heading — grip taps to dismiss */}
            <div style={{flexShrink:0,padding:"14px 20px 10px",textAlign:"center"}}>
              <button onClick={()=>setCartOpen(false)} aria-label="Close" style={{display:"block",width:48,height:16,padding:"6px",margin:"0 auto 6px",background:"none",border:"none",cursor:"pointer"}}>
                <div style={{width:36,height:4,borderRadius:2,background:T.greyDim,margin:"0 auto"}}/>
              </button>
              <div style={{fontSize:14,fontWeight:700,color:T.white,fontFamily:"'Playfair Display',serif"}}>Your Cart</div>
            </div>

            {/* Line items — scrollable */}
            <div style={{flex:1,overflowY:"auto",padding:"12px 16px 6px"}}>
              {cartLines.map(line => {
                const {key, item, size, qty, price} = line;
                return (
                  <div key={key} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",marginBottom:8,background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:12}}>
                    {/* Thumb */}
                    <div style={{width:44,height:44,borderRadius:8,overflow:"hidden",flexShrink:0,background:T.navyLight}}>
                      {CONCESSION_IMAGES[item.id]
                        ? <img src={CONCESSION_IMAGES[item.id]} alt={item.name} loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                        : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{item.emoji}</div>}
                    </div>

                    {/* Name + price */}
                    <div style={{flex:1,minWidth:0,fontFamily:"'Outfit',sans-serif"}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                        <span style={{fontSize:12,fontWeight:700,color:T.white,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.name}</span>
                        {size && (
                          <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",minWidth:17,height:15,padding:"0 4px",borderRadius:3,border:`1px solid ${T.goldBorder}`,color:T.gold,fontSize:8,fontWeight:700,letterSpacing:".04em",flexShrink:0}}>{size}</span>
                        )}
                      </div>
                      <div style={{fontSize:10,color:T.grey,fontWeight:500,fontVariantNumeric:"tabular-nums"}}>
                        ${price.toFixed(2)} each · <span style={{color:T.gold,fontWeight:700}}>${(price*qty).toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Stepper — shrinks to "×" on qty 1 so user can clearly remove */}
                    <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                      <button
                        onClick={()=>{ if(size) remSize(item.id,size); else remFlat(item); }}
                        aria-label={qty===1?`Remove ${item.name} from cart`:`Remove one ${item.name}`}
                        style={{
                          width:26,height:26,borderRadius:"50%",
                          background:"transparent",
                          border:`1.5px solid ${qty===1?"rgba(220,80,80,.55)":T.goldBorder}`,
                          color:qty===1?"#e46d6d":T.gold,
                          fontSize:qty===1?12:15,fontWeight:600,cursor:"pointer",
                          display:"flex",alignItems:"center",justifyContent:"center",
                          lineHeight:1,padding:0,
                          fontFamily:"'Outfit',sans-serif"
                        }}>{qty===1?"×":"−"}</button>
                      <span style={{minWidth:14,textAlign:"center",fontSize:13,fontWeight:700,color:T.gold,fontVariantNumeric:"tabular-nums",fontFamily:"'Outfit',sans-serif"}}>{qty}</span>
                      <button
                        onClick={()=>{ if(size) addSize(item.id,size); else addFlat(item); }}
                        aria-label={`Add another ${item.name}`}
                        style={{
                          width:26,height:26,borderRadius:"50%",
                          background:"transparent",border:`1.5px solid ${T.goldBorder}`,
                          color:T.gold,fontSize:15,fontWeight:600,cursor:"pointer",
                          display:"flex",alignItems:"center",justifyContent:"center",
                          lineHeight:1,padding:0,
                          fontFamily:"'Outfit',sans-serif"
                        }}>+</button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Subtotal + Checkout CTA */}
            <div style={{flexShrink:0,padding:"12px 20px 24px",borderTop:`1px solid ${T.glassBorder}`,background:`linear-gradient(180deg,transparent,rgba(0,0,0,.2))`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:12,fontFamily:"'Outfit',sans-serif"}}>
                <span style={{fontSize:11,color:T.grey,letterSpacing:".08em",textTransform:"uppercase",fontWeight:700}}>Subtotal</span>
                <span style={{fontSize:18,fontWeight:700,color:T.white,fontVariantNumeric:"tabular-nums"}}>${totalAmt.toFixed(2)}</span>
              </div>
              <button className="btn-gold" style={{width:"100%",padding:13,fontSize:13,borderRadius:50}}
                onClick={()=>{
                  const cartItems = Object.entries(cart).map(([key,qty])=>{
                    const [idStr,size] = key.split(":");
                    const it = CONCESSIONS.find(c=>c.id===+idStr);
                    if(!it) return null;
                    const price = it.sizes ? (it.sizes[size]||it.sizes.M) : it.price;
                    const name = it.sizes ? `${it.name} (${size})` : it.name;
                    return {...it, name, price, qty, cartKey:key};
                  }).filter(Boolean);
                  setConcessionOrder({
                    items:cartItems,
                    pickupMovie:PICKUP_MOVIES[pickupMovie],
                    pickupTime:PICKUP_SLOTS[pickupTime],
                    totalAmt,totalItems
                  });
                  setCartOpen(false);
                  nav("concessions-checkout");
                }}>
                Checkout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`}}>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:18,color:T.white,margin:0}}>Concessions</h1>
        <span style={{fontSize:10,color:T.grey}}>Pre-order & skip the line</span>
      </div>

      {/* ── Compact context chip — opens picker on tap ── */}
      <button
        onClick={()=>setPickerOpen(true)}
        aria-label="Change movie or pickup time"
        style={{
          flexShrink:0,
          margin:"12px 16px 0",
          padding:"10px 14px",
          background:T.navyCard,
          border:`1px solid ${T.glassBorder}`,
          borderRadius:12,
          cursor:"pointer",
          display:"flex",alignItems:"center",gap:11,
          fontFamily:"'Outfit',sans-serif",
          textAlign:"left"
        }}>
        {/* Small clapperboard glyph */}
        <div style={{
          width:26,height:26,borderRadius:7,flexShrink:0,
          background:`linear-gradient(135deg,${T.goldDim},rgba(201,168,76,.05))`,
          border:`1px solid ${T.goldBorder}`,
          display:"flex",alignItems:"center",justifyContent:"center",
          fontSize:13
        }}>🎬</div>
        <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",gap:2}}>
          <div style={{fontSize:9,color:T.grey,letterSpacing:".1em",textTransform:"uppercase",fontWeight:700}}>Ordering for</div>
          <div style={{fontSize:12,color:T.white,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",lineHeight:1.25}}>
            {currentMovie?.title}
          </div>
          <div style={{fontSize:10,color:T.grey,fontWeight:500,lineHeight:1.2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
            {PICKUP_SLOTS[pickupTime]}
          </div>
        </div>
        <span style={{color:T.grey,fontSize:12,flexShrink:0}}>▾</span>
      </button>

      {/* Existing-order notice — subtle informational strip */}
      {existingForMovie && (
        <div
          role="status"
          style={{
            flexShrink:0,
            margin:"8px 16px 0",
            padding:"9px 12px",
            borderRadius:10,
            background:"rgba(201,168,76,.06)",
            border:`1px solid ${T.goldBorder}`,
            display:"flex",alignItems:"center",gap:9,
            fontFamily:"'Outfit',sans-serif"
          }}>
          {/* Exclamation-in-circle — info, not alarm */}
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div style={{flex:1,minWidth:0,fontSize:10,lineHeight:1.35,color:T.gold,fontWeight:500}}>
            You already placed a concessions order for this showtime.
          </div>
        </div>
      )}

      {/* Category tabs — horizontally scrollable */}
      <div style={{flexShrink:0,overflowX:"auto",WebkitOverflowScrolling:"touch",marginTop:14}}>
        <div style={{display:"flex",padding:"0 16px",borderBottom:`1px solid ${T.glassBorder}`,whiteSpace:"nowrap"}}>
          {cats.map(c=>(
            <button key={c} onClick={()=>setTab(c)} className={tab===c?"tab-on":"tab-off"}
              style={{flex:1,background:"none",border:"none",cursor:"pointer",padding:"10px 0",fontSize:12,fontWeight:600,fontFamily:"'Outfit',sans-serif",textAlign:"center"}}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state for favorites/recent */}
      {filtered.length===0 && (
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:32,marginBottom:8}}>{tab==="Favorites"?"♡":"🕐"}</div>
            <div style={{fontSize:13,color:T.grey}}>{tab==="Favorites"?"Tap the ♡ on any item to add favorites":"No recent purchases yet"}</div>
          </div>
        </div>
      )}

      {/* Item grid */}
      {filtered.length>0 && (
        <div style={{flex:1,overflowY:"auto",padding:"14px 16px",paddingBottom:80}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {filtered.map(item=>{
              const faved = favIds.includes(item.id);
              const isSized = !!item.sizes;
              // For sized items: the pill the user is currently viewing. Default
              // to "M". Each size has its own independent cart line and stepper
              // state — switching views never moves qty between sizes.
              const currentSize = isSized ? (viewedSize[item.id] || "M") : null;
              const qty = isSized ? sizeQty(item.id, currentSize) : flatQty(item);
              const price = isSized ? item.sizes[currentSize] : item.price;
              const stepBtn = {
                width:24,height:24,borderRadius:"50%",
                background:"transparent",border:`1.5px solid ${T.goldBorder}`,
                color:T.gold,fontSize:14,fontWeight:600,cursor:"pointer",
                display:"flex",alignItems:"center",justifyContent:"center",
                lineHeight:1,padding:0,fontFamily:"'Outfit',sans-serif"
              };

              return (
              <div key={item.id} className="glass-card" style={{padding:"14px 12px 12px",position:"relative",display:"flex",flexDirection:"column"}}>
                {/* Favorite star — top-right of card, off the image */}
                <button
                  onClick={()=>toggleFav(item.id)}
                  aria-label={faved?`Remove ${item.name} from favorites`:`Add ${item.name} to favorites`}
                  style={{position:"absolute",top:6,right:8,background:"none",border:"none",cursor:"pointer",fontSize:15,color:faved?T.gold:T.greyDim,transition:"color .2s",padding:4,lineHeight:1,zIndex:2}}
                >{faved?"♥":"♡"}</button>

                {/* Image */}
                <div style={{width:70,height:70,borderRadius:10,overflow:"hidden",margin:"4px auto 10px",background:T.navyLight}}>
                  {CONCESSION_IMAGES[item.id]
                    ? <img src={CONCESSION_IMAGES[item.id]} alt={item.name} style={{width:"100%",height:"100%",objectFit:"cover"}} loading="lazy"/>
                    : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:32}}>{item.emoji}</div>}
                </div>

                {/* Name */}
                <div style={{fontSize:12,fontWeight:600,color:T.white,textAlign:"center",marginBottom:isSized?8:10,lineHeight:1.25,minHeight:15}}>{item.name}</div>

                {/* Size pills — always visible, tap to switch VIEW only (not transfer qty) */}
                {isSized && (
                  <div style={{display:"flex",justifyContent:"center",gap:5,marginBottom:8}}>
                    {Object.keys(item.sizes).map(sz=>{
                      const active = sz === currentSize;
                      const hasQty = sizeQty(item.id,sz) > 0;
                      return (
                        <button
                          key={sz}
                          onClick={()=>setViewedSize(s=>({...s,[item.id]:sz}))}
                          aria-label={`View ${sz}, $${item.sizes[sz].toFixed(2)}${hasQty?`, ${sizeQty(item.id,sz)} in cart`:""}`}
                          aria-pressed={active}
                          style={{
                            position:"relative",
                            width:26,height:22,borderRadius:6,
                            background:active?`linear-gradient(135deg,${T.goldDim},rgba(201,168,76,.08))`:"transparent",
                            border:`1.5px solid ${active?T.gold:T.greyDim}`,
                            color:active?T.goldLight:T.grey,
                            fontSize:10,fontWeight:700,
                            cursor:"pointer",
                            fontFamily:"'Outfit',sans-serif",
                            display:"flex",alignItems:"center",justifyContent:"center",
                            lineHeight:1,padding:0,
                            transition:"all .15s",
                            boxShadow:active?"0 0 0 1px rgba(201,168,76,.25) inset":"none"
                          }}>
                          {sz}
                          {/* Gold dot indicator when this size has items in cart but isn't the active view */}
                          {hasQty && !active && (
                            <span style={{position:"absolute",top:-2,right:-2,width:6,height:6,borderRadius:"50%",background:T.gold,border:`1px solid ${T.navyCard}`}}/>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Price — large, centered, reflects currently-viewed size */}
                <div style={{fontSize:15,fontWeight:700,color:qty>0?T.gold:T.white,textAlign:"center",marginBottom:10,fontVariantNumeric:"tabular-nums",fontFamily:"'Outfit',sans-serif",lineHeight:1}}>
                  ${price.toFixed(2)}
                </div>

                {/* Add button OR stepper — controls the currently-viewed size independently */}
                {qty>0 ? (
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 6px"}}>
                    <button
                      onClick={()=>{ if(isSized) remSize(item.id,currentSize); else remFlat(item); }}
                      aria-label={`Remove one ${item.name}${isSized?` (${currentSize})`:""}`}
                      style={stepBtn}>−</button>
                    <span style={{fontSize:14,fontWeight:700,color:T.gold,fontVariantNumeric:"tabular-nums",fontFamily:"'Outfit',sans-serif"}}>{qty}</span>
                    <button
                      onClick={()=>{ if(isSized) addSize(item.id,currentSize); else addFlat(item); }}
                      aria-label={`Add another ${item.name}${isSized?` (${currentSize})`:""}`}
                      style={stepBtn}>+</button>
                  </div>
                ) : (
                  <button
                    onClick={()=>{ if(isSized) addSize(item.id,currentSize); else addFlat(item); }}
                    aria-label={`Add ${item.name}${isSized?` (${currentSize})`:""}`}
                    style={{
                      width:"100%",padding:"7px 0",borderRadius:50,
                      background:"transparent",
                      border:`1.5px solid ${T.goldBorder}`,
                      color:T.gold,fontSize:12,fontWeight:700,
                      cursor:"pointer",
                      fontFamily:"'Outfit',sans-serif",letterSpacing:".02em",
                      transition:"all .15s"
                    }}>Add</button>
                )}
              </div>
            );})}
          </div>
        </div>
      )}

      {/* Cart footer — absolutely positioned above the bottom nav so mounting it
          doesn't reflow the flex column above. The item grid already reserves
          80px of paddingBottom to clear this. Pure-transform slideUp animation
          stays silky smooth because nothing below it has to move. */}
      {totalItems>0&&(
        <div
          aria-live="polite"
          style={{
            position:"absolute",
            left:0,right:0,bottom:0,
            zIndex:50,
            borderTop:`1px solid ${T.glassBorder}`,
            background:T.navyGlass,
            backdropFilter:"blur(20px)",
            WebkitBackdropFilter:"blur(20px)",
            padding:"10px 16px 12px",
            animation:cartPreloadedRef.current?"none":"slideUp .25s ease",
            willChange:"transform"
          }}>
          <button className="btn-gold" style={{width:"100%",padding:"13px 20px",fontSize:13,display:"flex",justifyContent:"space-between",alignItems:"center"}}
            onClick={()=>setCartOpen(true)}>
            <span style={{display:"flex",alignItems:"center",gap:6}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.navy} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>{totalItems} item{totalItems>1?"s":""}</span>
            <span>Review Cart · ${totalAmt.toFixed(2)}</span>
          </button>
        </div>
      )}
    </div>
  );
};

// CONCESSIONS CHECKOUT
const ConcessionsCheckoutScreen = ({ nav, goBack, order, setRewardPoints, setRewardHistory, rewardPoints, setPurchasedConcessions }) => {
  const [pay, setPay] = useState("apple");
  const [promo, setPromo] = useState("");
  const [promoOK, setPromoOK] = useState(false);
  const [rewardIncrements, setRewardIncrements] = useState(0);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const [email, setEmail] = useState("");

  const items = order?.items || [];
  const sub = order?.totalAmt || 0;
  const rewardBalance = rewardPoints || 12480;
  const rewardValue = Math.floor(rewardBalance / 5000) * 5;
  const maxIncrements = Math.floor(rewardValue / 5);
  const rewardDisc = rewardIncrements * 5;
  const disc = promoOK ? sub * 0.10 : 0;
  const total = Math.max(0, sub - disc - rewardDisc);
  const ptsEarned = rewardIncrements > 0 ? 0 : Math.round(total * 10);

  const PAYMENT_OPTS = [
    {id:"apple", label:"Apple Pay", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill={T.offwhite}><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>},
    {id:"gpay",  label:"Google Pay", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12.24 10.28V14.1h5.05c-.23 1.26-.85 2.32-1.8 3.03l2.75 2.13c1.6-1.48 2.52-3.65 2.52-6.23 0-.57-.05-1.12-.14-1.65h-8.38z" fill="#4285F4"/><path d="M5.43 14.28l-.63.48-2.23 1.73C4.24 19.77 7.84 22 12.24 22c2.97 0 5.46-.98 7.28-2.66l-2.75-2.13c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53l-.47.54z" fill="#34A853"/><path d="M2.57 6.51C1.96 7.72 1.6 9.06 1.6 10.5s.36 2.78.97 3.99l2.86-2.22c-.22-.66-.34-1.36-.34-2.09s.12-1.43.34-2.09L2.57 6.51z" fill="#FBBC05"/><path d="M12.24 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.69 1.33 15.19.25 12.24.25 7.84.25 4.24 2.48 2.57 5.77l2.86 2.22c.87-2.6 3.3-4.49 6.81-4.49z" fill="#EA4335"/></svg>},
    {id:"paypal",label:"PayPal", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M7.02 21.5L7.8 17h-2L9.64 2.5h5.56c2.5 0 4.14 1.58 3.77 4.1-.52 3.57-3.15 5.18-6.07 5.18H11l-1 5.4h-2l-.48 2.82-.5 1.5z" fill="#003087"/><path d="M9.72 16.22L10.68 11h2.22c2.14 0 4.11-1.22 4.5-3.87.28-1.9-.76-3.13-2.6-3.13h-3.6L8.2 17.5h2l-.48 2.72h2z" fill="#009CDE"/></svg>},
    {id:"venmo", label:"Venmo", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="#3D95CE"><path d="M19.78 3.38c.66 1.09.96 2.21.96 3.64 0 4.53-3.87 10.42-7.01 14.55H7.23L4.22 3.88l5.55-.53 1.81 14.49c1.68-2.74 3.76-7.06 3.76-10 0-1.36-.23-2.29-.62-3.05l5.06-1.41z"/></svg>},
    {id:"card",  label:"Credit / Debit Card", icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.offwhite} strokeWidth="1.8"><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/><path d="M5 15h4M14 15h5"/></svg>},
  ];

  const payLabel = PAYMENT_OPTS.find(o=>o.id===pay)?.label || "Apple Pay";

  const handleConfirmPurchase = () => {
    setSummaryOpen(false);
    const ptsEarnedNow = rewardIncrements > 0 ? 0 : Math.round(total * 10);
    if(rewardIncrements>0){
      const ptsSpent = rewardIncrements * 5000;
      setRewardPoints(p=>p - ptsSpent + ptsEarnedNow);
      setRewardHistory(h=>[
        ...(ptsEarnedNow>0?[{l:"Concessions Purchase",p:`+${ptsEarnedNow}`,d:"Today"}]:[]),
        {l:"Reward Redeemed — Concessions",p:`-${ptsSpent.toLocaleString()}`,d:"Today"},
        ...h
      ]);
    } else {
      setRewardPoints(p=>p+ptsEarnedNow);
      setRewardHistory(h=>[{l:"Concessions Purchase",p:`+${ptsEarnedNow}`,d:"Today"},...h]);
    }
    // Save completed concession order to purchasedConcessions array
    setPurchasedConcessions(prev=>[{...order, code:"C-"+Math.random().toString(36).substr(2,6).toUpperCase()},...prev]);
    nav("concessions-confirm");
  };

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0,position:"relative"}}>
      {/* ── "Before You Buy" confirmation sheet ── */}
      {summaryOpen && (
        <div style={{position:"absolute",inset:0,zIndex:400}} onClick={()=>setSummaryOpen(false)}>
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.55)"}}/>
          <div onClick={e=>e.stopPropagation()} style={{position:"absolute",bottom:0,left:0,right:0,background:T.navyMid,borderRadius:"20px 20px 0 0",padding:"20px 20px 32px",border:`1px solid ${T.glassBorder}`,borderBottom:"none",overflow:"hidden",overscrollBehavior:"contain",boxShadow:"0 -8px 40px rgba(0,0,0,.5)"}}>
            <div style={{textAlign:"center",marginBottom:16}}>
              <button onClick={()=>setSummaryOpen(false)} aria-label="Close" style={{display:"block",width:48,height:16,padding:"6px",margin:"0 auto 8px",background:"none",border:"none",cursor:"pointer"}}>
                <div style={{width:36,height:4,borderRadius:2,background:T.greyDim,margin:"0 auto"}}/>
              </button>
              <div style={{fontSize:14,fontWeight:700,color:T.white,fontFamily:"'Playfair Display',serif"}}>Before You Buy</div>
              <div style={{fontSize:10,color:T.grey,marginTop:3}}>Please review your order</div>
            </div>
            {/* Summary grid */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
              {[
                ["🍿",`${order?.totalItems || items.length} item${(order?.totalItems||items.length)!==1?"s":""}`],
                ["📅",order?.pickupTime || "At showtime"],
                ["logo",order?.pickupMovie?.theater || "ReelView Grand"],
                ["🎬",order?.pickupMovie?.title || "Movie"],
                ["💳",payLabel],
                ["💰",`$${total.toFixed(2)} total`],
              ].map(([icon,val,sub],i)=>(
                <div key={i} style={{background:T.navyCard,borderRadius:10,padding:"10px 12px",border:`1px solid ${T.glassBorder}`}}>
                  <div style={{fontSize:14,marginBottom:3}}>{icon==="logo"?<Logo size={0.55}/>:icon}</div>
                  <div style={{fontSize:11,color:T.white,fontWeight:500,lineHeight:1.4}}>{val}</div>
                  {sub&&<div style={{fontSize:10,color:T.grey,lineHeight:1.4}}>{sub}</div>}
                </div>
              ))}
            </div>
            {rewardIncrements>0&&<div style={{textAlign:"center",fontSize:11,color:T.gold,fontWeight:600,marginBottom:12}}>🎁 ${rewardDisc.toFixed(2)} reward applied</div>}
            {promoOK&&<div style={{textAlign:"center",fontSize:11,color:T.green,fontWeight:600,marginBottom:12}}>✓ 10% promo discount applied</div>}
            <button className="btn-gold" style={{width:"100%",padding:14,fontSize:14,borderRadius:50}} onClick={handleConfirmPurchase}>
              Confirm Purchase · ${total.toFixed(2)}
            </button>
            <button onClick={()=>setSummaryOpen(false)} style={{width:"100%",padding:10,fontSize:12,color:T.grey,background:"none",border:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",marginTop:6}}>Go Back</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{flexShrink:0,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`}}>
        <div style={{padding:"12px 16px",display:"flex",alignItems:"flex-start",gap:10}}>
          <button aria-label="Go back" onClick={goBack} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0,marginTop:2}}>←</button>
          <div style={{flex:1}}>
            <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:16,color:T.white,margin:0}}>Concessions Checkout</h1>
            <div style={{fontSize:11,color:T.grey,marginTop:2}}>{order?.pickupMovie?.title} · {order?.pickupMovie?.time}</div>
          </div>
          <button onClick={()=>nav("concessions")} style={{background:"none",border:"none",color:T.grey,fontSize:20,cursor:"pointer",flexShrink:0}}>×</button>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div style={{flex:1,overflowY:"auto"}}>
        <div style={{padding:"16px 16px 8px"}}>

          {/* ── Pickup info — compact strip (unique context) ── */}
          <div style={{display:"flex",alignItems:"center",gap:11,padding:"11px 14px",background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:12,marginBottom:18}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:11,color:T.offwhite}}>Pickup <span style={{color:T.white,fontWeight:600}}>{order?.pickupTime || "at showtime"}</span></div>
              <div style={{fontSize:10,color:T.grey,marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{order?.pickupMovie?.theater || "ReelView Grand"} · Concessions counter</div>
            </div>
          </div>

          {/* ── Your order — image-forward list, primary display ── */}
          <div style={{marginBottom:22}}>
            <div style={{fontSize:13,fontWeight:700,color:T.white,marginBottom:10,fontFamily:"'Outfit',sans-serif",display:"flex",alignItems:"baseline",justifyContent:"space-between"}}>
              <span>Your Order</span>
              <span style={{fontSize:10,color:T.grey,fontWeight:500}}>{order?.totalItems || 0} item{(order?.totalItems||0)!==1?"s":""}</span>
            </div>
            {items.map((item,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:i<items.length-1?`1px solid ${T.glassBorder}`:"none"}}>
                <div style={{width:44,height:44,borderRadius:8,overflow:"hidden",flexShrink:0,background:T.navyLight,border:`1px solid ${T.glassBorder}`}}>
                  {CONCESSION_IMAGES[item.id] ? <img src={CONCESSION_IMAGES[item.id]} alt={item.name} style={{width:"100%",height:"100%",objectFit:"cover"}} loading="lazy"/> : <span style={{fontSize:24,display:"flex",alignItems:"center",justifyContent:"center",width:"100%",height:"100%"}}>{item.emoji}</span>}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,color:T.white,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.name}</div>
                  <div style={{fontSize:10,color:T.grey,marginTop:2}}>Qty {item.qty}</div>
                </div>
                <span style={{fontSize:12,color:T.white,fontWeight:600,fontVariantNumeric:"tabular-nums",flexShrink:0}}>${(item.price*item.qty).toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* ── Payment ── */}
          <div style={{marginBottom:22}}>
            <div style={{fontSize:13,fontWeight:700,color:T.white,marginBottom:10,fontFamily:"'Outfit',sans-serif"}}>Payment</div>
            {/* Prototype disclaimer */}
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:10,background:"rgba(201,168,76,.06)",border:`1px solid ${T.goldBorder}`,marginBottom:10}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span style={{fontSize:9,color:T.grey,lineHeight:1.4}}><strong style={{color:T.goldLight}}>Prototype</strong> — No real payments. Do not enter real information.</span>
            </div>
            {/* Quick-pay row */}
            <div style={{display:"flex",gap:8,marginBottom:8}}>
              {PAYMENT_OPTS.filter(o=>o.id!=="card").map(opt=>(
                <button key={opt.id} onClick={()=>setPay(opt.id)} style={{flex:1,padding:"10px 4px",borderRadius:10,background:pay===opt.id?T.goldDim:T.navyCard,border:`1.5px solid ${pay===opt.id?T.goldBorder:T.glassBorder}`,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3,transition:"all .15s"}}>
                  <span style={{fontSize:16}}>{opt.icon}</span>
                  <span style={{fontSize:8,fontWeight:600,color:pay===opt.id?T.goldLight:T.grey,fontFamily:"'Outfit',sans-serif"}}>{opt.label.split(" ")[0]}</span>
                </button>
              ))}
            </div>
            {/* Card option */}
            <button onClick={()=>setPay("card")} style={{width:"100%",padding:"10px 14px",borderRadius:10,background:pay==="card"?T.goldDim:T.navyCard,border:`1.5px solid ${pay==="card"?T.goldBorder:T.glassBorder}`,cursor:"pointer",display:"flex",alignItems:"center",gap:10,transition:"all .15s"}}>
              <span style={{fontSize:16}}>💳</span>
              <span style={{fontSize:12,fontWeight:500,color:pay==="card"?T.goldLight:T.offwhite,fontFamily:"'Outfit',sans-serif"}}>Credit / Debit Card</span>
            </button>
            {pay==="card"&&(
              <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:8}}>
                <input className="inp" aria-label="Card number" placeholder="Card Number"/>
                <div style={{display:"flex",gap:8}}>
                  <input className="inp" aria-label="Card expiration date" placeholder="MM / YY" style={{flex:1}}/>
                  <input className="inp" aria-label="Card security code" placeholder="CVV" style={{flex:1}}/>
                </div>
                <input className="inp" aria-label="Name on card" placeholder="Name on Card"/>
              </div>
            )}
          </div>

          {/* ── Rewards + Promo — single expand-on-tap row ── */}
          <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:12,marginBottom:22,overflow:"hidden"}}>
            <button onClick={()=>setRewardsOpen(!rewardsOpen)} style={{width:"100%",background:"none",border:"none",cursor:"pointer",padding:"12px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",fontFamily:"'Outfit',sans-serif"}}>
              <div style={{display:"flex",alignItems:"center",gap:9,minWidth:0}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={(rewardDisc>0||promoOK)?T.goldLight:T.grey} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                <span aria-live="polite" style={{fontSize:12,color:(rewardDisc>0||promoOK)?T.white:T.offwhite,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {(rewardDisc>0||promoOK)
                    ? <>{rewardDisc>0&&<span style={{color:T.gold,fontWeight:600}}>${rewardDisc} reward</span>}{rewardDisc>0&&promoOK&&<span style={{color:T.grey}}> · </span>}{promoOK&&<span style={{color:T.green,fontWeight:600}}>Promo applied</span>}</>
                    : "Add a reward or promo"}
                </span>
              </div>
              <span style={{color:T.grey,fontSize:11,transform:rewardsOpen?"rotate(180deg)":"none",transition:"transform .2s",flexShrink:0,marginLeft:8}}>▾</span>
            </button>
            {rewardsOpen&&(
              <div style={{padding:"0 14px 14px",borderTop:`1px solid ${T.glassBorder}`}}>
                {/* Reward stepper */}
                <div style={{padding:"12px 0",borderBottom:`1px solid ${T.glassBorder}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:11,fontWeight:600,color:T.white}}>{rewardBalance.toLocaleString()} pts</div>
                    <div style={{fontSize:9,color:T.grey,marginTop:1}}>${rewardValue.toFixed(2)} available · 5,000 pts = $5</div>
                  </div>
                  {maxIncrements>0?(
                    <div role="group" aria-label="Reward amount" style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                      <button onClick={()=>setRewardIncrements(Math.max(0,rewardIncrements-1))} aria-label="Decrease reward" style={{width:26,height:26,borderRadius:"50%",background:rewardIncrements>0?T.navyLight:"transparent",border:`1.5px solid ${rewardIncrements>0?T.goldBorder:T.greyDim}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:rewardIncrements>0?T.gold:T.greyDim,fontSize:14,fontWeight:700}}>−</button>
                      <span aria-live="polite" aria-atomic="true" style={{fontSize:13,fontWeight:700,color:rewardIncrements>0?T.gold:T.grey,minWidth:30,textAlign:"center",fontVariantNumeric:"tabular-nums"}}>${rewardDisc}</span>
                      <button onClick={()=>setRewardIncrements(Math.min(maxIncrements,rewardIncrements+1))} aria-label="Increase reward" style={{width:26,height:26,borderRadius:"50%",background:rewardIncrements<maxIncrements?T.navyLight:"transparent",border:`1.5px solid ${rewardIncrements<maxIncrements?T.goldBorder:T.greyDim}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:rewardIncrements<maxIncrements?T.gold:T.greyDim,fontSize:14,fontWeight:700}}>+</button>
                    </div>
                  ):(
                    <span style={{fontSize:9,color:T.grey,flexShrink:0}}>Need 5,000+ pts</span>
                  )}
                </div>
                {/* Promo input */}
                <div style={{paddingTop:12,display:"flex",gap:8}}>
                  <input className="inp" aria-label="Promo code" value={promo} onChange={e=>setPromo(e.target.value)} placeholder="Promo code" style={{flex:1,padding:"9px 12px",fontSize:12}}/>
                  <button className="btn-outline" style={{padding:"9px 12px",fontSize:11,whiteSpace:"nowrap"}} onClick={()=>promo.length>2&&setPromoOK(true)}>Apply</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Contact ── */}
          <div style={{marginBottom:22}}>
            <div style={{fontSize:13,fontWeight:700,color:T.white,marginBottom:10,fontFamily:"'Outfit',sans-serif"}}>Contact</div>
            {/* Prototype disclaimer */}
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:10,background:"rgba(201,168,76,.06)",border:`1px solid ${T.goldBorder}`,marginBottom:10}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span style={{fontSize:9,color:T.grey,lineHeight:1.4}}><strong style={{color:T.goldLight}}>Prototype</strong> — Do not enter your real email address.</span>
            </div>
            <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:14}}>
              <div style={{fontSize:11,fontWeight:600,color:T.offwhite,marginBottom:2}}>Email Address</div>
              <div style={{fontSize:10,color:T.grey,lineHeight:1.4,marginBottom:10}}>We use your email address to send you your e-ticket and order confirmation receipt.</div>
              <input className="inp" type="email" aria-label="Email address" value={email} onChange={e=>setEmail(e.target.value)} placeholder="email@example.com" style={{width:"100%",padding:"11px 14px",fontSize:12}}/>
            </div>
          </div>

          {/* ── Order Summary — always visible ── */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,color:T.white,marginBottom:10,fontFamily:"'Outfit',sans-serif"}}>Order Summary</div>
            <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:"12px 14px"}}>
              <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:11}}>
                <span style={{color:T.grey}}>Subtotal ({order?.totalItems||0} item{(order?.totalItems||0)!==1?"s":""})</span>
                <span style={{color:T.grey,fontVariantNumeric:"tabular-nums"}}>${sub.toFixed(2)}</span>
              </div>
              {rewardIncrements>0&&(
                <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:11}}>
                  <span style={{color:T.gold}}>Reward</span>
                  <span style={{color:T.gold,fontVariantNumeric:"tabular-nums"}}>−${rewardDisc.toFixed(2)}</span>
                </div>
              )}
              {promoOK&&(
                <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:11}}>
                  <span style={{color:T.green}}>Promo (10% off)</span>
                  <span style={{color:T.green,fontVariantNumeric:"tabular-nums"}}>−${disc.toFixed(2)}</span>
                </div>
              )}
              <div style={{height:1,background:T.glassBorder,margin:"8px 0 6px"}}/>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                <span style={{fontSize:13,fontWeight:700,color:T.white}}>Total</span>
                <span style={{fontSize:16,fontWeight:800,color:T.gold,fontVariantNumeric:"tabular-nums"}}>${total.toFixed(2)}</span>
              </div>
              {ptsEarned>0&&<div style={{fontSize:9,color:T.gold,textAlign:"right",marginTop:2}}>+{ptsEarned} pts earned</div>}
            </div>
          </div>

          {/* ── Terms ── */}
          <div style={{fontSize:9,color:T.grey,textAlign:"center",marginBottom:14,lineHeight:1.6}}>
            By purchasing, I agree to the <span style={{color:T.gold,textDecoration:"underline",cursor:"pointer"}}>ReelView Terms of Use</span>.
          </div>
        </div>
      </div>

      {/* Sticky footer — matching Ticket Checkout */}
      <div style={{flexShrink:0,borderTop:`1px solid ${T.glassBorder}`,background:T.navyGlass,backdropFilter:"blur(20px)",padding:"12px 16px 18px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
            <div>
              <div style={{fontSize:10,color:T.grey}}>{order?.totalItems || 0} item{(order?.totalItems||0)!==1?"s":""}</div>
              <div style={{fontSize:15,fontWeight:700,color:T.gold}}>${total.toFixed(2)}</div>
            </div>
          </div>
          <button className="btn-gold" style={{padding:"13px 28px",fontSize:14,borderRadius:50}} onClick={()=>setSummaryOpen(true)}>
            Purchase
          </button>
        </div>
      </div>
    </div>
  );
};

const ConcessionsConfirmScreen = ({ nav, goBack, order, setNavStack, setPrevScreen, setScreen, setConcessionOrder }) => {
  const code = "C-"+Math.random().toString(36).substr(2,6).toUpperCase();
  const items = order?.items || [];
  const sub = order?.totalAmt || 0;
  const pickupMovie = order?.pickupMovie;
  const pickupTime = order?.pickupTime || "At showtime";
  const theaterName = pickupMovie?.theater || "ReelView Grand Cineplex";
  const ptsEarned = Math.round(sub * 10);

  return (
    <div className="screen" style={{paddingBottom:40,background:T.navy}}>
      <div style={{padding:"14px 16px",borderBottom:`1px solid ${T.glassBorder}`,display:"flex",alignItems:"center",gap:10}}>
        <button aria-label="Go back" onClick={()=>{setConcessionOrder(null);setNavStack(["movies"]);setPrevScreen(null);setScreen("movies");}} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0}}>←</button>
        <div style={{flex:1,display:"flex",justifyContent:"center"}}><Logo size={0.9}/></div>
        <div style={{width:34}}/>
      </div>
      <div style={{padding:"22px 16px"}}>

        {/* ── Success state ── */}
        <div style={{textAlign:"center",marginBottom:22}}>
          <div style={{width:64,height:64,borderRadius:"50%",background:"rgba(39,174,96,.15)",border:`2px solid ${T.green}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",fontSize:26,animation:"scaleIn .4s ease"}}>🍿</div>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:T.white,marginBottom:4,margin:"0 0 4px",fontWeight:700}}>Order Confirmed!</h1>
          <p style={{fontSize:12,color:T.grey}}>Show this screen at the concessions counter</p>
          <p style={{fontSize:10,color:T.gold,marginTop:4}}>+{ptsEarned} reward points earned on this order</p>
        </div>

        {/* ── Order Receipt Card ── */}
        <div style={{background:`linear-gradient(145deg,${T.navyLight},${T.navyMid})`,border:`1px solid ${T.glassBorder}`,borderRadius:20,padding:20,marginBottom:18,position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:-28,right:-28,width:100,height:100,borderRadius:"50%",background:T.goldDim,pointerEvents:"none"}}/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
            <Logo size={0.72}/>
            <span className="tag">CONCESSIONS</span>
          </div>

          {/* Pickup details grid */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            {[
              ["Pickup Time",pickupTime],
              ["Movie",pickupMovie?.title||"—"],
              ["Theater",theaterName],
              ["Items",`${order?.totalItems||items.length} item${(order?.totalItems||items.length)!==1?"s":""}`],
            ].map(([k,v])=>(
              <div key={k}><div style={{fontSize:9,color:T.grey,marginBottom:1,letterSpacing:".07em",textTransform:"uppercase"}}>{k}</div><div style={{fontSize:12,fontWeight:600,color:T.white}}>{v}</div></div>
            ))}
          </div>

          {/* Itemized order */}
          <div style={{borderTop:`1px solid rgba(255,255,255,.06)`,paddingTop:12,marginBottom:14}}>
            <div style={{fontSize:9,color:T.grey,letterSpacing:".08em",textTransform:"uppercase",marginBottom:8}}>ORDER DETAILS</div>
            {items.map((item,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 0",borderBottom:i<items.length-1?`1px solid rgba(255,255,255,.04)`:"none"}}>
                <div style={{width:30,height:30,borderRadius:6,overflow:"hidden",flexShrink:0,background:T.navyLight}}>
                  {CONCESSION_IMAGES[item.id] ? <img src={CONCESSION_IMAGES[item.id]} alt={item.name} style={{width:"100%",height:"100%",objectFit:"cover"}} loading="lazy"/> : <span style={{fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",width:"100%",height:"100%"}}>{item.emoji}</span>}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,color:T.white,fontWeight:500}}>{item.name}</div>
                  <div style={{fontSize:9,color:T.grey}}>Qty: {item.qty} × ${item.price.toFixed(2)}</div>
                </div>
                <span style={{fontSize:12,color:T.white,fontWeight:600}}>${(item.price*item.qty).toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Total */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",padding:"10px 0 2px",borderTop:`1px solid rgba(255,255,255,.08)`}}>
            <span style={{fontSize:14,fontWeight:700,color:T.white}}>Total Paid</span>
            <span style={{fontSize:16,fontWeight:700,color:T.gold}}>${sub.toFixed(2)}</span>
          </div>

          {/* Tear line */}
          <div style={{display:"flex",alignItems:"center",gap:0,margin:"16px -20px"}}>
            <div style={{width:18,height:18,borderRadius:"50%",background:T.navy,flexShrink:0}}/>
            <div style={{flex:1,borderTop:`2px dashed ${T.greyDim}`}}/>
            <div style={{width:18,height:18,borderRadius:"50%",background:T.navy,flexShrink:0}}/>
          </div>

          {/* Barcode + order number */}
          <div style={{textAlign:"center",paddingTop:2}}>
            <div style={{display:"flex",gap:1.5,justifyContent:"center",marginBottom:5}}>
              {Array.from({length:34},(_,i)=><div key={i} style={{width:i%5===0?3:i%3===0?2:1.5,height:40,background:T.white,borderRadius:1,opacity:.3+((i*17)%7)*.1}}/>)}
            </div>
            <div style={{fontSize:9,color:T.grey,letterSpacing:".18em"}}>{code}</div>
            <div style={{fontSize:8,color:T.grey,marginTop:2}}>Show this barcode at the counter</div>
          </div>
        </div>

        {/* ── Wallet & Calendar ── */}
        <div style={{display:"flex",gap:10,marginBottom:14}}>
          <button className="btn-outline" style={{flex:1,padding:11,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
            Add to Wallet
          </button>
          <button className="btn-outline" style={{flex:1,padding:11,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            📅 Add to Calendar
          </button>
        </div>


      </div>
    </div>
  );
};

// (Invite Friends is now a modal inside TicketConfirmScreen)

// ══════════════════════════════════════════════════════
// ROOT
// ══════════════════════════════════════════════════════
const NAV_ACTIVE = {
  movies:["movies","movie-detail","seat-map","ticket-qty","ticket-checkout","ticket-confirm"],
  theaters:["theaters","theater-detail"],
  concessions:["concessions","concessions-checkout","concessions-confirm"],
  myreelview:["myreelview"],
};

// ─── Prototype demo affordances ────────────────────────────────────────────────
// First-launch coachmark — slides down from beneath the notch, auto-fades after 10s.
// Mounts/unmounts smoothly: enter and exit both run a .5s transition.
const IntroToast = ({ visible, onDismiss }) => {
  const [render, setRender] = useState(visible);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (visible) {
      setRender(true);
      setExiting(false);
    } else if (render) {
      setExiting(true);
      const t = setTimeout(() => setRender(false), 520);
      return () => clearTimeout(t);
    }
  }, [visible, render]);

  if (!render) return null;
  return (
    <button
      onClick={onDismiss}
      aria-live="polite"
      style={{
        position:"absolute",top:42,left:14,right:14,zIndex:400,
        display:"flex",alignItems:"center",gap:12,
        background:"linear-gradient(135deg,rgba(201,168,76,.18),rgba(201,168,76,.10))",
        border:`1px solid ${T.goldBorder}`,
        backdropFilter:"blur(14px)",
        borderRadius:14,padding:"11px 14px",
        boxShadow:"0 12px 32px rgba(0,0,0,.5),0 0 0 1px rgba(201,168,76,.18)",
        animation: exiting
          ? "fadeOutToast .52s cubic-bezier(.22,1,.36,1) both"
          : "slideDownToast .5s cubic-bezier(.22,1,.36,1) both",
        textAlign:"left",cursor:"pointer",fontFamily:"'Outfit',sans-serif",
      }}
    >
      {/* Bare film reel — no encasing circle */}
      <svg width="22" height="22" viewBox="0 0 26 26" fill="none" style={{flexShrink:0}}>
        <circle cx="13" cy="13" r="11" stroke={T.goldLight} strokeWidth="1.6"/>
        <circle cx="13" cy="13" r="2.4" fill={T.goldLight}/>
        <circle cx="13" cy="5.2" r="1.7" stroke={T.goldLight} strokeWidth="1.2"/>
        <circle cx="19.6" cy="9.1" r="1.7" stroke={T.goldLight} strokeWidth="1.2"/>
        <circle cx="19.6" cy="16.9" r="1.7" stroke={T.goldLight} strokeWidth="1.2"/>
        <circle cx="13" cy="20.8" r="1.7" stroke={T.goldLight} strokeWidth="1.2"/>
        <circle cx="6.4" cy="16.9" r="1.7" stroke={T.goldLight} strokeWidth="1.2"/>
        <circle cx="6.4" cy="9.1" r="1.7" stroke={T.goldLight} strokeWidth="1.2"/>
      </svg>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:11,fontWeight:700,color:T.goldLight,letterSpacing:".06em",textTransform:"uppercase",marginBottom:1}}>Demo Tip</div>
        <div style={{fontSize:12,color:T.white,lineHeight:1.35}}>Hold the <strong style={{color:T.goldLight}}>ReelView</strong> logo for 1 second to restart the demo.</div>
      </div>
      <div style={{flexShrink:0,fontSize:14,color:T.grey,marginLeft:2}} aria-hidden="true">×</div>
      <style>{`
        @keyframes slideDownToast {
          from { opacity:0; transform:translateY(-16px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes fadeOutToast {
          from { opacity:1; transform:translateY(0); }
          to   { opacity:0; transform:translateY(-10px); }
        }
      `}</style>
    </button>
  );
};

// Reset confirmation — slides up briefly above the bottom nav.
const ResetSnackbar = ({ visible }) => {
  if (!visible) return null;
  return (
    <div
      role="status" aria-live="polite"
      style={{
        position:"absolute",bottom:84,left:"50%",transform:"translateX(-50%)",zIndex:400,
        background:"rgba(4,8,15,.92)",border:`1px solid ${T.goldBorder}`,
        backdropFilter:"blur(14px)",
        borderRadius:50,padding:"9px 18px",
        display:"flex",alignItems:"center",gap:8,
        boxShadow:"0 8px 24px rgba(0,0,0,.5),0 0 24px rgba(201,168,76,.18)",
        animation:"snackPop .4s cubic-bezier(.22,1,.36,1) both",
        fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap",
      }}
    >
      <span style={{fontSize:14,color:T.goldLight}}>✦</span>
      <span style={{fontSize:12,fontWeight:600,color:T.white,letterSpacing:".02em"}}>Demo reset</span>
      <style>{`
        @keyframes snackPop {
          from { opacity:0; transform:translate(-50%,12px) scale(.92); }
          to   { opacity:1; transform:translate(-50%,0) scale(1); }
        }
      `}</style>
    </div>
  );
};

// Bottom-sheet modal — explains the prototype and the reset gesture.
const DemoInfoSheet = ({ onClose, onReset }) => {
  useModalDismiss(onClose);
  return (
    <div onClick={onClose} style={{position:"absolute",inset:0,background:"rgba(0,0,0,.6)",backdropFilter:"blur(6px)",zIndex:500,display:"flex",alignItems:"flex-end",justifyContent:"center",animation:"fadeIn .2s ease"}}>
      <div onClick={e=>e.stopPropagation()} style={{
        position:"relative",width:"100%",
        background:T.navyMid,borderRadius:"20px 20px 0 0",
        border:`1px solid ${T.glassBorder}`,borderBottom:"none",
        padding:"22px 22px 28px",
        boxShadow:"0 -8px 40px rgba(0,0,0,.5)",
        animation:"slideUp .28s cubic-bezier(.22,1,.36,1) both",
        maxHeight:"82%",overflowY:"auto",
      }}>
        {/* Drag handle */}
        <div style={{width:40,height:4,borderRadius:2,background:T.greyDim,margin:"-6px auto 16px"}}/>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
          {/* Bare film reel — no encasing circle */}
          <svg width="30" height="30" viewBox="0 0 26 26" fill="none" style={{flexShrink:0}}>
            <circle cx="13" cy="13" r="11" stroke={T.goldLight} strokeWidth="1.5"/>
            <circle cx="13" cy="13" r="2.6" fill={T.goldLight}/>
            <circle cx="13" cy="5.2" r="1.8" stroke={T.goldLight} strokeWidth="1.2"/>
            <circle cx="19.6" cy="9.1" r="1.8" stroke={T.goldLight} strokeWidth="1.2"/>
            <circle cx="19.6" cy="16.9" r="1.8" stroke={T.goldLight} strokeWidth="1.2"/>
            <circle cx="13" cy="20.8" r="1.8" stroke={T.goldLight} strokeWidth="1.2"/>
            <circle cx="6.4" cy="16.9" r="1.8" stroke={T.goldLight} strokeWidth="1.2"/>
            <circle cx="6.4" cy="9.1" r="1.8" stroke={T.goldLight} strokeWidth="1.2"/>
          </svg>
          <div>
            <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:18,color:T.white,lineHeight:1.1}}>About this prototype</div>
            <div style={{fontSize:11,color:T.grey,marginTop:2,letterSpacing:".04em"}}>Portfolio Project · 2026</div>
          </div>
        </div>

        <div style={{fontSize:13,color:T.offwhite,lineHeight:1.55,marginBottom:18}}>
          ReelView is a portfolio prototype of a cinema ticketing app. It walks through the full UX from movie discovery to seat selection, checkout, and rewards.
        </div>

        <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:"14px 16px",marginBottom:14}}>
          <div style={{fontSize:10,fontWeight:800,letterSpacing:".18em",textTransform:"uppercase",color:T.goldLight,marginBottom:8}}>Reset Gesture</div>
          <div style={{fontSize:13,color:T.white,lineHeight:1.55}}>
            Hold the <strong style={{color:T.goldLight}}>ReelView</strong> logo for 1 second to restart the demo. All favorites, points, and purchases will reset.
          </div>
        </div>

        <div style={{display:"flex",gap:10}}>
          <button className="btn-outline press" onClick={onClose} style={{flex:1,padding:"11px 0",fontSize:13,borderRadius:50}}>Got it</button>
          <button className="btn-gold press" onClick={()=>{onReset();onClose();}} style={{flex:1,padding:"11px 0",fontSize:13,borderRadius:50}}>Reset now</button>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  const [screen, setScreen] = useState("movies");
  const [movie, setMovie] = useState(null);
  const [theater, setTheater] = useState(null);
  const [showtime, setShowtime] = useState(null);
  const [seatData, setSeatData] = useState(null);
  const [ticketOrder, setTicketOrder] = useState(null);
  const [concessionOrder, setConcessionOrder] = useState(null); // temp state for current concession flow
  const [purchasedConcessions, setPurchasedConcessions] = useState([]); // completed concession orders
  const [concessionPreselect, setConcessionPreselect] = useState(null); // movie title to pre-select in Concessions screen
  const [theaterInitTab, setTheaterInitTab] = useState("showtimes");
  const [seatTimer, setSeatTimer] = useState(null); // null = not started, number = seconds left
  const [prevScreen, setPrevScreen] = useState(null); // track where user came from
  const [navStack, setNavStack] = useState(["movies"]); // full navigation history stack
  const [favs, setFavs] = useState([1,4]);
  const [purchasedTickets, setPurchasedTickets] = useState([]); // tickets purchased this session
  const [purchasedSeats, setPurchasedSeats] = useState({}); // {showtimeKey: [seat ids]}
  const [rewardPoints, setRewardPoints] = useState(12480); // live reward points balance
  const [rewardHistory, setRewardHistory] = useState([]); // new reward transactions this session
  const [releaseSeatModal, setReleaseSeatModal] = useState(false); // modal for releasing seats
  const [timerExpiredModal, setTimerExpiredModal] = useState(false); // modal for expired timer
  const [autoInvite, setAutoInvite] = useState(false); // auto-open invite modal on ticket-confirm
  const [myReelViewTab, setMyReelViewTab] = useState("rewards"); // persist MyReelView active tab
  const [moviesTab, setMoviesTab] = useState("now"); // persist Movies screen active tab

  // ── Prototype demo controls ─────────────────────────────────────────────────
  // Gate on a "dismissed" key (not "seen") — robust to React StrictMode's double-mount in dev,
  // because the key is only written when the user actually dismisses the toast.
  const [showIntroToast, setShowIntroToast] = useState(() => {
    try { return !sessionStorage.getItem("rv-demo-intro-dismissed-v2"); }
    catch { return true; }
  });
  const [showDemoInfo, setShowDemoInfo] = useState(false);     // info sheet (ⓘ button)
  const [resetSnackbar, setResetSnackbar] = useState(false);   // confirmation after reset
  const [logoFlash, setLogoFlash] = useState(false);           // gold flash on logo when reset fires

  const dismissIntroToast = useCallback(() => {
    try { sessionStorage.setItem("rv-demo-intro-dismissed-v2", "1"); } catch (_) {}
    setShowIntroToast(false);
  }, []);

  // Auto-dismiss the toast after 10 seconds if it's visible.
  useEffect(() => {
    if (!showIntroToast) return;
    const t = setTimeout(dismissIntroToast, 10000);
    return () => clearTimeout(t);
  }, [showIntroToast, dismissIntroToast]);

  const resetPrototype = useCallback(() => {
    setScreen("movies");
    setMovie(null); setTheater(null); setShowtime(null);
    setNavStack(["movies"]); setPrevScreen(null);
    setSeatTimer(null); setSeatData(null); setTicketOrder(null);
    setConcessionOrder(null); setPurchasedConcessions([]);
    setConcessionPreselect(null); setTheaterInitTab("showtimes");
    setFavs([1,4]); setPurchasedTickets([]); setPurchasedSeats({});
    setRewardPoints(12480); setRewardHistory([]);
    setReleaseSeatModal(false); setTimerExpiredModal(false);
    setAutoInvite(false);
    setMyReelViewTab("rewards"); setMoviesTab("now");
    // Visual feedback: gold flash on logo + bottom snackbar
    dismissIntroToast();
    setLogoFlash(true);
    setTimeout(() => setLogoFlash(false), 700);
    setResetSnackbar(true);
    setTimeout(() => setResetSnackbar(false), 1800);
  }, [dismissIntroToast]);

  const addPurchasedTicket = (ticket) => setPurchasedTickets(t=>[ticket,...t]);
  const addPurchasedSeats = (st, seatArr) => {
    const key = `${st?.theater?.id}-${st?.movie?.id}-${st?.fmt}-${st?.time}`;
    setPurchasedSeats(prev => {
      const existing = prev[key] || [];
      const combined = [...existing, ...seatArr.filter(s => !existing.includes(s))];
      return {...prev, [key]: combined};
    });
  };
  const toggleFav = (e, id) => { e.stopPropagation(); setFavs(f=>f.includes(id)?f.filter(x=>x!==id):[...f,id]); };

  // Persistent seat-hold timer — ticks across all transaction screens
  const TIMER_SCREENS = ["seat-map","ticket-qty","ticket-checkout"];
  useEffect(() => {
    if (seatTimer === null || seatTimer <= 0) return;
    if (!TIMER_SCREENS.includes(screen)) return;
    const id = setInterval(() => setSeatTimer(t => t > 0 ? t - 1 : 0), 1000);
    return () => clearInterval(id);
  }, [seatTimer, screen]);
  // Handle timer expiry separately — use ref to prevent re-firing
  const timerExpiredRef = useRef(false);
  useEffect(() => {
    if (seatTimer === 0 && !timerExpiredRef.current) {
      timerExpiredRef.current = true;
      // Defer navigation to avoid batched state conflicts
      setTimeout(() => {
        setSeatTimer(null);
        setSeatData(null);
        setTicketOrder(null);
        setNavStack(st => {
          const target = st.includes("theater-detail") ? "theater-detail" : "movie-detail";
          const idx = st.lastIndexOf(target);
          if (idx >= 0) { setScreen(target); return st.slice(0, idx+1); }
          setScreen("movie-detail");
          return ["movies","movie-detail"];
        });
        setTimerExpiredModal(true);
      }, 0);
    }
    if (seatTimer !== null && seatTimer > 0) timerExpiredRef.current = false;
  }, [seatTimer]);

  const nav = s => { setPrevScreen(screen); setNavStack(st=>[...st,s]); setScreen(s); };
  const goBack = () => {
    setNavStack(st => {
      const next = st.slice(0,-1);
      const target = next[next.length-1] || "movies";
      setPrevScreen(next.length>1 ? next[next.length-2] : null);
      setScreen(target);
      return next;
    });
  };
  const activeNav = Object.entries(NAV_ACTIVE).find(([,screens])=>screens.includes(screen))?.[0]||"movies";
  const hideBottomNav = ["ticket-checkout","ticket-confirm","concessions-confirm"].includes(screen);

  // ── Zoom-safe scaling: shrink the phone shell horizontally if viewport is too narrow ──
  // Only constrains WIDTH — the page scrolls vertically as normal.
  // Prevents the horizontal overflow / clipping that breaks layout at high zoom.
  const shellRef = useRef(null);
  useEffect(() => {
    const SHELL_W = 376; // 360 content + 16 border
    const PAD_X = 40;    // horizontal breathing room (wrapper padding)
    const fit = () => {
      const el = shellRef.current;
      if (!el) return;
      // Skip when full-bleed mobile kicks in (CSS handles it)
      if (window.innerWidth <= 420) { el.style.transform = "none"; return; }
      const s = Math.min(1, (window.innerWidth - PAD_X) / SHELL_W);
      el.style.transform = s < 1 ? `scale(${s})` : "none";
    };
    fit();
    window.addEventListener("resize", fit);
    const vv = window.visualViewport;
    if (vv) vv.addEventListener("resize", fit);
    return () => {
      window.removeEventListener("resize", fit);
      if (vv) vv.removeEventListener("resize", fit);
    };
  }, []);

  return (
    <div className="phone-wrapper">
      <FontStyle/>
      {/* Phone shell — flex column so screens + nav stack vertically */}
      <div ref={shellRef} className="phone-shell" style={{background:`linear-gradient(180deg,${T.navy} 0%,#020508 100%)`,borderRadius:44,overflow:"hidden",position:"relative",boxShadow:"0 60px 140px rgba(0,0,0,.85),0 0 0 1px rgba(255,255,255,.06),inset 0 1px 0 rgba(255,255,255,.04)",border:"8px solid #0a0a0a",display:"flex",flexDirection:"column"}}>
        {/* Screen glow — ambient projector light hitting the ceiling */}
        <div style={{position:"absolute",top:0,left:0,right:0,height:280,background:"radial-gradient(ellipse at 50% -20%,rgba(201,168,76,0.05) 0%,rgba(201,168,76,0.02) 40%,transparent 70%)",pointerEvents:"none",zIndex:1}}/>
        {/* Film grain overlay */}
        <div style={{position:"absolute",inset:0,backgroundImage:"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E\")",opacity:.5,pointerEvents:"none",zIndex:2,mixBlendMode:"overlay",animation:"grainDrift 8s steps(10) infinite"}}/>
        {/* Notch */}
        <div style={{position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",width:110,height:26,background:"#0a0a0a",borderRadius:"0 0 16px 16px",zIndex:300,flexShrink:0}}/>
        {/* Notch spacer */}
        <div style={{height:26,flexShrink:0}}/>
        {/* Screens area — flex:1 so it fills remaining space, overflow hidden so each screen manages its own scroll */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",position:"relative",zIndex:3}}>
          {screen==="movies" && <MoviesScreen nav={nav} setMovie={setMovie} favs={favs} toggleFav={toggleFav} tab={moviesTab} setTab={setMoviesTab} onResetPrototype={resetPrototype} onOpenDemoInfo={()=>setShowDemoInfo(true)} logoFlash={logoFlash}/>}
          {screen==="movie-detail" && <MovieDetailScreen movie={movie} nav={nav} goBack={goBack} setShowtime={setShowtime} setTheater={setTheater} setTheaterInitTab={setTheaterInitTab} favs={favs} toggleFav={toggleFav} setSeatTimer={setSeatTimer} setSeatData={setSeatData} setTicketOrder={setTicketOrder}/>}
          {screen==="seat-map" && <SeatMapScreen showtime={showtime} nav={nav} goBack={goBack} setSeatData={setSeatData} seatData={seatData} seatTimer={seatTimer} setSeatTimer={setSeatTimer} purchasedSeats={purchasedSeats} rewardPoints={rewardPoints} onReleaseSeat={()=>{setSeatTimer(null);setSeatData(null);setTicketOrder(null);goBack();}}/>}
          {screen==="ticket-qty" && <TicketQtyScreen showtime={showtime} seats={seatData} nav={nav} goBack={goBack} setTicketOrder={setTicketOrder} seatTimer={seatTimer} onReleaseSeat={()=>{setSeatTimer(null);setSeatData(null);setTicketOrder(null);setScreen("movies");setNavStack(["movies"]);setPrevScreen(null);}}/>}
          {screen==="ticket-checkout" && <TicketCheckoutScreen showtime={showtime} seats={seatData} ticketOrder={ticketOrder} nav={nav} goBack={goBack} seatTimer={seatTimer} addPurchasedTicket={addPurchasedTicket} setRewardPoints={setRewardPoints} setRewardHistory={setRewardHistory} rewardPoints={rewardPoints} addPurchasedSeats={addPurchasedSeats} setNavStack={setNavStack} setPrevScreen={setPrevScreen} setScreen={setScreen} setSeatTimer={setSeatTimer} onReleaseSeat={()=>{setSeatTimer(null);setSeatData(null);setTicketOrder(null);setScreen("movies");setNavStack(["movies"]);setPrevScreen(null);}}/>}
          {screen==="ticket-confirm" && <TicketConfirmScreen showtime={showtime} seats={seatData} nav={nav} goBack={goBack} prevScreen={prevScreen} autoInvite={autoInvite} setAutoInvite={setAutoInvite}/>}
          {screen==="theaters" && <TheatersScreen nav={nav} setTheater={setTheater} setTheaterInitTab={setTheaterInitTab}/>}
          {screen==="theater-detail" && <TheaterDetailScreen theater={theater} nav={nav} goBack={goBack} setMovie={setMovie} setShowtime={setShowtime} initTab={theaterInitTab} setSeatTimer={setSeatTimer} setSeatData={setSeatData} setTicketOrder={setTicketOrder}/>}
          {screen==="myreelview" && <MyReelViewScreen nav={nav} setShowtime={setShowtime} setSeatData={setSeatData} purchasedConcessions={purchasedConcessions} purchasedTickets={purchasedTickets} rewardPoints={rewardPoints} rewardHistory={rewardHistory} setConcessionPreselect={setConcessionPreselect} setAutoInvite={setAutoInvite} tab={myReelViewTab} setTab={setMyReelViewTab}/>}
          {screen==="concessions" && <ConcessionsScreen nav={nav} setConcessionOrder={setConcessionOrder} existingOrder={concessionOrder} purchasedTickets={purchasedTickets} purchasedConcessions={purchasedConcessions} concessionPreselect={concessionPreselect} setConcessionPreselect={setConcessionPreselect}/>}
          {screen==="concessions-checkout" && <ConcessionsCheckoutScreen nav={nav} goBack={goBack} order={concessionOrder} setRewardPoints={setRewardPoints} setRewardHistory={setRewardHistory} rewardPoints={rewardPoints} setPurchasedConcessions={setPurchasedConcessions}/>}
          {screen==="concessions-confirm" && <ConcessionsConfirmScreen nav={nav} goBack={goBack} order={concessionOrder} setNavStack={setNavStack} setPrevScreen={setPrevScreen} setScreen={setScreen} setConcessionOrder={setConcessionOrder}/>}
        </div>
        {/* Timer expired modal */}
        {timerExpiredModal && (
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(6px)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
            <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:18,padding:"28px 22px 22px",maxWidth:280,width:"100%",textAlign:"center"}}>
              <div style={{fontSize:28,marginBottom:12}}>⏰</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:16,color:T.white,marginBottom:8}}>Time's Up</div>
              <div style={{fontSize:12,color:T.grey,lineHeight:1.5,marginBottom:20}}>Your seat hold has expired and your held seats have been released. Please select a new showtime.</div>
              <button className="btn-gold" style={{width:"100%",padding:12,fontSize:13,borderRadius:50}} onClick={()=>setTimerExpiredModal(false)}>
                OK
              </button>
            </div>
          </div>
        )}
        {/* Bottom nav — structural, always at bottom of phone */}
        {!hideBottomNav && <BottomNav active={activeNav} nav={nav}/>}

        {/* ── Prototype affordances — live inside the phone-shell so they overlay correctly ── */}
        <IntroToast visible={showIntroToast} onDismiss={dismissIntroToast}/>
        <ResetSnackbar visible={resetSnackbar}/>
        {showDemoInfo && <DemoInfoSheet onClose={()=>setShowDemoInfo(false)} onReset={resetPrototype}/>}
      </div>
    </div>
  );
}
